import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useModalZIndex } from '../contexts/ModalStackContext';
import { App as CapacitorApp } from '@capacitor/app';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { 
  Plus, Edit, Trash, X, PieChart, Filter, Loader, GripVertical, 
  Search, Settings, Users, TrendingUp, Building, Building2, Link, FileText, MapPin, ArrowLeft, ArrowRight, Folder, MessageSquare, Mail, Phone, ArrowRightLeft, ChevronDown, ChevronRight, Calendar, CalendarDays, Car, ClipboardList, NotebookPen, FileSearch, Clock, CheckCircle2, ArrowDownLeft, ArrowUpRight
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';
import { supabase } from '../lib/supabase';
import { Customer, getCustomer } from '../lib/customers';
import { recordOpportunityActivity } from '../lib/opportunityActivities';
import { getOpportunityDetail } from '../lib/crm/opportunityDetail';
import { CustomFieldOptions, CUSTOM_FIELDS_CHANGED_EVENT } from '../lib/crm/customFields';
import { triggerEnterStageAutomations } from '../lib/automationsV2';
import { CRM_OPPORTUNITY_READ_ON_ACTIVITY_EVENT } from '../lib/crm/markOpportunityRead';
import SelectCustomerModal from '../components/manage/SelectCustomerModal';
import { useUnreadOpportunitiesContext } from '../contexts/UnreadOpportunitiesContext';
import CustomerMessageModal from '../components/CustomerMessageModal';
import BookingCustomerInfo from '../components/booking/BookingCustomerInfo';
import { CRMSkeleton } from '../components/CRMSkeleton';
import CreateProposalModal from '../components/CreateProposalModal';
import { getProposal, Proposal } from '../lib/proposals';
import ProposalDetailsModal from '../components/ProposalDetailsModal';
import CreateBookingModal from '../components/CreateBookingModal';
import CreateMobileBookingModal from '../components/CreateMobileBookingModal';
import { getBooking, Booking } from '../lib/bookings';
import BookingInfoModal from '../components/BookingInfoModal';
import CreateProjectModal from '../components/CreateProjectModal';
import ScheduleFieldQuoteModal from '../components/ScheduleFieldQuoteModal';
import FieldQuoteDetailModal from '../components/FieldQuoteDetailModal';
import CustomerDetailsModal from '../components/CustomerDetailsModal';
import LostReasonModal from '../components/LostReasonModal';
import OpportunityLeadAgentSection from '../components/crm/OpportunityLeadAgentSection';
import OpportunityIntakeFormsSection from '../components/crm/OpportunityIntakeFormsSection';
import SendIntakeFormModal from '../components/crm/SendIntakeFormModal';
import CrmCallActivityBody, { callOutcomeLabel, type CallActivityData } from '../components/crm/CrmCallActivityBody';
import CrmCreateActionDropdown, { type CrmCreateActionOption } from '../components/crm/CrmCreateActionDropdown';
import BusinessCardScanModal from '../components/crm/BusinessCardScanModal';
import LeadCreateChoiceModal from '../components/crm/LeadCreateChoiceModal';
import OpportunitySalesPinCard from '../components/sales-map/OpportunitySalesPinCard';
import SalesPinDropModal from '../components/sales-map/SalesPinDropModal';
import { addressMarkerService, type AddressMarker } from '../services/addressMarkerService';
import { resolveLeadCreator } from '../lib/crm/createLead';
import { getResources, type Resource } from '../lib/resources';
import CreateGoogleCalendarEventModal from '../components/CreateGoogleCalendarEventModal';
import { useGoogleCalendarConnection } from '../hooks/useGoogleCalendarConnection';
import {
  buildGoogleCalendarPrefillFromOpportunity,
  type GoogleCalendarEventPrefill,
} from '../lib/crm/googleCalendarEventPrefill';
import OpportunityNextFollowUpHeader from '../components/crm/OpportunityNextFollowUpHeader';
import OpportunityFollowUpPromptModal from '../components/crm/OpportunityFollowUpPromptModal';
import CrmFollowUpDueBadge from '../components/crm/CrmFollowUpDueBadge';
import { UNASSIGNED_STAGE_ID, stageStillExists, withUnassignedStage } from '../lib/crm/unassignedStage';
import LeadScoreThermometer from '../components/crm/LeadScoreThermometer';
import ProposalExpiryBadge from '../components/crm/ProposalExpiryBadge';
import {
  getFollowUpClosePromptReason,
  hasActiveFutureFollowUp,
  type FollowUpClosePromptReason,
} from '../lib/crm/followUpClosePrompt';
import {
  hasOutreachActivityAfterFollowUpDue,
  markOpportunityDetailsFirstOpened,
} from '../lib/crm/checkOutreachAfterFollowUpDue';
import { getAgents, invokeLeadAgentTurn, type AIAgent } from '../lib/aiAgents';
import toast from 'react-hot-toast';

interface Stage {
  id: number;
  name: string;
  position?: number;
}

interface Pipeline {
  id: number;
  business_id: string;
  pipeline_name: string;
  stages?: Stage[];
  sort_order?: number;
}

interface Opportunity {
  id: number;
  business_id: string;
  customer_id: string;
  title: string;
  pipeline_id: number;
  stage_id: number | null;
  value: number;
  business_name?: string | null;
  source?: string | null;
  tags?: string | null;
  status: string;
  created_at: string;
  custom_fields?: string | null; // JSON string of custom fields
  is_unread?: boolean; // Indicates if the opportunity is unread (shows red dot indicator)
  customer_name?: string; // Customer name for display
  customers?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  lead_agent_id?: string | null;
  lead_agent_next_follow_up_at?: string | null;
  lead_agent_follow_up_reason?: string | null;
  lead_agent_last_wake_at?: string | null;
  next_follow_up_at?: string | null;
  details_first_opened_at?: string | null;
  initial_follow_up_prompt_dismissed_at?: string | null;
  follow_up_due_prompt_dismissed_for_due_at?: string | null;
  created_by_user_id?: string | null;
  created_by_resource_id?: string | null;
  assigned_resource_id?: string | null;
  /** Sales' 0–100 read. Null until Sales has scored this lead. */
  lead_score?: number | null;
  lead_score_reason?: string | null;
}

function crmIdsMatch(a: unknown, b: unknown): boolean {
  if (a == null || b == null) return false;
  return Number(a) === Number(b);
}

function resolveOpportunityPipelineStage(
  pipelines: Pipeline[],
  opportunity: Pick<Opportunity, 'pipeline_id' | 'stage_id'>,
): { pipeline: Pipeline | null; stage: Stage | null } {
  const pipeline = pipelines.find((p) => crmIdsMatch(p.id, opportunity.pipeline_id)) ?? null;
  const stage = pipeline?.stages?.find((s) => crmIdsMatch(s.id, opportunity.stage_id)) ?? null;
  return { pipeline, stage };
}

export interface CRMProps {
  /** When set, opens the opportunity details drawer without showing the CRM page chrome. */
  embedOpportunityId?: number | null;
  /** Opens the new-opportunity drawer (control centre). Mutually exclusive with embedOpportunityId. */
  embedCreateOpportunity?: boolean;
  /** Pre-select pipeline when embedCreateOpportunity is true. */
  embedPipelineId?: number | null;
  /** Pre-fill the customer when embedCreateOpportunity is true. */
  embedInitialCustomer?: Customer | null;
  onEmbedClose?: () => void;
  /** Called when the embedded opportunity is saved, deleted, or the drawer closes. */
  onEmbedOpportunityChange?: () => void;
  hidePageChrome?: boolean;
}

/** Re-enable when lead agent UI returns to opportunity details. */
const SHOW_OPPORTUNITY_LEAD_AGENT = false;

export default function CRM({
  embedOpportunityId = null,
  embedCreateOpportunity = false,
  embedPipelineId = null,
  embedInitialCustomer = null,
  onEmbedClose,
  onEmbedOpportunityChange,
  hidePageChrome = false,
}: CRMProps = {}) {
  const { selectedBusiness } = useBusiness();
  const { connected: gcalConnected, syncId: gcalSyncId } = useGoogleCalendarConnection(
    selectedBusiness?.id,
  );
  const { markAsRead, incrementCount } = useUnreadOpportunitiesContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'opportunities' | 'pipelines' | 'integrations'>('opportunities');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentPipelineId, setCurrentPipelineId] = useState<number | null>(null);
  const [pipelineName, setPipelineName] = useState('');
  const [stages, setStages] = useState<Stage[]>([{ id: 1, name: '', position: 1 }]);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPipelines, setIsLoadingPipelines] = useState(false);
  const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customerMap, setCustomerMap] = useState<Map<string, Customer>>(new Map());
  const [proposalMap, setProposalMap] = useState<Map<number, Proposal>>(new Map()); // Map opportunity id -> proposal
  const [salesPinMap, setSalesPinMap] = useState<Map<number, { id: string; marker_type: string; address?: string | null }>>(new Map()); // Map opportunity id -> linked sales pin

  // Lead creator tracking + resource assignment
  const [crmResources, setCrmResources] = useState<Resource[]>([]);
  const [myResourceId, setMyResourceId] = useState<string | null>(null);
  const [myUserId, setMyUserId] = useState<string | null>(null);
  const [assignedResourceId, setAssignedResourceId] = useState<string | null>(null);
  /** True once the user explicitly picks an assignee in create mode (stops late default). */
  const assignedTouchedRef = useRef(false);

  /** Display name for a resource id (assignment chip / created-by label). */
  const resourceDisplayName = (id?: string | null): string | null => {
    if (!id) return null;
    const r = crmResources.find((res) => res.id === id);
    if (!r) return null;
    return `${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email || 'Resource';
  };
  const [fieldQuoteMap, setFieldQuoteMap] = useState<
    Map<number, { id: string; status: string; is_measurement: boolean }>
  >(new Map());
  const [bookingMap, setBookingMap] = useState<Map<number, Booking>>(new Map()); // Map opportunity id -> booking
  const [projectMap, setProjectMap] = useState<Map<number, { id: string; title: string; status: string }>>(new Map()); // Map opportunity id -> project
  const channelsRef = React.useRef<{ channel?: any; proposalChannel?: any; bookingChannel?: any; projectChannel?: any }>({});
  const [draggedStageId, setDraggedStageId] = useState<number | null>(null);
  const [draggedOpportunity, setDraggedOpportunity] = useState<Opportunity | null>(null);
  const [dragOverStageId, setDragOverStageId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'all' | 'list'>('all');
  const [isOpportunityModalOpen, setIsOpportunityModalOpen] = useState(false);
  const [isCardScanOpen, setIsCardScanOpen] = useState(false);
  const [isLeadChoiceOpen, setIsLeadChoiceOpen] = useState(false);
  const [aiCardScanEnabled, setAiCardScanEnabled] = useState(false);

  // "AI Create from Business Card" CRM app setting (off by default). When on,
  // the create-lead buttons show a Create / Create from Photo chooser.
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) {
      setAiCardScanEnabled(false);
      return;
    }
    let cancelled = false;
    void supabase
      .rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: 'ai_create_from_business_card',
        p_default_value: false,
      })
      .then(({ data }) => {
        if (!cancelled) setAiCardScanEnabled(data === true);
      });

    const onSettingChanged = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.key === 'ai_create_from_business_card') {
        setAiCardScanEnabled(!!detail.value);
      }
    };
    window.addEventListener('crmSettingChanged', onSettingChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('crmSettingChanged', onSettingChanged);
    };
  }, [selectedBusiness?.id]);
  // Auto-elevate the opportunity drawer above a parent modal when CRM is embedded
  // (e.g. CustomerMessageModal's lead banner → open opportunity). Standalone CRM
  // keeps the original z-index.
  const opportunityModalZ = useModalZIndex(9999);
  const [isOpportunityInfoModalOpen, setIsOpportunityInfoModalOpen] = useState(false);
  const [hasProposalsApp, setHasProposalsApp] = useState(false);
  const [hasProjectsApp, setHasProjectsApp] = useState(false);
  const [showLocationBooking, setShowLocationBooking] = useState(false);
  const [showMobileBooking, setShowMobileBooking] = useState(false);
  const [showFieldQuotesInCrm, setShowFieldQuotesInCrm] = useState(false);
  // Mirrors the FAB's marketing "Create Sales Pin" setting so pins can also be
  // dropped for an existing lead from the opportunity create menu.
  const [showSalesPinCreate, setShowSalesPinCreate] = useState(false);
  const [isSalesPinModalOpen, setIsSalesPinModalOpen] = useState(false);
  const [opportunityForSalesPin, setOpportunityForSalesPin] = useState<Opportunity | null>(null);
  const [isCreateProposalModalOpen, setIsCreateProposalModalOpen] = useState(false);
  const [isCreateGcalEventModalOpen, setIsCreateGcalEventModalOpen] = useState(false);
  const [gcalEventPrefill, setGcalEventPrefill] = useState<GoogleCalendarEventPrefill | null>(
    null,
  );
  const [liveIntakeFormsEnabled, setLiveIntakeFormsEnabled] = useState(false);
  const [isSendIntakeFormModalOpen, setIsSendIntakeFormModalOpen] = useState(false);
  const [intakeInstancesNonce, setIntakeInstancesNonce] = useState(0);
  const [opportunityForProposal, setOpportunityForProposal] = useState<Opportunity | null>(null);
  const [proposalCustomer, setProposalCustomer] = useState<Customer | null>(null);
  const [isCreateBookingModalOpen, setIsCreateBookingModalOpen] = useState(false);
  const [isCreateMobileBookingModalOpen, setIsCreateMobileBookingModalOpen] = useState(false);
  const [opportunityForBooking, setOpportunityForBooking] = useState<Opportunity | null>(null);
  const [bookingCustomer, setBookingCustomer] = useState<Customer | null>(null);
  // Stable reference so CreateMobileBookingModal's prefill effect doesn't refire every render.
  const mobileBookingPrefilledData = useMemo(
    () => (bookingCustomer ? { customer: bookingCustomer } : undefined),
    [bookingCustomer]
  );
  const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
  const [opportunityForProject, setOpportunityForProject] = useState<Opportunity | null>(null);
  const [projectCustomer, setProjectCustomer] = useState<Customer | null>(null);
  const [linkedProposal, setLinkedProposal] = useState<Proposal | null>(null);
  const [isLoadingLinkedProposal, setIsLoadingLinkedProposal] = useState(false);
  const [linkedBooking, setLinkedBooking] = useState<Booking | null>(null);
  const [isLoadingLinkedBooking, setIsLoadingLinkedBooking] = useState(false);
  const [linkedProject, setLinkedProject] = useState<{ id: string; title: string; status?: string } | null>(null);
  const [isLoadingLinkedProject, setIsLoadingLinkedProject] = useState(false);
  const [linkedFieldQuote, setLinkedFieldQuote] = useState<{
    id: string;
    status: string;
    is_measurement: boolean;
  } | null>(null);
  const [isLoadingLinkedFieldQuote, setIsLoadingLinkedFieldQuote] = useState(false);
  const [isScheduleFieldQuoteFromCrmOpen, setIsScheduleFieldQuoteFromCrmOpen] = useState(false);
  const [opportunityForFieldQuote, setOpportunityForFieldQuote] = useState<Opportunity | null>(null);
  const [fieldQuoteCustomer, setFieldQuoteCustomer] = useState<Customer | null>(null);
  const [crmFieldQuoteDetailId, setCrmFieldQuoteDetailId] = useState<string | null>(null);
  const [fieldQuotes, setFieldQuotes] = useState<
    Array<{
      id: string;
      status: string;
      is_measurement: boolean;
      pipeline_opportunities_id: number | null;
      customer_id: string | null;
      start_date: string | null;
      end_date: string | null;
    }>
  >([]);
  const [isLoadingFieldQuotes, setIsLoadingFieldQuotes] = useState(false);
  const [fieldQuoteSearchQuery, setFieldQuoteSearchQuery] = useState('');
  const [lastActivity, setLastActivity] = useState<{ activity_type: string; activity_data: any; created_at: string } | null>(null);
  const [isLoadingLastActivity, setIsLoadingLastActivity] = useState(false);
  const [firstActivity, setFirstActivity] = useState<{ activity_type: string; activity_data: any; created_at: string } | null>(null);
  const [isLoadingFirstActivity, setIsLoadingFirstActivity] = useState(false);
  const [timeToFirstResponse, setTimeToFirstResponse] = useState<string | null>(null);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [followUpPrompt, setFollowUpPrompt] = useState<{
    opportunity: Opportunity;
    reason: FollowUpClosePromptReason;
  } | null>(null);
  const [followUpPromptSaving, setFollowUpPromptSaving] = useState(false);
  const handleModalCloseRef = React.useRef<(options?: { skipFollowUpPrompt?: boolean }) => void>(() => {});
  const [allActivities, setAllActivities] = useState<Array<{ activity_type: string; activity_data: any; created_at: string; created_by: string | null }>>([]);
  const [isLoadingAllActivities, setIsLoadingAllActivities] = useState(false);
  const [isBookingDetailsModalOpen, setIsBookingDetailsModalOpen] = useState(false);
  const [isProposalDetailsModalOpen, setIsProposalDetailsModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkModalType, setLinkModalType] = useState<'bookings' | 'proposals' | 'projects' | 'field_quotes' | null>(null); // null = options; then list type
  const [proposals, setProposals] = useState<Array<Proposal>>([]);
  const [isLoadingProposals, setIsLoadingProposals] = useState(false);
  const [proposalSearchQuery, setProposalSearchQuery] = useState('');
  const [bookings, setBookings] = useState<Array<Booking>>([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [bookingSearchQuery, setBookingSearchQuery] = useState('');
  const [projects, setProjects] = useState<Array<{ id: string; title: string; status: string; customer_id: string | null; customer?: { id: string; first_name: string; last_name: string } | null }>>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isCustomerDetailsModalOpen, setIsCustomerDetailsModalOpen] = useState(false);
  const [isCustomFieldsModalOpen, setIsCustomFieldsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [opportunityTitle, setOpportunityTitle] = useState('');
  const [selectedOpportunityForInfo, setSelectedOpportunityForInfo] = useState<Opportunity | null>(null);
  const [leadAgentsForPicker, setLeadAgentsForPicker] = useState<AIAgent[]>([]);
  const [opportunitiesViewPipeline, setOpportunitiesViewPipeline] = useState<Pipeline | null>(null);
  const [selectedStage, setSelectedStage] = useState<Stage | null>(null);
  const [opportunityValue, setOpportunityValue] = useState(0);
  const [businessName, setBusinessName] = useState('');
  const [opportunitySource, setOpportunitySource] = useState('');
  const [opportunityStatus, setOpportunityStatus] = useState('Open');
  const [leadInfo, setLeadInfo] = useState('');
  const [availableTags, setAvailableTags] = useState<Array<{id: string, name: string}>>([]);
  const [selectedTags, setSelectedTags] = useState<Array<{id: string, name: string}>>([]);
  const [isNewTagModalOpen, setIsNewTagModalOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [customFields, setCustomFields] = useState<Array<{id: string, name: string, type: string, value: string}>>([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<Array<{id: string, name: string, type: string, required: boolean, options: CustomFieldOptions | null}>>([]);
  // CRM app setting: master toggle for custom fields on lead creation (off by default).
  const [customFieldsEnabled, setCustomFieldsEnabled] = useState(false);
  // Dropdown custom fields where the user picked "Custom…" and is typing a value.
  const [customModeFieldIds, setCustomModeFieldIds] = useState<Set<string>>(new Set());
  
  // Message modal state
  const [messageModalOpen, setMessageModalOpen] = useState(false);
  const [messageModalCustomer, setMessageModalCustomer] = useState<Customer | null>(null);
  
  // Status filter state
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['Open']); // Default to Open status only
  const [availableStatuses] = useState<string[]>(['Open', 'Won', 'Lost']);
  const [dropdownPosition, setDropdownPosition] = useState<{top: number, left: number, width: number} | null>(null);
  const statusButtonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  
  const [openStatusMenuId, setOpenStatusMenuId] = useState<number | null>(null);

  // Lead search (deals header) — results show in a dropdown under the search
  // bar and respect the current pipeline + status filter; the kanban board
  // itself is never filtered by the query.
  const [dealSearchOpen, setDealSearchOpen] = useState(false);
  const [dealSearchQuery, setDealSearchQuery] = useState('');
  const dealSearchWrapRef = React.useRef<HTMLDivElement>(null);
  const dealSearchInputRef = React.useRef<HTMLInputElement>(null);

  // Lost reason modal state
  const [recordLostReasonEnabled, setRecordLostReasonEnabled] = useState(true);
  const [isLostReasonModalOpen, setIsLostReasonModalOpen] = useState(false);
  const [pendingLostOpportunity, setPendingLostOpportunity] = useState<Opportunity | null>(null);
  const [pendingLostSource, setPendingLostSource] = useState<'card' | 'modal' | null>(null);

  // Mobile kanban swipe state
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  
  // Move dropdown state
  const [moveDropdownOpen, setMoveDropdownOpen] = useState<number | null>(null);
  
  // Mobile move animation state (iOS-style)
  const [moveAnimationOpportunity, setMoveAnimationOpportunity] = useState<Opportunity | null>(null);
  const [moveAnimationPosition, setMoveAnimationPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const opportunityCardRefs = React.useRef<{ [key: number]: HTMLDivElement | null }>({});
  const buttonsContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [buttonsOffset, setButtonsOffset] = useState(0);
  const [exitDirection, setExitDirection] = useState<'left' | 'right' | null>(null);
  const moveButtonTouchHandledRef = React.useRef<number | null>(null);
  const moveButtonRefs = React.useRef<{ [key: number]: HTMLButtonElement | null }>({});

  // Calculate proposal display total (max potential for unapproved, selected total for approved)
  const getProposalDisplayTotal = (proposal: Proposal | null): number => {
    if (!proposal) return 0;
    if (proposal.status === 'approved' || proposal.status === 'booked') {
      const approvedOffer = proposal.offers?.find(o => o.status === 'approved');
      if (approvedOffer) {
        const services = (approvedOffer.services || []).filter((s: any) => {
          const selectable = s.isSelectable || s.is_selectable;
          return !selectable || (selectable && s.is_selected);
        });
        const products = ((approvedOffer as any).products || []).filter((p: any) => {
          const selectable = p.isSelectable || p.is_selectable;
          return !selectable || (selectable && p.is_selected);
        });
        const svcSub = services.reduce((sum: number, s: any) => sum + (s.price || 0), 0);
        const svcDisc = services.reduce((sum: number, s: any) => sum + (s.discount_amount || 0), 0);
        const prodSub = products.reduce((sum: number, p: any) => sum + ((p.price || 0) * (p.quantity || 1)), 0);
        const prodDisc = products.reduce((sum: number, p: any) => sum + (p.discount_amount || 0), 0);
        const discSub = (svcSub + prodSub) - (svcDisc + prodDisc);
        const tax = services.reduce((sum: number, s: any) => sum + (s.tax_exempt ? 0 : ((s.price || 0) - (s.discount_amount || 0)) * ((s.tax_rate || 0) / 100)), 0)
          + products.reduce((sum: number, p: any) => sum + (p.tax_exempt ? 0 : (((p.price || 0) * (p.quantity || 1)) - (p.discount_amount || 0)) * ((p.tax_rate || 0) / 100)), 0);
        return Math.round((discSub + tax) * 100) / 100;
      }
      return proposal.total_amount;
    }
    // For non-approved: max potential of any single offer
    if (proposal.offers && proposal.offers.length > 0) {
      const offerTotals = proposal.offers.map(offer => {
        const services = offer.services || [];
        const products = (offer as any).products || [];
        const svcSub = services.reduce((sum: number, s: any) => sum + (s.price || 0), 0);
        const svcDisc = services.reduce((sum: number, s: any) => sum + (s.discount_amount || 0), 0);
        const prodSub = products.reduce((sum: number, p: any) => sum + ((p.price || 0) * (p.quantity || 1)), 0);
        const prodDisc = products.reduce((sum: number, p: any) => sum + (p.discount_amount || 0), 0);
        const discSub = (svcSub + prodSub) - (svcDisc + prodDisc);
        const tax = services.reduce((sum: number, s: any) => sum + (s.tax_exempt ? 0 : ((s.price || 0) - (s.discount_amount || 0)) * ((s.tax_rate || 0) / 100)), 0)
          + products.reduce((sum: number, p: any) => sum + (p.tax_exempt ? 0 : (((p.price || 0) * (p.quantity || 1)) - (p.discount_amount || 0)) * ((p.tax_rate || 0) / 100)), 0);
        return Math.round((discSub + tax) * 100) / 100;
      });
      return Math.max(...offerTotals);
    }
    return proposal.total_amount;
  };

  const isProposalMaxPotential = (proposal: Proposal | null): boolean => {
    if (!proposal) return false;
    return (proposal.status !== 'approved' && proposal.status !== 'booked')
      && (proposal.offers?.length ?? 0) > 1;
  };

  // Reset button offset when animation opens (buttons are now below card, no offset needed)
  React.useEffect(() => {
    if (isMobile && moveAnimationOpportunity) {
      setButtonsOffset(0);
    }
  }, [isMobile, moveAnimationOpportunity]);

  // Function to fetch pipelines from the database
  const fetchPipelines = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      setIsLoadingPipelines(true);

      const { data, error } = await supabase
        .from('pipelines')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('sort_order', { ascending: true })
        .order('pipeline_name', { ascending: true });

      if (error) {
        throw error;
      }
      
      const fetchedPipelines = data || [];
      
      // Transform pipelines to include parsed stages (stages are stored as JSON in the stages column)
      const pipelinesWithStages = fetchedPipelines.map(pipeline => ({
        ...pipeline,
        stages: Array.isArray(pipeline.stages) ? pipeline.stages : []
      }));

      setPipelines(pipelinesWithStages);
      
    } catch (err) {
      console.error('Error fetching pipelines:', err);
      setError('Failed to load pipelines. Please try again.');
    } finally {
      setIsLoadingPipelines(false);
    }
  };

  // Function to fetch opportunities from the database
  const fetchOpportunities = async () => {
    if (!selectedBusiness?.id || !supabase) return;

    try {
      setIsLoadingOpportunities(true);
      
      const { data: opportunitiesData, error: opportunitiesError } = await supabase
          .from('pipeline_opportunities')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .order('created_at', { ascending: false })
          .limit(500);
          
      if (opportunitiesError) throw opportunitiesError;
      if (!opportunitiesData) {
        setOpportunities([]);
        return;
      }

      const customerIds = [...new Set(opportunitiesData.map(opp => opp.customer_id).filter(Boolean))];
      const opportunityIds = opportunitiesData.map(opp => opp.id);

      // Fire ALL data fetches in parallel — single batch query each
      const [customersResult, proposalsResult, bookingsResult, projectsResult, fieldQuotesResult, salesPinsResult] = await Promise.all([
        // Batch customer fetch — single query instead of N individual getCustomer calls
        customerIds.length > 0
          ? supabase
              .from('customers')
              .select('*')
              .in('id', customerIds)
          : Promise.resolve({ data: [], error: null }),

        // Batch proposals — only the fields needed for card pills
        opportunityIds.length > 0
          ? supabase
              .from('proposals')
              .select('id, pipeline_opportunities_id, status, total_amount, valid_until')
              .eq('business_id', selectedBusiness.id)
              .neq('status', 'held')
              .in('pipeline_opportunities_id', opportunityIds)
              .not('pipeline_opportunities_id', 'is', null)
          : Promise.resolve({ data: [], error: null }),

        // Batch bookings — only the fields needed for card pills
        opportunityIds.length > 0
          ? supabase
              .from('bookings')
              .select('id, pipeline_opportunities_id, status, total_amount')
              .eq('business_id', selectedBusiness.id)
              .in('pipeline_opportunities_id', opportunityIds)
              .not('pipeline_opportunities_id', 'is', null)
          : Promise.resolve({ data: [], error: null }),

        // Batch projects — already lightweight
        opportunityIds.length > 0
          ? supabase
              .from('projects')
              .select('id, title, status, pipeline_opportunities_id')
              .eq('business_id', selectedBusiness.id)
              .eq('is_deleted', false)
              .in('pipeline_opportunities_id', opportunityIds)
              .not('pipeline_opportunities_id', 'is', null)
          : Promise.resolve({ data: [], error: null }),

        opportunityIds.length > 0
          ? supabase
              .from('project_field_quotes')
              .select('id, status, is_measurement, pipeline_opportunities_id')
              .eq('business_id', selectedBusiness.id)
              .in('pipeline_opportunities_id', opportunityIds)
              .not('pipeline_opportunities_id', 'is', null)
          : Promise.resolve({ data: [], error: null }),

        // Batch linked door-to-door sales pins — only the fields needed for card pills
        opportunityIds.length > 0
          ? supabase
              .from('address_markers')
              .select('id, opportunity_id, marker_type, address')
              .eq('business_id', selectedBusiness.id)
              .in('opportunity_id', opportunityIds)
              .not('opportunity_id', 'is', null)
          : Promise.resolve({ data: [], error: null }),
      ]);

      // Process customers
      const newCustomerMap = new Map<string, Customer>();
      if (!customersResult.error && customersResult.data) {
        (customersResult.data as Customer[]).forEach(customer => {
          if (customer.repeat === undefined) customer.repeat = false;
          newCustomerMap.set(customer.id, customer);
        });
      }
      
      const opportunitiesWithNames = opportunitiesData.map(opportunity => ({
        ...opportunity,
        customer_name: newCustomerMap.get(opportunity.customer_id) 
          ? `${newCustomerMap.get(opportunity.customer_id)?.first_name || ''} ${newCustomerMap.get(opportunity.customer_id)?.last_name || ''}`.trim() || 'Unknown Contact'
          : 'Unknown Contact'
      }));
      
      setOpportunities(opportunitiesWithNames);
      setCustomerMap(newCustomerMap);

      // Process proposals
      if (!proposalsResult.error && proposalsResult.data && proposalsResult.data.length > 0) {
        const newProposalMap = new Map<number, Proposal>();
        proposalsResult.data.forEach((proposal: any) => {
          if (proposal.pipeline_opportunities_id) {
            newProposalMap.set(proposal.pipeline_opportunities_id, proposal as Proposal);
          }
        });
        setProposalMap(newProposalMap);
      } else {
        setProposalMap(new Map());
      }

      // Process bookings
      if (!bookingsResult.error && bookingsResult.data && bookingsResult.data.length > 0) {
        const newBookingMap = new Map<number, Booking>();
        bookingsResult.data.forEach((booking: any) => {
          if (booking.pipeline_opportunities_id) {
            newBookingMap.set(booking.pipeline_opportunities_id, booking as Booking);
          }
        });
        setBookingMap(newBookingMap);
      } else {
        setBookingMap(new Map());
      }

      // Process projects
      if (!projectsResult.error && projectsResult.data && projectsResult.data.length > 0) {
        const newProjectMap = new Map<number, { id: string; title: string; status: string }>();
        projectsResult.data.forEach((project: any) => {
          if (project.pipeline_opportunities_id) {
            newProjectMap.set(project.pipeline_opportunities_id, {
              id: project.id,
              title: project.title || 'Untitled Project',
              status: project.status || 'open'
            });
          }
        });
        setProjectMap(newProjectMap);
      } else {
        setProjectMap(new Map());
      }

      if (!fieldQuotesResult.error && fieldQuotesResult.data && fieldQuotesResult.data.length > 0) {
        const newFqMap = new Map<number, { id: string; status: string; is_measurement: boolean }>();
        (fieldQuotesResult.data as any[]).forEach((fq: any) => {
          if (fq.pipeline_opportunities_id) {
            newFqMap.set(fq.pipeline_opportunities_id, {
              id: fq.id,
              status: fq.status || 'scheduled',
              is_measurement: !!fq.is_measurement
            });
          }
        });
        setFieldQuoteMap(newFqMap);
      } else {
        setFieldQuoteMap(new Map());
      }

      // Process linked sales pins
      if (!salesPinsResult.error && salesPinsResult.data && salesPinsResult.data.length > 0) {
        const newSalesPinMap = new Map<number, { id: string; marker_type: string; address?: string | null }>();
        (salesPinsResult.data as any[]).forEach((pin: any) => {
          if (pin.opportunity_id) {
            newSalesPinMap.set(pin.opportunity_id, {
              id: pin.id,
              marker_type: pin.marker_type || 'interested',
              address: pin.address
            });
          }
        });
        setSalesPinMap(newSalesPinMap);
      } else {
        setSalesPinMap(new Map());
      }
      
    } catch (err) {
      console.error('Error fetching opportunities:', err);
    } finally {
      setIsLoadingOpportunities(false);
    }
  };

  // Helper function to enrich a single opportunity with customer data
  const enrichOpportunityWithCustomer = async (opportunity: any): Promise<Opportunity> => {
    let customerName = 'Unknown Contact';
    
    // If we have customer data from the join
    if (opportunity.customers) {
      customerName = `${opportunity.customers.first_name || ''} ${opportunity.customers.last_name || ''}`.trim();
    } 
    // If no customer data from join, fetch it separately
    else if (opportunity.customer_id && supabase) {
      try {
        const { data: customerData } = await supabase
          .from('customers')
          .select('first_name, last_name')
          .eq('id', opportunity.customer_id)
          .single();
          
        if (customerData) {
          customerName = `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim();
        }
      } catch (customerError) {
        console.warn('Failed to fetch customer for opportunity:', opportunity.id, customerError);
      }
    }
    
    return {
      ...opportunity,
      customer_name: customerName || 'Unknown Contact'
    };
  };

  // Function to fetch available tags
  const fetchTags = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('tags')
        .select('id, name')
        .eq('business_id', selectedBusiness.id)
        .order('name');

      if (error) {
        // If table doesn't exist, just set empty array (graceful degradation)
        if (error.code === '42P01' || error.code === 'PGRST116') {
          setAvailableTags([]);
          return;
        }
        console.error('Error fetching tags:', error);
        return;
      }

      setAvailableTags(data || []);
    } catch (error: any) {
      // If table doesn't exist, just set empty array (graceful degradation)
      if (error?.code === '42P01' || error?.code === 'PGRST116') {
        setAvailableTags([]);
        return;
      }
      console.error('Error fetching tags:', error);
    }
  };

  // Function to create a new tag
  const createNewTag = async (name: string) => {
    if (!selectedBusiness?.id || !supabase) return null;
    
    try {
      const { data, error } = await supabase
        .from('tags')
        .insert({
          business_id: selectedBusiness.id,
          name: name.trim()
        })
        .select('id, name')
        .single();

      if (error) {
        console.error('Error creating tag:', error);
        return null;
      }

      // Add to available tags
      setAvailableTags(prev => [...prev, data]);
      return data;
    } catch (error) {
      console.error('Error creating tag:', error);
      return null;
    }
  };

  // Function to handle adding a new tag from the modal
  const handleAddNewTag = async () => {
    if (!newTagName.trim()) return;
    
    const newTag = await createNewTag(newTagName);
    if (newTag) {
      setSelectedTags(prev => [...prev, newTag]);
      setNewTagName('');
      setIsNewTagModalOpen(false);
    }
  };

  // Function to handle tag selection
  const handleTagSelect = (tag: {id: string, name: string}) => {
    if (!selectedTags.find(t => t.id === tag.id)) {
      setSelectedTags(prev => [...prev, tag]);
    }
  };

  // Function to remove a selected tag
  const handleTagRemove = (tagId: string) => {
    setSelectedTags(prev => prev.filter(t => t.id !== tagId));
  };

  // Load custom field definitions from database
  const loadCustomFieldDefinitions = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      const { data, error } = await supabase
        .from('opportunity_custom_fields')
        .select('id, field_name, field_type, is_required, display_order, options')
        .eq('business_id', selectedBusiness.id)
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error loading custom field definitions:', error);
        return;
      }

      // Transform database format to component format
      const definitions = (data || []).map(field => ({
        id: field.id,
        name: field.field_name,
        type: field.field_type,
        required: field.is_required === true,
        options: (field.options as CustomFieldOptions | null) || null
      }));

      setCustomFieldDefinitions(definitions);
    } catch (error) {
      console.error('ÃƒÂ¢Ã‚ÂÃ…â€™ Error loading custom field definitions:', error);
    }
  };

  // Initialize custom fields based on definitions
  const initializeCustomFields = () => {
    const initialFields = customFieldDefinitions.map(def => ({
      id: def.id,
      name: def.name,
      type: def.type,
      value: ''
    }));
    setCustomFields(initialFields);
    setCustomModeFieldIds(new Set());
  };

  // "Custom Fields" CRM app setting (off by default). When on, configured
  // fields render on the lead form and required ones block Nexus creation.
  // External inserts (webhooks, automations) are intentionally never blocked.
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) {
      setCustomFieldsEnabled(false);
      return;
    }
    let cancelled = false;
    void supabase
      .rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: 'custom_fields_enabled',
        p_default_value: false,
      })
      .then(({ data }) => {
        if (!cancelled) setCustomFieldsEnabled(data === true);
      });

    const onSettingChanged = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.key === 'custom_fields_enabled') {
        setCustomFieldsEnabled(!!detail.value);
        if (detail.value) void loadCustomFieldDefinitions();
      }
    };
    const onDefinitionsChanged = () => {
      void loadCustomFieldDefinitions();
    };
    window.addEventListener('crmSettingChanged', onSettingChanged);
    window.addEventListener(CUSTOM_FIELDS_CHANGED_EVENT, onDefinitionsChanged);
    return () => {
      cancelled = true;
      window.removeEventListener('crmSettingChanged', onSettingChanged);
      window.removeEventListener(CUSTOM_FIELDS_CHANGED_EVENT, onDefinitionsChanged);
    };
  }, [selectedBusiness?.id]);

  // NOTE: the status filter used to be a persisted multi-select backed by the
  // pipeline_status_filters table. The UI became a single-select, but the old
  // loader kept restoring legacy multi-status rows (e.g. ['Open','Won','Lost'])
  // — the dropdown showed only the first ("Open") while the board matched all
  // of them. The filter is now purely in-memory: defaults to Open, and the
  // board always matches the dropdown.

  // Close status filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isStatusFilterOpen && !target.closest('.relative')) {
        setIsStatusFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isStatusFilterOpen]);

  // Initialize custom fields when definitions change
  useEffect(() => {
    initializeCustomFields();
  }, [customFieldDefinitions]);

  // Load critical data first (pipelines and opportunities) for faster initial render
  useEffect(() => {
    if (selectedBusiness?.id) {
      // Load critical data first
      Promise.all([
        fetchPipelines(),
        fetchOpportunities()
      ]).catch(err => {
        console.error('Error loading initial data:', err);
      });
      
      // Lazy load non-critical data after a short delay
      setTimeout(() => {
        Promise.all([
          fetchTags(),
          loadCustomFieldDefinitions()
        ]).catch(err => {
          console.error('Error loading secondary data:', err);
        });
      }, 100);
    }
  }, [selectedBusiness?.id]);

  // Load CRM lost reason setting
  useEffect(() => {
    if (!selectedBusiness?.id) return;
    const loadLostReasonSetting = async () => {
      try {
        const { data } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'crm',
          p_setting_key: 'record_lost_reason',
          p_default_value: true
        });
        if (data !== null) setRecordLostReasonEnabled(data);
      } catch (err) {
        console.error('Error loading lost reason setting:', err);
      }
    };
    loadLostReasonSetting();
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (!isOpportunityModalOpen || !selectedBusiness?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await getAgents(selectedBusiness.id, { kind: 'lead' });
        if (!cancelled) setLeadAgentsForPicker(list);
      } catch (err) {
        console.error('Failed to load lead agents:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpportunityModalOpen, selectedBusiness?.id]);

  // Set default pipeline for opportunities view when pipelines are loaded
  useEffect(() => {
    if (pipelines.length > 0 && !opportunitiesViewPipeline) {
      setOpportunitiesViewPipeline(pipelines[0]);
    }
  }, [pipelines, opportunitiesViewPipeline]);

  // Re-fill pipeline/stage when pipelines finish loading after the drawer opened (control centre embed).
  useEffect(() => {
    if (!isOpportunityModalOpen || !selectedOpportunityForInfo || pipelines.length === 0) return;
    if (selectedPipeline && selectedStage) return;

    const { pipeline, stage } = resolveOpportunityPipelineStage(pipelines, selectedOpportunityForInfo);
    if (!pipeline || !stage) return;

    if (!selectedPipeline && !selectedStage) {
      setSelectedPipeline(pipeline);
      setSelectedStage(stage);
    }
  }, [
    isOpportunityModalOpen,
    selectedOpportunityForInfo,
    pipelines,
    selectedPipeline,
    selectedStage,
  ]);

  // Handle navigation state to open opportunity modal
  useEffect(() => {
    const navigationState = location.state as { openOpportunityId?: number } | null;
    if (navigationState?.openOpportunityId && selectedBusiness?.id && supabase && pipelines.length > 0) {
      console.log('ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ‚Â Navigation state (open opportunity):', navigationState.openOpportunityId);
      
      // Ensure we're on the opportunities tab
      if (activeTab !== 'opportunities') {
        setActiveTab('opportunities');
      }
      
      const openOpportunity = async () => {
        // First check if opportunity is already in the list
        let opportunity = opportunities.find(opp => opp.id === navigationState.openOpportunityId);
        
        // If not found in list, fetch it directly
        if (!opportunity) {
          console.log('ÃƒÂ°Ã…Â¸Ã¢â‚¬Å“Ã‚Â¥ Opportunity not in list, fetching from database...');
          try {
            const { data, error } = await supabase
              .from('pipeline_opportunities')
              .select('*')
              .eq('id', navigationState.openOpportunityId)
              .eq('business_id', selectedBusiness.id)
              .single();
            
            if (!error && data) {
              // Enrich with customer data
              if (data.customer_id) {
                try {
                  const customer = await getCustomer(data.customer_id);
                  opportunity = {
                    ...data,
                    customer_name: customer 
                      ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown Contact'
                      : 'Unknown Contact',
                    customers: customer ? {
                      id: customer.id,
                      first_name: customer.first_name || '',
                      last_name: customer.last_name || ''
                    } : undefined
                  };
                } catch (err) {
                  console.error('Error fetching customer for opportunity:', err);
                  opportunity = {
                    ...data,
                    customer_name: 'Unknown Contact'
                  };
                }
              } else {
                opportunity = {
                  ...data,
                  customer_name: 'Unknown Contact'
                };
              }
            } else {
              console.error('ÃƒÂ¢Ã‚ÂÃ…â€™ Error fetching opportunity:', error);
            }
          } catch (err) {
            console.error('ÃƒÂ¢Ã‚ÂÃ…â€™ Exception fetching opportunity:', err);
          }
        }
        
        if (opportunity) {
          // Use the same function that's used when clicking an opportunity card
          // This ensures all the form data is properly loaded
          handleOpenOpportunityInfo(opportunity);
          // Clear the navigation state after a short delay to ensure modal opens
          setTimeout(() => {
            window.history.replaceState({}, document.title);
          }, 100);
        } else {
          console.error('ÃƒÂ¢Ã‚ÂÃ…â€™ Opportunity not found');
        }
      };
      
      // Small delay to ensure component is fully mounted and tab is switched
      const timeoutId = setTimeout(() => {
        openOpportunity();
      }, 200);
      
      return () => clearTimeout(timeoutId);
    }
  }, [location.state, opportunities, supabase, selectedBusiness?.id, activeTab, pipelines]);

  // Memoize filtered opportunities by stage to avoid recalculating on every render
  const opportunitiesByStage = useMemo(() => {
    if (!opportunitiesViewPipeline?.stages || !opportunities.length) {
      return new Map();
    }
    
    const map = new Map<number, Opportunity[]>();
    const stages = opportunitiesViewPipeline.stages;
    stages.forEach(stage => {
      const filtered = opportunities.filter(opp => 
        opp.pipeline_id === opportunitiesViewPipeline.id && 
        opp.stage_id === stage.id &&
        selectedStatuses.includes(opp.status)
      );
      map.set(stage.id, filtered);
    });
    const loose = opportunities.filter(opp =>
      opp.pipeline_id === opportunitiesViewPipeline.id &&
      selectedStatuses.includes(opp.status) &&
      !stageStillExists(opp.stage_id, stages)
    );
    if (loose.length) map.set(UNASSIGNED_STAGE_ID, loose);
    return map;
  }, [opportunities, opportunitiesViewPipeline, selectedStatuses]);

  const boardStages = useMemo(
    () => withUnassignedStage(opportunitiesViewPipeline?.stages || [], opportunitiesByStage.get(UNASSIGNED_STAGE_ID)?.length || 0),
    [opportunitiesViewPipeline, opportunitiesByStage],
  );

  // Lead search results: same pipeline + status filter as the board, walked in
  // stage order so results group naturally. Empty query lists every lead under
  // the current filter.
  const dealSearchResults = useMemo(() => {
    if (!dealSearchOpen || !opportunitiesViewPipeline?.stages) return [];
    const q = dealSearchQuery.trim().toLowerCase();
    const results: { opportunity: Opportunity; stageName: string }[] = [];
    boardStages.forEach(stage => {
      const opps = (opportunitiesByStage.get(stage.id) || []) as Opportunity[];
      opps.forEach(opp => {
        if (q) {
          const customer =
            opp.customer_name ||
            [opp.customers?.first_name, opp.customers?.last_name].filter(Boolean).join(' ');
          const haystack =
            `${opp.title} ${customer} ${opp.business_name || ''} ${opp.source || ''} ${opp.tags || ''}`.toLowerCase();
          if (!haystack.includes(q)) return;
        }
        results.push({ opportunity: opp, stageName: stage.name });
      });
    });
    return results;
  }, [dealSearchOpen, dealSearchQuery, opportunitiesViewPipeline, opportunitiesByStage, boardStages]);

  // Focus the search input when opened; close on Escape or outside click.
  useEffect(() => {
    if (!dealSearchOpen) return;
    dealSearchInputRef.current?.focus();
    const close = () => {
      setDealSearchOpen(false);
      setDealSearchQuery('');
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (dealSearchWrapRef.current && !dealSearchWrapRef.current.contains(t)) close();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [dealSearchOpen]);

  // Memoize stage totals to avoid recalculating on every render
  const stageTotals = useMemo(() => {
    const totals = new Map<number, number>();
    opportunitiesByStage.forEach((opps, stageId) => {
      totals.set(stageId, opps.reduce((sum: number, opp: Opportunity) => sum + (opp.value || 0), 0));
    });
    return totals;
  }, [opportunitiesByStage]);

  // Set up real-time subscription for live opportunity updates
  // Defer until after initial data loads to improve initial page load time
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase || isLoadingOpportunities || isLoadingPipelines) return;

    // Wait a bit after initial load to set up real-time subscription
    const timeoutId = setTimeout(() => {
      const channel = supabase
        .channel(`crm-opportunities-${selectedBusiness.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'pipeline_opportunities',
            filter: `business_id=eq.${selectedBusiness.id}`,
          },
          async (payload) => {
            const newOpportunity = payload.new as any;
            
            // Check if opportunity already exists in state (prevent duplicates from manual creation)
            setOpportunities(prev => {
              const exists = prev.some(opp => opp.id === newOpportunity.id);
              if (exists) return prev;
              return prev; // Return unchanged, we'll update after enrichment
            });
            
            // Enrich with customer data and add to state
            try {
              const enrichedOpportunity = await enrichOpportunityWithCustomer(newOpportunity);
              setOpportunities(prev => {
                // Double-check it still doesn't exist (race condition protection)
                const exists = prev.some(opp => opp.id === enrichedOpportunity.id);
                if (exists) {
                  return prev;
                }
                return [enrichedOpportunity, ...prev];
              });
            } catch (error) {
              console.error('Error enriching new opportunity:', error);
              // Still add it without customer data if enrichment fails
              setOpportunities(prev => {
                const exists = prev.some(opp => opp.id === newOpportunity.id);
                if (exists) {
                  return prev;
                }
                return [{
                  ...newOpportunity,
                  customer_name: 'Unknown Contact'
                }, ...prev];
              });
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'pipeline_opportunities',
            filter: `business_id=eq.${selectedBusiness.id}`,
          },
          async (payload) => {
            const updatedOpportunity = payload.new as any;
            
            // Enrich with customer data and update in state
            try {
              const enrichedOpportunity = await enrichOpportunityWithCustomer(updatedOpportunity);
              setOpportunities(prev => 
                prev.map(opp => opp.id === enrichedOpportunity.id ? enrichedOpportunity : opp)
              );
            } catch (error) {
              console.error('Error enriching updated opportunity:', error);
              // Still update it without customer data if enrichment fails
              setOpportunities(prev => 
                prev.map(opp => opp.id === updatedOpportunity.id ? {
                  ...updatedOpportunity,
                  customer_name: opp.customer_name || 'Unknown Contact'
                } : opp)
              );
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'pipeline_opportunities',
            filter: `business_id=eq.${selectedBusiness.id}`,
          },
          (payload) => {
            const deletedOpportunity = payload.old as any;
            setOpportunities(prev => prev.filter(opp => opp.id !== deletedOpportunity.id));
            // Also remove from proposal map if it was linked
            setProposalMap(prev => {
              const newMap = new Map(prev);
              newMap.delete(deletedOpportunity.id);
              return newMap;
            });
          }
        )
        .subscribe();

      // Set up realtime subscription for proposals to keep proposalMap in sync
      const proposalChannel = supabase
        .channel(`crm-proposals-${selectedBusiness.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'proposals',
            filter: `business_id=eq.${selectedBusiness.id}`,
          },
          async (payload: any) => {
            const isDelete = payload.eventType === 'DELETE' || (payload.old && !payload.new);
            
            if (isDelete) {
              // Remove from proposal map
              const deletedProposal = payload.old as any;
              if (deletedProposal.pipeline_opportunities_id) {
                setProposalMap(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(deletedProposal.pipeline_opportunities_id);
                  return newMap;
                });
              }
            } else if (payload.new) {
              // Handle INSERT or UPDATE
              const proposalData = payload.new as any;
              if (proposalData.pipeline_opportunities_id) {
                try {
                  // Fetch full proposal data
                  const fullProposal = await getProposal(proposalData.id);
                  if (fullProposal) {
                    setProposalMap(prev => {
                      const newMap = new Map(prev);
                      newMap.set(fullProposal.pipeline_opportunities_id!, fullProposal);
                      return newMap;
                    });
                  }
                } catch (error) {
                  console.error('Error loading proposal for proposalMap:', error);
                }
              }
            }
          }
        )
        .subscribe();

      // Set up realtime subscription for bookings to keep bookingMap in sync
      const bookingChannel = supabase
        .channel(`crm-bookings-${selectedBusiness.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'bookings',
            filter: `business_id=eq.${selectedBusiness.id}`,
          },
          async (payload: any) => {
            const isDelete = payload.eventType === 'DELETE' || (payload.old && !payload.new);
            
            if (isDelete) {
              // Remove from booking map
              const deletedBooking = payload.old as any;
              if (deletedBooking.pipeline_opportunities_id) {
                setBookingMap(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(deletedBooking.pipeline_opportunities_id);
                  return newMap;
                });
              }
            } else if (payload.new) {
              // Handle INSERT or UPDATE
              const bookingData = payload.new as any;
              if (bookingData.pipeline_opportunities_id) {
                try {
                  // Fetch full booking data
                  const fullBooking = await getBooking(bookingData.id);
                  if (fullBooking) {
                    setBookingMap(prev => {
                      const newMap = new Map(prev);
                      newMap.set((fullBooking as any).pipeline_opportunities_id!, fullBooking);
                      return newMap;
                    });
                  }
                } catch (error) {
                  console.error('Error loading booking for bookingMap:', error);
                }
              }
            }
          }
        )
        .subscribe();

      // Set up realtime subscription for projects to keep projectMap in sync
      const projectChannel = supabase
        .channel(`crm-projects-${selectedBusiness.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'projects',
            filter: `business_id=eq.${selectedBusiness.id}`,
          },
          async (payload: any) => {
            const isDelete = payload.eventType === 'DELETE' || (payload.old && !payload.new);
            
            if (isDelete) {
              // Remove from project map
              const deletedProject = payload.old as any;
              if (deletedProject.pipeline_opportunities_id) {
                setProjectMap(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(deletedProject.pipeline_opportunities_id);
                  return newMap;
                });
              }
            } else if (payload.new) {
              // Handle INSERT or UPDATE
              const projectData = payload.new as any;
              // Always ignore deleted projects - never add or keep them in the map
              if (projectData.is_deleted) {
                // Remove from map if project is marked as deleted
                if (projectData.pipeline_opportunities_id) {
                  setProjectMap(prev => {
                    const newMap = new Map(prev);
                    newMap.delete(projectData.pipeline_opportunities_id);
                    return newMap;
                  });
                }
              } else if (projectData.pipeline_opportunities_id) {
                // Only add/update if project is not deleted and has a linked opportunity
                setProjectMap(prev => {
                  const newMap = new Map(prev);
                  newMap.set(projectData.pipeline_opportunities_id, {
                    id: projectData.id,
                    title: projectData.title || 'Untitled Project',
                    status: projectData.status || 'open'
                  });
                  return newMap;
                });
              } else if (payload.old?.pipeline_opportunities_id && !projectData.pipeline_opportunities_id) {
                // Project was unlinked (pipeline_opportunities_id changed from value to null)
                setProjectMap(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(payload.old.pipeline_opportunities_id);
                  return newMap;
                });
              }
            }
          }
        )
        .subscribe();

      // Store channels for cleanup
      channelsRef.current = { channel, proposalChannel, bookingChannel, projectChannel };
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      if (channelsRef.current.channel) {
        supabase.removeChannel(channelsRef.current.channel);
      }
      if (channelsRef.current.proposalChannel) {
        supabase.removeChannel(channelsRef.current.proposalChannel);
      }
      if (channelsRef.current.bookingChannel) {
        supabase.removeChannel(channelsRef.current.bookingChannel);
      }
      if (channelsRef.current.projectChannel) {
        supabase.removeChannel(channelsRef.current.projectChannel);
      }
      channelsRef.current = {};
    };
  }, [selectedBusiness?.id, isLoadingOpportunities, isLoadingPipelines]);

  const addStage = () => {
    const newPosition = stages.length + 1;
    setStages([...stages, { id: Date.now(), name: '', position: newPosition }]);
  };

  const updateStageName = (id: number, name: string) => {
    setStages(stages.map(stage => 
      stage.id === id ? { ...stage, name } : stage
    ));
  };

  const removeStage = (id: number) => {
    if (stages.length > 1) {
      const filteredStages = stages.filter(stage => stage.id !== id);
      // Reorder positions after removing
      const updatedStages = filteredStages.map((stage, index) => ({
        ...stage,
        position: index + 1
      }));
      setStages(updatedStages);
    }
  };

  const handleDragStart = (e: React.DragEvent, id: number) => {
    setDraggedStageId(id);
    e.dataTransfer.effectAllowed = 'move';
    // Set a ghost image for dragging (optional)
    const draggedElement = e.currentTarget as HTMLElement;
    if (draggedElement) {
      const ghost = draggedElement.cloneNode(true) as HTMLElement;
      ghost.style.position = 'absolute';
      ghost.style.top = '-1000px';
      document.body.appendChild(ghost);
      e.dataTransfer.setDragImage(ghost, 0, 0);
      setTimeout(() => {
        document.body.removeChild(ghost);
      }, 0);
    }
  };

  const handleDragOver = (e: React.DragEvent, id: number) => {
    e.preventDefault();
    if (draggedStageId === null || draggedStageId === id) return;

    // Find positions of the dragged and target stages
    const draggedStage = stages.find(stage => stage.id === draggedStageId);
    const targetStage = stages.find(stage => stage.id === id);
    
    if (!draggedStage || !targetStage) return;
    
    // Create a new array with reordered stages
    const draggedPosition = draggedStage.position || 0;
    const targetPosition = targetStage.position || 0;
    
    const newStages = stages.map(stage => {
      if (stage.id === draggedStageId) {
        return { ...stage, position: targetPosition };
      } else if (
        (draggedPosition < targetPosition && 
          stage.position && 
          stage.position > draggedPosition && 
          stage.position <= targetPosition)
      ) {
        return { ...stage, position: (stage.position || 0) - 1 };
      } else if (
        (draggedPosition > targetPosition && 
          stage.position && 
          stage.position < draggedPosition && 
          stage.position >= targetPosition)
      ) {
        return { ...stage, position: (stage.position || 0) + 1 };
      }
      return stage;
    });
    
    // Sort by position and update
    setStages([...newStages].sort((a, b) => (a.position || 0) - (b.position || 0)));
  };

  const handleDragEnd = () => {
    setDraggedStageId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedBusiness?.id || !pipelineName.trim() || !supabase) {
      return;
    }
    
    // Validate that at least one stage has a name
    const validStages = stages.filter(stage => stage.name.trim() !== '');
    if (validStages.length === 0) {
      alert('Please add at least one stage with a name');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Format stages for storage - ensure stages have position property
      const formattedStages = validStages.map((stage, index) => ({
        id: stage.id,
        name: stage.name.trim(),
        position: stage.position || index + 1
      }));
      
      // Data to insert/update
      const pipelineData: any = { 
        business_id: selectedBusiness.id,
        pipeline_name: pipelineName.trim(),
        stages: formattedStages
      };
      
      let error;
      
      if (isEditMode && currentPipelineId) {
        // Update existing pipeline
        const { error: updateError } = await supabase
          .from('pipelines')
          .update(pipelineData)
          .eq('id', currentPipelineId);
        
        error = updateError;
      } else {
        // New pipelines go to the end of the list
        pipelineData.sort_order = pipelines.length;
        const { error: insertError } = await supabase
          .from('pipelines')
          .insert([pipelineData])
          .select();
        
        error = insertError;
      }
      
      if (error) throw error;
      
      // Refresh the pipelines list
      fetchPipelines();
      
      // Close modal and reset form
      handleCloseModal();
      
    } catch (err) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} pipeline:`, err);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} pipeline. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (editMode: boolean = false, pipeline?: Pipeline) => {
    setIsEditMode(editMode);
    
    if (editMode && pipeline) {
      // Load pipeline data for editing
      setCurrentPipelineId(pipeline.id);
      setPipelineName(pipeline.pipeline_name);
      
      // Load stages if they exist, otherwise create a default one
      if (pipeline.stages && pipeline.stages.length > 0) {
        setStages(pipeline.stages.map(stage => ({
          ...stage,
          id: typeof stage.id === 'number' ? stage.id : Date.now() + Math.floor(Math.random() * 1000)
        })));
      } else {
        setStages([{ id: Date.now(), name: '', position: 1 }]);
      }
    } else {
      // Reset for create mode
      setCurrentPipelineId(null);
      setPipelineName('');
      setStages([{ id: 1, name: '', position: 1 }]);
    }
    
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setCurrentPipelineId(null);
    setPipelineName('');
    setStages([{ id: 1, name: '', position: 1 }]);
    setOpportunityValue(0);
    setBusinessName('');
    setOpportunitySource('');
    setCustomFields([]);
  };

  // Function to delete a pipeline
  const handleDeletePipeline = async (id: number) => {
    if (!supabase || !window.confirm('Are you sure you want to delete this pipeline?')) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('pipelines')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      // Refresh the list
      fetchPipelines();
      
    } catch (err) {
      console.error('Error deleting pipeline:', err);
      alert('Failed to delete the pipeline. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const [draggingPipelineId, setDraggingPipelineId] = useState<number | null>(null);

  const handlePipelineDragStart = (e: React.DragEvent, pipelineId: number) => {
    setDraggingPipelineId(pipelineId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePipelineDragOver = (e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    if (draggingPipelineId === null || draggingPipelineId === targetId) return;

    setPipelines(prev => {
      const fromIndex = prev.findIndex(p => p.id === draggingPipelineId);
      const toIndex = prev.findIndex(p => p.id === targetId);
      if (fromIndex === -1 || toIndex === -1) return prev;

      const reordered = [...prev];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);
      return reordered;
    });
  };

  const handlePipelineDragEnd = async () => {
    setDraggingPipelineId(null);
    if (!supabase) return;

    try {
      const updates = pipelines.map((pipeline, index) => 
        supabase!.from('pipelines').update({ sort_order: index }).eq('id', pipeline.id)
      );
      await Promise.all(updates);
    } catch (err) {
      console.error('Error saving pipeline order:', err);
      fetchPipelines();
    }
  };

  // This function will render the kanban board stage columns
  const renderStageColumns = () => {
    // If no pipelines exist at all, show "create pipeline" message
    if (pipelines.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Pipelines Created</h3>
            <p className="text-gray-500 mb-4">Create a pipeline to start managing your opportunities.</p>
            <button 
              onClick={() => setActiveTab('pipelines')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Create Your First Pipeline
            </button>
          </div>
        </div>
      );
    }

    // If pipeline exists but has no stages, show empty stage message
    if (!opportunitiesViewPipeline || !opportunitiesViewPipeline.stages || opportunitiesViewPipeline.stages.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Stages in Pipeline</h3>
            <p className="text-gray-500 mb-4">This pipeline doesn't have any stages. Edit the pipeline to add stages.</p>
            <button 
              onClick={() => setActiveTab('pipelines')}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
            >
              Edit Pipeline
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex space-x-4 overflow-x-auto pb-4">
        {opportunitiesViewPipeline.stages.map(stage => {
          // Filter opportunities for this stage and selected statuses
          const stageOpportunities = opportunities.filter(opp => 
            opp.pipeline_id === opportunitiesViewPipeline.id && 
            opp.stage_id === stage.id &&
            selectedStatuses.includes(opp.status)
          );
          
          // Calculate total value for this stage
          const totalValue = stageOpportunities.reduce((sum, opp) => sum + (opp.value || 0), 0);
          
          return (
            <div key={stage.id} className="bg-gray-100 rounded-md p-3 min-w-[300px] flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-700">{stage.name}</h3>
                <div className="text-xs text-gray-500">
                  {stageOpportunities.length} Opportunities <span className="ml-2">C${totalValue.toFixed(2)}</span>
                </div>
              </div>
              
              {/* Opportunity Cards */}
              <div className="space-y-3">
                {stageOpportunities.map(opportunity => (
                  <div 
                    key={opportunity.id} 
                    className="bg-white border border-gray-200 rounded-md p-3 relative cursor-pointer hover:shadow-md transition-shadow overflow-hidden"
                    onClick={() => handleOpenOpportunityInfo(opportunity)}
                  >
                    {/* Unread indicator */}
                    {opportunity.is_unread && (
                      <div className="absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white z-10"></div>
                    )}
                    
                    {/* Delete button */}
                    <button 
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center text-xs hover:bg-red-800 transition-colors"
                      onClick={async (e) => {
                        e.stopPropagation(); // Prevent triggering the card click
                        handleDeleteOpportunityFromCard(opportunity);
                      }}
                      title="Delete opportunity"
                      disabled={isLoading}
                    >
                      <X className="h-4 w-4" />
                    </button>
                    
                    {/* Opportunity Title */}
                    <h4 className="font-medium text-gray-900 mb-2 pr-8">{opportunity.title}</h4>

                    {opportunity.next_follow_up_at ? (
                      <div className="mb-3">
                        <CrmFollowUpDueBadge nextFollowUpAt={opportunity.next_follow_up_at} />
                      </div>
                    ) : null}
                    
                    {/* Opportunity Details */}
                    <div className="space-y-2 mb-3">
                      <div className="flex text-sm">
                        <span className="text-blue-600 w-32">Opportunity Source:</span>
                        <span className="text-gray-600">{opportunity.source || 'Direct'}</span>
                      </div>
                      <div className="flex text-sm">
                        <span className="text-blue-600 w-32">Opportunity Value:</span>
                        <span className="text-gray-600">C${(opportunity.value || 0).toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Linked Items - Minimal pills */}
                    {(() => {
                      const proposal = proposalMap.get(opportunity.id);
                      const booking = bookingMap.get(opportunity.id);
                      const project = projectMap.get(opportunity.id);
                      const fieldQuote = fieldQuoteMap.get(opportunity.id);
                      const salesPin = salesPinMap.get(opportunity.id);
                      const assignedName = resourceDisplayName(opportunity.assigned_resource_id);
                      if (!proposal && !booking && !project && !fieldQuote && !salesPin && !assignedName) return null;
                      return (
                        <div className="mb-2 -mt-1 flex flex-wrap gap-1.5">
                          {assignedName && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-600 bg-gray-100 pl-0.5 pr-2 py-0.5 rounded-full">
                              <span className="h-3.5 w-3.5 rounded-full bg-blue-100 text-blue-700 text-[7px] font-bold flex items-center justify-center">
                                {assignedName.split(/\s+/).map((p) => p.charAt(0)).join('').slice(0, 2).toUpperCase()}
                              </span>
                              {assignedName}
                            </span>
                          )}
                          {salesPin && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full"
                              style={{
                                color: addressMarkerService.getMarkerColor(salesPin.marker_type as AddressMarker['marker_type']),
                                backgroundColor: `${addressMarkerService.getMarkerColor(salesPin.marker_type as AddressMarker['marker_type'])}1A`,
                              }}
                            >
                              <MapPin className="w-2.5 h-2.5" />
                              Sales Pin · {addressMarkerService.getMarkerTypeLabel(salesPin.marker_type as AddressMarker['marker_type'])}
                            </span>
                          )}
                          {proposal && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                              <FileText className="w-2.5 h-2.5" />
                              Proposal · <span className="capitalize">{proposal.status}</span>
                            </span>
                          )}
                          {proposal && <ProposalExpiryBadge validUntil={proposal.valid_until} />}
                          {booking && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                              <Calendar className="w-2.5 h-2.5" />
                              Booking · {formatCurrency(booking.total_amount || 0)}
                            </span>
                          )}
                          {fieldQuote && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-800 bg-amber-50 px-2 py-0.5 rounded-full">
                              <NotebookPen className="w-2.5 h-2.5" />
                              {fieldQuote.is_measurement ? 'Measurement' : 'Field quote'} ·{' '}
                              <span className="capitalize">{(fieldQuote.status || '').replace('_', ' ')}</span>
                            </span>
                          )}
                          {project && (
                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">
                              <Folder className="w-2.5 h-2.5" />
                              Project
                            </span>
                          )}
                        </div>
                      );
                    })()}
                    
                    {/* Action Icons */}
                    <div className="flex space-x-2 pt-2 border-t border-gray-100">
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Call"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Message"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenMessageModal(opportunity);
                        }}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-blue-600 hover:text-blue-800" 
                        title="Favorite"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Document"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Email"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                          <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                        </svg>
                      </button>
                      <button 
                        className="p-1 text-gray-400 hover:text-gray-600" 
                        title="Calendar"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                        </svg>
                      </button>
                      
                      {/* Move to Stage Button - mobile only; desktop uses drag-and-drop */}
                      {isMobile && (
                        <div className="relative ml-auto">
                          <button 
                            className="px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded transition-colors" 
                            title="Move to different stage"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMoveDropdownOpen(moveDropdownOpen === opportunity.id ? null : opportunity.id);
                            }}
                          >
                            Move
                          </button>
                          
                          {/* Quick Move Dropdown */}
                          {moveDropdownOpen === opportunity.id && opportunitiesViewPipeline?.stages && (
                            <div className="absolute bottom-full right-0 mb-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                              <div className="py-2">
                                {opportunitiesViewPipeline.stages.map(stage => (
                                  <button
                                    key={stage.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleQuickMove(opportunity.id, stage.id);
                                    }}
                                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                                      stage.id === opportunity.stage_id ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
                                    }`}
                                  >
                                    {stage.name}
                                    {stage.id === opportunity.stage_id && ' (current)'}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderNoOpportunitiesState = () => (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-40 h-40 mb-6">
        <img 
          src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgdmlld0JveD0iMCAwIDE2MCAxNjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPGcgb3BhY2l0eT0iMC44Ij4KICAgIDxjaXJjbGUgY3g9IjgwIiBjeT0iODAiIHI9IjgwIiBmaWxsPSIjRjVGNUY1Ii8+CiAgICA8cGF0aCBkPSJNMTE3LjQ0OSA0Mi41NTExQzEyMS4zNjMgNDYuNDY1MSAxMjEuMzYzIDUyLjc5ODIgMTE3LjQ0OSA1Ni43MTIyTDU2LjcxMjIgMTE3LjQ0OUM1Mi43OTgyIDEyMS4zNjMgNDYuNDY1MSAxMjEuMzYzIDQyLjU1MTEgMTE3LjQ0OUMzOC42MzcxIDExMy41MzUgMzguNjM3MSAxMDcuMjAyIDQyLjU1MTEgMTAzLjI4OEwxMDMuMjg4IDQyLjU1MTFDMTAzLjI4OCA0Mi41NTExIDEwNy4yMDIgMzguNjM3MSAxMTcuNDQ5IDQyLjU1MTFaIiBzdHJva2U9IiNCRUJFQkUiIHN0cm9rZS13aWR0aD0iMiIvPgogICAgPHJlY3QgeD0iNjUiIHk9IjY1IiB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHJ4PSIxIiBmaWxsPSJ3aGl0ZSIgc3Ryb2tlPSIjQkVCRUJFIiBzdHJva2Utd2lkdGg9IjIiLz4KICAgIDxjaXJjbGUgY3g9IjgwIiBjeT0iODAiIHI9IjEwIiBzdHJva2U9IiNGRjZCNkIiIHN0cm9rZS13aWR0aD0iMiIvPgogICAgPHBhdGggZD0iTTc1IDgwTDc4IDgzTDg1IDc2IiBzdHJva2U9IiNGRjZCNkIiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+CiAgPC9nPgo8L3N2Zz4K" 
          alt="No opportunities found" 
          className="w-full h-full"
        />
      </div>
      <h3 className="text-lg font-medium text-gray-700">No Opportunities Match the Filter's Set</h3>
      <p className="text-gray-500 text-center mt-2 max-w-md">
        Try adjusting your filters to find the Opportunities you're looking for.
      </p>
      <div className="flex mt-4 space-x-3">
        <button className="flex items-center bg-blue-500 text-white px-4 py-2 rounded-md">
          <Filter size={16} className="mr-2" /> Update Filter
        </button>
        <button className="flex items-center bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md">
          <X size={16} className="mr-2" /> Clear Filter
        </button>
      </div>
    </div>
  );

  // Handle customer selection and set default title
  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomer(customer);
    setOpportunityTitle(customer.first_name); // Set default title to customer's first name
    setIsCustomerModalOpen(false);
  };

  // Handle customer removal
  const handleCustomerRemove = () => {
    setSelectedCustomer(null);
    setOpportunityTitle(''); // Clear title when customer is removed
  };

  // Handle pipeline selection and clear stage
  const handlePipelineSelect = (pipeline: Pipeline | null) => {
    setSelectedPipeline(pipeline);
    setSelectedStage(null); // Clear stage when pipeline changes
  };

  // Handle opening modal with current pipeline context
  // Resources for the Assigned To picker + the current user's own resource
  // (creator tracking / default assignment).
  useEffect(() => {
    if (!selectedBusiness?.id) return;
    let cancelled = false;
    getResources(selectedBusiness.id)
      .then((rows) => {
        if (!cancelled) setCrmResources(rows || []);
      })
      .catch((err) => console.error('Error loading resources for CRM:', err));
    resolveLeadCreator(selectedBusiness.id)
      .then((creator) => {
        if (cancelled) return;
        setMyUserId(creator.userId);
        setMyResourceId(creator.resourceId);
      })
      .catch((err) => console.error('Error resolving lead creator:', err));
    return () => {
      cancelled = true;
    };
  }, [selectedBusiness?.id]);

  // Creator context can resolve after the create drawer already opened (e.g. the
  // embedded create flow) — apply the default assignee late unless the user
  // already picked one.
  useEffect(() => {
    if (!isOpportunityModalOpen || selectedOpportunityForInfo || assignedTouchedRef.current) return;
    setAssignedResourceId(myResourceId);
  }, [myResourceId, isOpportunityModalOpen, selectedOpportunityForInfo]);

  const handleOpenOpportunityModal = (currentPipeline?: Pipeline) => {
    // Clear any selected opportunity (prevent edit mode when creating new)
    setSelectedOpportunityForInfo(null);
    setSelectedCustomer(null);
    setOpportunityTitle('');
    setOpportunityValue(0);
    setBusinessName('');
    setOpportunitySource('');
    setLeadInfo('');
    // Default the assignment to whoever is creating the lead.
    assignedTouchedRef.current = false;
    setAssignedResourceId(myResourceId);
    
    setIsOpportunityModalOpen(true);
    
    // Initialize custom fields based on definitions
    initializeCustomFields();
    
    if (currentPipeline) {
      setSelectedPipeline(currentPipeline);
      // Auto-select first stage if available
      if (currentPipeline.stages && currentPipeline.stages.length > 0) {
        setSelectedStage(currentPipeline.stages[0]);
      }
    } else if (pipelines.length > 0) {
      // Default to first pipeline if no specific pipeline context
      const firstPipeline = pipelines[0];
      setSelectedPipeline(firstPipeline);
      // Auto-select first stage if available
      if (firstPipeline.stages && firstPipeline.stages.length > 0) {
        setSelectedStage(firstPipeline.stages[0]);
      }
    }
  };

  // Handle creating a new opportunity (now with database)
  const handleCreateOpportunity = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedCustomer || !selectedPipeline || !selectedStage || !selectedBusiness?.id || !supabase) {
      return;
    }

    // Required custom fields only block creation here in Nexus —
    // webhooks/automations insert leads directly and are never gated.
    if (customFieldsEnabled) {
      const missing = customFieldDefinitions.filter(def => {
        if (!def.required) return false;
        const field = customFields.find(f => f.id === def.id);
        return !field || !String(field.value ?? '').trim();
      });
      if (missing.length > 0) {
        toast.error(`Please fill in: ${missing.map(m => m.name).join(', ')}`);
        return;
      }
    }

    try {
      setIsLoading(true);
      
      const opportunityData = {
        business_id: selectedBusiness.id,
        customer_id: selectedCustomer.id,
        title: opportunityTitle,
        pipeline_id: selectedPipeline.id,
        stage_id: selectedStage.id,
        value: opportunityValue,
        business_name: businessName || null,
        source: opportunitySource || null,
        tags: selectedTags.length > 0 ? JSON.stringify(selectedTags) : null,
        status: opportunityStatus,
        lead_info: leadInfo || null,
        custom_fields: customFields.length > 0 ? JSON.stringify(customFields) : null,
        created_by_user_id: myUserId,
        created_by_resource_id: myResourceId,
        assigned_resource_id: assignedResourceId
      };

      // Create the opportunity in database
      const { data, error } = await supabase
        .from('pipeline_opportunities')
        .insert(opportunityData)
        .select()
        .single();

      if (error) throw error;

      void triggerEnterStageAutomations(selectedBusiness.id, data.id, { previousStageId: null });

      // Enrich the newly created opportunity with customer data before adding to state
      const enrichedOpportunity: Opportunity = {
        ...data,
        customer_name: selectedCustomer 
          ? `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim() || 'Unknown Contact'
          : 'Unknown Contact',
        customers: selectedCustomer ? {
          id: selectedCustomer.id,
          first_name: selectedCustomer.first_name || '',
          last_name: selectedCustomer.last_name || ''
        } : undefined
      };

      // Add the enriched opportunity to the local state
      setOpportunities(prev => {
        const exists = prev.some(opp => opp.id === enrichedOpportunity.id);
        if (exists) return prev;
        return [...prev, enrichedOpportunity];
      });

      if (selectedCustomer) {
        setCustomerMap(prev => new Map(prev).set(selectedCustomer.id, selectedCustomer));
      }

      // Increment unread count since new opportunities default to unread
      incrementCount();
      
      // Close the modal and reset
      handleModalClose();
      
    } catch (err) {
      console.error('Error creating opportunity:', err);
      setError('Failed to create opportunity. Please try again.');
      alert('Error creating opportunity: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle adding a custom field
  const handleAddCustomField = async (name: string, type: string) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      console.log('ÃƒÂ¢Ã…Â¾• Adding custom field to database:', { name, type });
      
      // Insert into database
      const { data, error } = await supabase
        .from('opportunity_custom_fields')
        .insert({
          business_id: selectedBusiness.id,
          field_name: name,
          field_type: type,
          display_order: customFieldDefinitions.length
        })
        .select()
        .single();

      if (error) {
        console.error('ÃƒÂ¢Ã‚ÂÃ…â€™ Error adding custom field:', error);
        alert('Failed to add custom field. Please try again.');
        return;
      }

      console.log('ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ Custom field added to database:', data);

      // Transform database format to component format
      const newFieldDef = {
        id: data.id,
        name: data.field_name,
        type: data.field_type
      };
      
      // Add to local state
      setCustomFieldDefinitions(prev => [...prev, newFieldDef]);
      
      // Add to current fields with empty value
      const newField = {
        id: newFieldDef.id,
        name: newFieldDef.name,
        type: newFieldDef.type,
        value: ''
      };
      setCustomFields(prev => [...prev, newField]);
    } catch (error) {
      console.error('ÃƒÂ¢Ã‚ÂÃ…â€™ Error adding custom field:', error);
      alert('Failed to add custom field. Please try again.');
    }
  };

  // Handle updating custom field value
  const handleUpdateCustomField = (id: string, value: string) => {
    setCustomFields(prev => 
      prev.map(field => field.id === id ? { ...field, value } : field)
    );
  };

  // Handle removing a custom field definition
  const handleRemoveCustomField = async (id: string) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      console.log('ÃƒÂ°Ã…Â¸—Ã¢â‚¬ËœÃƒÂ¯Ã‚Â¸Ã‚Â Removing custom field from database:', id);
      
      // Delete from database
      const { error } = await supabase
        .from('opportunity_custom_fields')
        .delete()
        .eq('id', id)
        .eq('business_id', selectedBusiness.id);

      if (error) {
        console.error('ÃƒÂ¢Ã‚ÂÃ…â€™ Error removing custom field:', error);
        alert('Failed to remove custom field. Please try again.');
        return;
      }

      console.log('ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ Custom field removed from database');

      // Remove from local state
      setCustomFieldDefinitions(prev => prev.filter(def => def.id !== id));
      
      // Remove from current fields
      setCustomFields(prev => prev.filter(field => field.id !== id));
    } catch (error) {
      console.error('ÃƒÂ¢Ã‚ÂÃ…â€™ Error removing custom field:', error);
      alert('Failed to remove custom field. Please try again.');
    }
  };

  const refreshLeadAgentScheduleForModal = useCallback(async () => {
    if (!selectedOpportunityForInfo?.id || !supabase) return;
    const { data, error } = await supabase
      .from('pipeline_opportunities')
      .select('lead_agent_next_follow_up_at, lead_agent_follow_up_reason, lead_agent_last_wake_at')
      .eq('id', selectedOpportunityForInfo.id)
      .single();
    if (error || !data) return;
    setSelectedOpportunityForInfo((prev) => (prev ? { ...prev, ...data } : null));
    setOpportunities((prev) =>
      prev.map((o) => (o.id === selectedOpportunityForInfo.id ? { ...o, ...data } : o))
    );
  }, [selectedOpportunityForInfo?.id, supabase]);

  const saveOpportunityFollowUp = useCallback(
    async (opportunity: Opportunity, followUpAt: string | null) => {
      if (!opportunity.id || !supabase || !selectedBusiness?.id) return;

      const opportunityId = opportunity.id;
      const customerId = opportunity.customer_id;
      const previousFollowUpAt = opportunity.next_follow_up_at ?? null;

      const dbPatch: Record<string, string | null> = {
        next_follow_up_at: followUpAt,
      };
      if (followUpAt) {
        dbPatch.follow_up_due_prompt_dismissed_for_due_at = null;
      }

      const { error } = await supabase
        .from('pipeline_opportunities')
        .update(dbPatch)
        .eq('id', opportunityId);
      if (error) {
        toast.error('Could not save follow-up');
        throw error;
      }

      if (customerId) {
        const formattedFollowUp = followUpAt
          ? new Date(followUpAt).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })
          : null;

        await recordOpportunityActivity({
          businessId: selectedBusiness.id,
          customerId,
          activityType: followUpAt ? 'follow_up_scheduled' : 'follow_up_cleared',
          activityData: followUpAt
            ? { next_follow_up_at: followUpAt, scheduled_for: formattedFollowUp }
            : { previous_follow_up_at: previousFollowUpAt },
          specificOpportunityId: opportunityId,
        });
      }

      const statePatch = { ...dbPatch };
      setSelectedOpportunityForInfo((prev) =>
        prev?.id === opportunityId ? { ...prev, ...statePatch } : prev,
      );
      setOpportunities((prev) =>
        prev.map((o) => (o.id === opportunityId ? { ...o, ...statePatch } : o)),
      );

      await loadLastActivity(opportunityId);
      if (isActivityModalOpen) {
        await loadAllActivities(opportunityId);
      }

      toast.success(followUpAt ? 'Follow-up scheduled' : 'Follow-up cleared');
    },
    [selectedBusiness?.id, supabase, isActivityModalOpen],
  );

  const completeOpportunityFollowUp = useCallback(
    async (opportunity: Opportunity) => {
      if (!opportunity.id || !supabase || !selectedBusiness?.id) return;

      const previousFollowUpAt = opportunity.next_follow_up_at;
      if (!previousFollowUpAt) return;

      const opportunityId = opportunity.id;
      let customerId = opportunity.customer_id;

      if (!customerId) {
        const { data: row, error: fetchError } = await supabase
          .from('pipeline_opportunities')
          .select('customer_id')
          .eq('id', opportunityId)
          .maybeSingle();
        if (fetchError) {
          toast.error('Could not complete follow-up');
          throw fetchError;
        }
        customerId = row?.customer_id ?? null;
      }

      if (!customerId) {
        toast.error('Could not log activity: this opportunity has no customer');
        return;
      }

      const formattedFollowUp = new Date(previousFollowUpAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

      const activityRecorded = await recordOpportunityActivity({
        businessId: selectedBusiness.id,
        customerId,
        activityType: 'follow_up_completed',
        activityData: {
          completed_follow_up_at: previousFollowUpAt,
          scheduled_for: formattedFollowUp,
        },
        specificOpportunityId: opportunityId,
      });

      if (!activityRecorded) {
        toast.error(
          'Could not save to activity log. Apply migration 20260518180000_opportunity_activity_follow_up_completed.sql in Supabase.',
        );
        return;
      }

      const { error } = await supabase
        .from('pipeline_opportunities')
        .update({ next_follow_up_at: null })
        .eq('id', opportunityId);
      if (error) {
        toast.error('Could not complete follow-up');
        throw error;
      }

      const statePatch = { next_follow_up_at: null as string | null };
      setSelectedOpportunityForInfo((prev) =>
        prev?.id === opportunityId ? { ...prev, ...statePatch } : prev,
      );
      setOpportunities((prev) =>
        prev.map((o) => (o.id === opportunityId ? { ...o, ...statePatch } : o)),
      );

      await loadLastActivity(opportunityId);
      if (isActivityModalOpen) {
        await loadAllActivities(opportunityId);
      }

      toast.success('Follow-up marked complete');
    },
    [selectedBusiness?.id, supabase, isActivityModalOpen],
  );

  const handleOpportunityFollowUpSave = useCallback(
    async (followUpAt: string | null) => {
      if (!selectedOpportunityForInfo) return;
      await saveOpportunityFollowUp(selectedOpportunityForInfo, followUpAt);
    },
    [selectedOpportunityForInfo, saveOpportunityFollowUp],
  );

  const handleOpportunityFollowUpComplete = useCallback(async () => {
    if (!selectedOpportunityForInfo) return;
    await completeOpportunityFollowUp(selectedOpportunityForInfo);
  }, [selectedOpportunityForInfo, completeOpportunityFollowUp]);

  const maybeShowFollowUpPromptOnClose = useCallback(
    async (opportunity: Opportunity) => {
      if (!supabase || opportunity.status !== 'Open') return;
      if (hasActiveFutureFollowUp(opportunity.next_follow_up_at)) return;

      let hasOutreachAfterDue = false;
      const dueAt = opportunity.next_follow_up_at;
      if (dueAt && new Date(dueAt).getTime() <= Date.now()) {
        hasOutreachAfterDue = await hasOutreachActivityAfterFollowUpDue(
          supabase,
          opportunity.id,
          dueAt,
        );
      }

      const reason = getFollowUpClosePromptReason(opportunity, hasOutreachAfterDue);
      if (reason) {
        setFollowUpPrompt({ opportunity, reason });
      }
    },
    [supabase],
  );

  const handleFollowUpPromptDismiss = useCallback(async () => {
    if (!followUpPrompt || !supabase) {
      setFollowUpPrompt(null);
      return;
    }

    const { opportunity, reason } = followUpPrompt;
    const dismissedAt = new Date().toISOString();
    const patch =
      reason === 'fresh'
        ? { initial_follow_up_prompt_dismissed_at: dismissedAt }
        : { follow_up_due_prompt_dismissed_for_due_at: opportunity.next_follow_up_at ?? null };

    const { error } = await supabase
      .from('pipeline_opportunities')
      .update(patch)
      .eq('id', opportunity.id);

    if (error) {
      toast.error('Could not save preference');
      return;
    }

    setOpportunities((prev) =>
      prev.map((o) => (o.id === opportunity.id ? { ...o, ...patch } : o)),
    );
    setSelectedOpportunityForInfo((prev) =>
      prev?.id === opportunity.id ? { ...prev, ...patch } : prev,
    );
    setFollowUpPrompt(null);
  }, [followUpPrompt, supabase]);

  const handleFollowUpPromptSave = useCallback(
    async (followUpAt: string) => {
      if (!followUpPrompt) return;
      setFollowUpPromptSaving(true);
      try {
        await saveOpportunityFollowUp(followUpPrompt.opportunity, followUpAt);
        setFollowUpPrompt(null);
      } finally {
        setFollowUpPromptSaving(false);
      }
    },
    [followUpPrompt, saveOpportunityFollowUp],
  );

  const handleLeadAgentAssign = async (agentId: string | null) => {
    if (!selectedOpportunityForInfo || !supabase || !selectedBusiness?.id) return;
    try {
      if (agentId) {
        const { error: clearErr } = await supabase.rpc(
          'pipeline_opportunities_clear_lead_agent_elsewhere',
          {
            p_agent_id: agentId,
            p_business_id: selectedBusiness.id,
            p_keep_opportunity_id: selectedOpportunityForInfo.id,
          }
        );
        if (clearErr) throw clearErr;
      }
      const followUpIso = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      const { error } = await supabase
        .from('pipeline_opportunities')
        .update(
          agentId
            ? {
                lead_agent_id: agentId,
                lead_agent_next_follow_up_at: followUpIso,
                lead_agent_follow_up_reason: 'Initial schedule after assign',
              }
            : {
                lead_agent_id: null,
                lead_agent_next_follow_up_at: null,
                lead_agent_follow_up_reason: null,
              }
        )
        .eq('id', selectedOpportunityForInfo.id)
        .eq('business_id', selectedBusiness.id);
      if (error) throw error;

      let initialRunOk = true
      if (agentId) {
        const initialKickoff = `[SYSTEM] Initial assignment: you are now the lead agent on this opportunity. Perform a full review of all CRM context you were given (including lead_info, activities, linked records, and SMS thread), then act per your system instructions — including sending the first SMS when appropriate.`
        const turn = await invokeLeadAgentTurn({
          opportunityId: selectedOpportunityForInfo.id,
          agentId,
          businessId: selectedBusiness.id,
          message: initialKickoff,
          runMode: 'initial_assign',
        })
        initialRunOk = !!turn.success
        if (!turn.success) {
          toast.error(turn.error || 'First lead-agent run failed. Try sending a message in the chat below.')
        }
      }

      const next = {
        ...selectedOpportunityForInfo,
        lead_agent_id: agentId,
        lead_agent_next_follow_up_at: agentId ? followUpIso : null,
        lead_agent_follow_up_reason: agentId ? 'Initial schedule after assign' : null,
      } as Opportunity;
      setSelectedOpportunityForInfo(next);
      setOpportunities((prev) =>
        prev.map((o) => (o.id === selectedOpportunityForInfo.id ? { ...o, ...next } : o))
      );
      if (agentId) {
        await refreshLeadAgentScheduleForModal();
      }
      if (agentId && initialRunOk) {
        toast.success('Lead agent assigned and started')
      } else if (!agentId) {
        toast.success('Lead agent removed')
      }
    } catch (e) {
      console.error(e);
      toast.error('Could not update lead agent');
    }
  };

  // Reset modal state when closing
  const handleModalClose = (options?: { skipFollowUpPrompt?: boolean }) => {
    const closingOpportunity = selectedOpportunityForInfo;

    // Flush any pending (debounced) auto-save before we tear down modal state,
    // so a quick edit-then-close never loses changes.
    if (pendingOppSaveRef.current) pendingOppSaveRef.current();

    setIsOpportunityModalOpen(false);
    setIsSendIntakeFormModalOpen(false);
    setLiveIntakeFormsEnabled(false);
    setLeadAgentsForPicker([]);
    setSelectedCustomer(null);
    setOpportunityTitle('');
    setSelectedPipeline(null);
    setSelectedStage(null);
    setOpportunityValue(0);
    setBusinessName('');
    setOpportunitySource('');
    setOpportunityStatus('Open');
    setSelectedTags([]);
    // Reset custom field values but keep the definitions
    initializeCustomFields();

    if (closingOpportunity && !options?.skipFollowUpPrompt) {
      void maybeShowFollowUpPromptOnClose(closingOpportunity);
    }

    if (onEmbedClose) {
      onEmbedOpportunityChange?.();
      onEmbedClose();
    }
  };
  handleModalCloseRef.current = handleModalClose;

  React.useEffect(() => {
    const handleCloseAll = () => {
      handleModalCloseRef.current();
      setIsActivityModalOpen(false);
    };
    window.addEventListener('closeAllModals', handleCloseAll);
    return () => window.removeEventListener('closeAllModals', handleCloseAll);
  }, []);

  useEffect(() => {
    const handleOpportunityReadOnActivity = (event: Event) => {
      const { opportunityIds } = (event as CustomEvent<{ opportunityIds: number[] }>).detail || {};
      if (!Array.isArray(opportunityIds) || opportunityIds.length === 0) return;

      const idSet = new Set(opportunityIds);
      setOpportunities((prevOpportunities) =>
        prevOpportunities.map((opp) =>
          idSet.has(opp.id) ? { ...opp, is_unread: false } : opp
        )
      );
      opportunityIds.forEach((id) => markAsRead(id));
    };

    window.addEventListener(CRM_OPPORTUNITY_READ_ON_ACTIVITY_EVENT, handleOpportunityReadOnActivity);
    return () => {
      window.removeEventListener(CRM_OPPORTUNITY_READ_ON_ACTIVITY_EVENT, handleOpportunityReadOnActivity);
    };
  }, [markAsRead]);

  // Quick move opportunity to different stage
  const handleQuickMove = async (opportunityId: number, newStageId: number) => {
    if (!supabase || !selectedBusiness?.id || newStageId === UNASSIGNED_STAGE_ID) return;
    
    // Get opportunity details before moving (for activity tracking and optimistic update)
    const opportunity = opportunities.find(opp => opp.id === opportunityId);
    if (!opportunity) return;

    const oldStageId = opportunity.stage_id;
    const oldStage = opportunitiesViewPipeline?.stages?.find(s => s.id === oldStageId);
    const newStage = opportunitiesViewPipeline?.stages?.find(s => s.id === newStageId);
    
    // Determine exit direction based on stage positions
    if (isMobile && moveAnimationOpportunity && opportunitiesViewPipeline?.stages) {
      const currentStageIndex = opportunitiesViewPipeline.stages.findIndex(
        stage => stage.id === moveAnimationOpportunity.stage_id
      );
      const newStageIndex = opportunitiesViewPipeline.stages.findIndex(
        stage => stage.id === newStageId
      );
      
      if (currentStageIndex !== -1 && newStageIndex !== -1) {
        // Animate left if moving to earlier stage, right if moving to later stage
        const direction = newStageIndex < currentStageIndex ? 'left' : 'right';
        
        // Optimistically remove the opportunity from the current pipeline immediately
        // This prevents it from briefly reappearing during the refresh
        setOpportunities(prev => prev.filter(opp => opp.id !== opportunityId));
        
        // Set exit direction immediately - React will handle the re-render
        setExitDirection(direction);
        
        // Wait for exit animation to complete
        setTimeout(async () => {
          try {
            const { error } = await supabase
              .from('pipeline_opportunities')
              .update({ stage_id: newStageId })
              .eq('id', opportunityId);
            
            if (error) throw error;

            if (oldStageId !== newStageId && selectedBusiness?.id) {
              void triggerEnterStageAutomations(selectedBusiness.id, opportunityId, {
                previousStageId: oldStageId,
                previousStageName: oldStage?.name ?? null,
              });
            }

            // Record activity for stage move (non-blocking)
            if (opportunity?.customer_id && newStage && (oldStage || oldStageId == null)) {
              recordOpportunityActivity({
                businessId: selectedBusiness.id,
                customerId: opportunity.customer_id,
                activityType: 'stage_moved',
                activityData: {
                  old_stage_id: oldStageId,
                  new_stage_id: newStageId,
                  old_stage_name: oldStage?.name || 'Unassigned',
                  new_stage_name: newStage.name
                },
                specificOpportunityId: opportunityId
              }).catch(err => {
                console.error('[Stage Move Activity] Error recording activity:', err);
              });
            }
            
            // Optimistic update: add opportunity back with new stage (no full refetch)
            setOpportunities(prev => [...prev, { ...opportunity, stage_id: newStageId }]);
            setMoveDropdownOpen(null);
            // Close mobile animation
            setMoveAnimationOpportunity(null);
            setMoveAnimationPosition(null);
            setExitDirection(null);
            setButtonsOffset(0);
          } catch (error) {
            console.error('Error moving opportunity:', error);
            // On error, restore by adding back and refetch to stay in sync
            setOpportunities(prev => [...prev, { ...opportunity, stage_id: oldStageId }]);
            fetchOpportunities();
            setExitDirection(null);
          }
        }, 400); // Match animation duration
        return;
      }
    }
    
    // Desktop: optimistic update first (instant UI), then persist
    setOpportunities(prev =>
      prev.map(opp => (opp.id === opportunityId ? { ...opp, stage_id: newStageId } : opp))
    );
    setMoveDropdownOpen(null);

    try {
      const { error } = await supabase
        .from('pipeline_opportunities')
        .update({ stage_id: newStageId })
        .eq('id', opportunityId);
      
      if (error) throw error;

      if (oldStageId !== newStageId && selectedBusiness?.id) {
        void triggerEnterStageAutomations(selectedBusiness.id, opportunityId, {
          previousStageId: oldStageId,
          previousStageName: oldStage?.name ?? null,
        });
      }

      // Record activity for stage move (non-blocking)
      if (opportunity?.customer_id && newStage && (oldStage || oldStageId == null)) {
        recordOpportunityActivity({
          businessId: selectedBusiness.id,
          customerId: opportunity.customer_id,
          activityType: 'stage_moved',
          activityData: {
            old_stage_id: oldStageId,
            new_stage_id: newStageId,
            old_stage_name: oldStage?.name || 'Unassigned',
            new_stage_name: newStage.name
          },
          specificOpportunityId: opportunityId
        }).catch(err => {
          console.error('[Stage Move Activity] Error recording activity:', err);
        });
      }
    } catch (error) {
      console.error('Error moving opportunity:', error);
      // Revert optimistic update and refetch to restore correct state
      setOpportunities(prev =>
        prev.map(opp => (opp.id === opportunityId ? { ...opp, stage_id: oldStageId } : opp))
      );
      fetchOpportunities();
    }
  };
  
  // Handle opening move animation on mobile
  const handleOpenMoveAnimation = (opportunity: Opportunity, e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    
    // Prevent double-firing in PWA - if touch was handled recently, ignore the subsequent click
    if (e.type === 'click' && moveButtonTouchHandledRef.current) {
      const now = Date.now();
      if ((now - moveButtonTouchHandledRef.current) < 300) {
        e.preventDefault();
        return;
      }
    }
    
    if (!isMobile) {
      // Desktop: use dropdown
      setMoveDropdownOpen(moveDropdownOpen === opportunity.id ? null : opportunity.id);
      return;
    }
    
    // Mobile: use iOS-style animation
    // Prevent default only on touch events to avoid navigation issues
    if (e.type === 'touchend') {
      e.preventDefault();
    }
    
    // Get the card element position
    const cardElement = opportunityCardRefs.current[opportunity.id];
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();
      setMoveAnimationPosition({
        x: rect.left,
        y: rect.top,
        width: rect.width,
        height: rect.height,
      });
    }
    
    setMoveAnimationOpportunity(opportunity);
    setButtonsOffset(0); // Reset offset - will be calculated after render
    setExitDirection(null); // Reset exit direction
  };
  

  // Drag and drop handlers for opportunity cards
  const handleOpportunityDragStart = (e: React.DragEvent, opportunity: Opportunity) => {
    setDraggedOpportunity(opportunity);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleStageDragOver = (e: React.DragEvent, stageId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStageId(stageId);
  };

  const handleStageDragLeave = () => {
    setDragOverStageId(null);
  };

  const handleStageDrop = async (e: React.DragEvent, newStageId: number) => {
    e.preventDefault();
    setDragOverStageId(null);
    
    if (!draggedOpportunity || draggedOpportunity.stage_id === newStageId || newStageId === UNASSIGNED_STAGE_ID) {
      setDraggedOpportunity(null);
      return;
    }

    await handleQuickMove(draggedOpportunity.id, newStageId);
    setDraggedOpportunity(null);
  };

  const handleOpportunityDragEnd = () => {
    setDraggedOpportunity(null);
    setDragOverStageId(null);
  };

  // Handle opening message modal for opportunity
  const handleOpenMessageModal = async (opportunity: Opportunity) => {
    try {
      // Fetch customer data for this opportunity
      if (!supabase) return;
      const { data: customerData } = await supabase
        .from('customers')
        .select('*')
        .eq('id', opportunity.customer_id)
        .single();
      
      if (customerData) {
        // Convert to full Customer object
        const customer: Customer = {
          ...customerData,
          vehicles: customerData.vehicles || []
        };
        setMessageModalCustomer(customer);
        setMessageModalOpen(true);
      } else {
        console.error('Customer not found for opportunity');
      }
    } catch (error) {
      console.error('Error opening message modal:', error);
    }
  };

  // Load linked proposal for opportunity
  const loadLinkedProposal = async (opportunityId: number) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    setIsLoadingLinkedProposal(true);
    try {
      const { data, error } = await supabase
        .from('proposals')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .eq('pipeline_opportunities_id', opportunityId)
        .neq('status', 'held')
        .single();

      if (error) {
        // No proposal linked is not an error
        if (error.code !== 'PGRST116') {
          console.error('Error loading linked proposal:', error);
        }
        setLinkedProposal(null);
      } else if (data) {
        // Fetch full proposal data
        try {
          const fullProposal = await getProposal(data.id);
          setLinkedProposal(fullProposal);
        } catch (proposalError) {
          // If proposal doesn't exist (was deleted), clear the link
          console.error('Error loading proposal (may have been deleted):', proposalError);
          setLinkedProposal(null);
        }
      } else {
        setLinkedProposal(null);
      }
    } catch (error) {
      console.error('Error loading linked proposal:', error);
      setLinkedProposal(null);
    } finally {
      setIsLoadingLinkedProposal(false);
    }
  };

  // Load linked booking for opportunity
  const loadLinkedBooking = async (opportunityId: number) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    setIsLoadingLinkedBooking(true);
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .eq('pipeline_opportunities_id', opportunityId)
        .single();

      if (error) {
        // No booking linked is not an error
        if (error.code !== 'PGRST116') {
          console.error('Error loading linked booking:', error);
        }
        setLinkedBooking(null);
      } else if (data) {
        // Fetch full booking data
        try {
          const fullBooking = await getBooking(data.id);
          setLinkedBooking(fullBooking);
        } catch (bookingError) {
          // If booking doesn't exist (was deleted), clear the link
          console.error('Error loading booking (may have been deleted):', bookingError);
          setLinkedBooking(null);
        }
      } else {
        setLinkedBooking(null);
      }
    } catch (error) {
      console.error('Error loading linked booking:', error);
      setLinkedBooking(null);
    } finally {
      setIsLoadingLinkedBooking(false);
    }
  };

  const loadLinkedFieldQuote = async (opportunityId: number) => {
    if (!selectedBusiness?.id || !supabase) return;

    setIsLoadingLinkedFieldQuote(true);
    try {
      const { data, error } = await supabase
        .from('project_field_quotes')
        .select('id, status, is_measurement')
        .eq('business_id', selectedBusiness.id)
        .eq('pipeline_opportunities_id', opportunityId)
        .maybeSingle();

      if (error) {
        console.error('Error loading linked field quote:', error);
        setLinkedFieldQuote(null);
      } else if (data) {
        setLinkedFieldQuote({
          id: data.id,
          status: data.status || 'scheduled',
          is_measurement: !!data.is_measurement
        });
      } else {
        setLinkedFieldQuote(null);
      }
    } catch (e) {
      console.error('Error loading linked field quote:', e);
      setLinkedFieldQuote(null);
    } finally {
      setIsLoadingLinkedFieldQuote(false);
    }
  };

  // Load linked project for opportunity
  const loadLinkedProject = async (opportunityId: number) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    setIsLoadingLinkedProject(true);
    try {
      console.log('Loading linked project for opportunity:', opportunityId, 'business:', selectedBusiness.id);
      const { data, error } = await supabase
        .from('projects')
        .select('id, title, status, pipeline_opportunities_id, is_deleted')
        .eq('business_id', selectedBusiness.id)
        .eq('pipeline_opportunities_id', opportunityId)
        .eq('is_deleted', false)
        .maybeSingle(); // Use maybeSingle() instead of single() to avoid errors when no row found

      if (error) {
        console.error('Error loading linked project:', error);
        setLinkedProject(null);
      } else if (data) {
        console.log('Found linked project:', data);
        setLinkedProject({
          id: data.id,
          title: data.title || 'Untitled Project',
          status: data.status || 'draft'
        });
      } else {
        console.log('No linked project found for opportunity:', opportunityId);
        setLinkedProject(null);
      }
    } catch (error) {
      console.error('Error loading linked project:', error);
      setLinkedProject(null);
    } finally {
      setIsLoadingLinkedProject(false);
    }
  };

  // Load last activity for opportunity
  const loadLastActivity = async (opportunityId: number) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    setIsLoadingLastActivity(true);
    try {
      // The lead's timeline: posted activities plus every text and email with
      // the customer while the lead is open, inbound and outbound.
      const { data, error } = await supabase.rpc('crm_lead_timeline', {
        p_business_id: selectedBusiness.id,
        p_opportunity_id: opportunityId,
        p_limit: 1,
      });

      if (error) {
        console.error('Error loading last activity:', error);
        setLastActivity(null);
      } else if (Array.isArray(data) && data[0]) {
        setLastActivity(data[0]);
      } else {
        setLastActivity(null);
      }
    } catch (error) {
      console.error('Error loading last activity:', error);
      setLastActivity(null);
    } finally {
      setIsLoadingLastActivity(false);
    }
  };

  // Load first activity for opportunity and calculate time to first response
  const loadFirstActivity = async (opportunityId: number, opportunityCreatedAt: string) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    setIsLoadingFirstActivity(true);
    try {
      const { data, error } = await supabase
        .from('opportunity_activities')
        .select('activity_type, activity_data, created_at')
        .eq('pipeline_opportunities_id', opportunityId)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        // No activity is not an error
        if (error.code !== 'PGRST116') {
          console.error('Error loading first activity:', error);
        }
        setFirstActivity(null);
        setTimeToFirstResponse(null);
      } else if (data) {
        setFirstActivity(data);
        // Calculate time to first response
        const createdDate = new Date(opportunityCreatedAt);
        const firstActivityDate = new Date(data.created_at);
        const diffMs = firstActivityDate.getTime() - createdDate.getTime();
        setTimeToFirstResponse(formatTimeDifference(diffMs));
      } else {
        setFirstActivity(null);
        setTimeToFirstResponse(null);
      }
    } catch (error) {
      console.error('Error loading first activity:', error);
      setFirstActivity(null);
      setTimeToFirstResponse(null);
    } finally {
      setIsLoadingFirstActivity(false);
    }
  };

  // Format time difference in human-readable format
  const formatTimeDifference = (milliseconds: number): string => {
    if (milliseconds < 0) return '0m';
    
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (years > 0) return `${years}y`;
    if (months > 0) return `${months}mo`;
    if (weeks > 0) return `${weeks}w`;
    if (days > 0) return `${days}d`;
    if (hours > 0) {
      const remainMins = minutes % 60;
      return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
    }
    if (minutes > 0) return `${minutes}m`;
    return `${seconds}s`;
  };

  // Load all activities for opportunity
  const loadAllActivities = async (opportunityId: number) => {
    if (!selectedBusiness?.id || !supabase) return;
    
    setIsLoadingAllActivities(true);
    try {
      // Same timeline the AI bots read: activities merged with the whole
      // conversation (texts and emails both ways) while the lead is open.
      const { data, error } = await supabase.rpc('crm_lead_timeline', {
        p_business_id: selectedBusiness.id,
        p_opportunity_id: opportunityId,
        p_limit: 150,
      });

      if (error) {
        console.error('Error loading all activities:', error);
        setAllActivities([]);
      } else {
        setAllActivities((data || []) as typeof allActivities);
      }
    } catch (error) {
      console.error('Error loading all activities:', error);
      setAllActivities([]);
    } finally {
      setIsLoadingAllActivities(false);
    }
  };

  // Handle opening activity modal
  const handleOpenActivityModal = async () => {
    if (selectedOpportunityForInfo?.id) {
      await loadAllActivities(selectedOpportunityForInfo.id);
      setIsActivityModalOpen(true);
    }
  };

  // Fetch proposals for link modal
  const fetchProposals = async () => {
    if (!selectedBusiness?.id || !selectedOpportunityForInfo || !supabase) return;

    setIsLoadingProposals(true);
    try {
      const opportunityCustomerId = selectedOpportunityForInfo.customer_id;
      
      let query = supabase
        .from('proposals')
        .select(`
          id,
          title,
          pipeline_opportunities_id,
          status,
          total_amount,
          valid_until,
          created_at,
          updated_at,
          customer:customers!proposals_customer_id_fkey(
            id,
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .neq('status', 'held');
      
      // Only show proposals with no customer OR matching customer
      if (opportunityCustomerId) {
        query = query.or(`customer_id.is.null,customer_id.eq.${opportunityCustomerId}`);
      } else {
        // If opportunity has no customer, only show proposals with no customer
        query = query.is('customer_id', null);
      }
      
      const { data, error } = await query
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching proposals:', error);
        return;
      }

      setProposals((data || []) as Proposal[]);
    } catch (error) {
      console.error('Error fetching proposals:', error);
    } finally {
      setIsLoadingProposals(false);
    }
  };

  // Fetch bookings for link modal
  const fetchBookings = async () => {
    if (!selectedBusiness?.id || !selectedOpportunityForInfo || !supabase) return;

    setIsLoadingBookings(true);
    try {
      const opportunityCustomerId = selectedOpportunityForInfo.customer_id;
      
      let query = supabase
        .from('bookings')
        .select(`
          id,
          title,
          booking_number,
          pipeline_opportunities_id,
          status,
          start_date,
          start_time,
          total_amount,
          customer:customers!bookings_customer_id_fkey(
            id,
            first_name,
            last_name
          ),
          services:booking_services(
            id,
            name,
            vehicle:customer_vehicles(
              make,
              model
            )
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('is_deleted', false);
      
      // Only show bookings with no customer OR matching customer
      if (opportunityCustomerId) {
        query = query.or(`customer_id.is.null,customer_id.eq.${opportunityCustomerId}`);
      } else {
        // If opportunity has no customer, only show bookings with no customer
        query = query.is('customer_id', null);
      }
      
      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('Error fetching bookings:', error);
        return;
      }

      // Transform the data to match the expected format (flatten vehicle data)
      const transformedData = (data || []).map(booking => ({
        ...booking,
        services: booking.services?.map((service: any) => ({
          ...service,
          vehicle_make: service.vehicle?.make || null,
          vehicle_model: service.vehicle?.model || null
        })) || []
      }));

      setBookings(transformedData as Booking[]);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setIsLoadingBookings(false);
    }
  };

  const fetchFieldQuotes = async () => {
    if (!selectedBusiness?.id || !selectedOpportunityForInfo || !supabase) return;

    setIsLoadingFieldQuotes(true);
    try {
      const opportunityCustomerId = selectedOpportunityForInfo.customer_id;

      let query = supabase
        .from('project_field_quotes')
        .select('id, status, is_measurement, pipeline_opportunities_id, customer_id, start_date, end_date')
        .eq('business_id', selectedBusiness.id);

      if (opportunityCustomerId) {
        query = query.or(`customer_id.is.null,customer_id.eq.${opportunityCustomerId}`);
      } else {
        query = query.is('customer_id', null);
      }

      const { data, error } = await query.order('created_at', { ascending: false }).limit(100);

      if (error) {
        console.error('Error fetching field quotes:', error);
        return;
      }

      const oppId = selectedOpportunityForInfo.id;
      const rows = (data || []) as typeof fieldQuotes;
      const linkable = rows.filter(
        fq => !fq.pipeline_opportunities_id || fq.pipeline_opportunities_id === oppId
      );
      setFieldQuotes(linkable);
    } catch (error) {
      console.error('Error fetching field quotes:', error);
    } finally {
      setIsLoadingFieldQuotes(false);
    }
  };

  // Fetch projects for link modal
  const fetchProjects = async () => {
    if (!selectedBusiness?.id || !selectedOpportunityForInfo || !supabase) return;

    setIsLoadingProjects(true);
    try {
      const opportunityCustomerId = selectedOpportunityForInfo.customer_id;
      
      let query = supabase
        .from('projects')
        .select(`
          id,
          title,
          status,
          customer_id,
          pipeline_opportunities_id,
          customer:customers!projects_customer_id_fkey(
            id,
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('is_deleted', false)
        .eq('project_status', 'open'); // Only show open projects, not closed (Completed/Canceled)
      
      // Only show projects with no customer OR matching customer
      if (opportunityCustomerId) {
        query = query.or(`customer_id.is.null,customer_id.eq.${opportunityCustomerId}`);
      } else {
        // If opportunity has no customer, only show projects with no customer
        query = query.is('customer_id', null);
      }
      
      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('Error fetching projects:', error);
        return;
      }

      setProjects((data || []) as Array<{ id: string; title: string; status: string; customer_id: string | null; customer?: { id: string; first_name: string; last_name: string } | null }>);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // Helper function to check if proposal is expired
  const isProposalExpired = (proposal: Proposal) => {
    if (!proposal.valid_until) return false;
    return new Date(proposal.valid_until) < new Date();
  };

  // Helper function to get display status
  const getProposalDisplayStatus = (proposal: Proposal) => {
    if (proposal.status === 'approved') return proposal.status;
    if (isProposalExpired(proposal)) return 'expired';
    return proposal.status;
  };

  // Helper function to get status timestamp
  const getProposalStatusTimestamp = (proposal: Proposal, displayStatus: string) => {
    if (displayStatus === 'expired' && proposal.valid_until) {
      return new Date(proposal.valid_until).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    // For other statuses, use updated_at as a fallback, or created_at for draft
    if (displayStatus === 'draft') {
      return new Date(proposal.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    // Use updated_at for other statuses (approved, sent, viewed, declined)
    if (proposal.updated_at) {
      return new Date(proposal.updated_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    return null;
  };

  // Filter proposals based on search query
  const filteredProposals = proposals.filter(proposal => {
    const query = proposalSearchQuery.toLowerCase();
    return (
      proposal.title?.toLowerCase().includes(query) ||
      proposal.customer?.first_name?.toLowerCase().includes(query) ||
      proposal.customer?.last_name?.toLowerCase().includes(query) ||
      `${proposal.customer?.first_name} ${proposal.customer?.last_name}`.toLowerCase().includes(query)
    );
  });

  // Filter bookings based on search query
  const filteredBookings = bookings.filter(booking => {
    const query = bookingSearchQuery.toLowerCase();
    return (
      booking.title?.toLowerCase().includes(query) ||
      booking.booking_number?.toString().includes(query) ||
      booking.customer?.first_name?.toLowerCase().includes(query) ||
      booking.customer?.last_name?.toLowerCase().includes(query) ||
      `${booking.customer?.first_name} ${booking.customer?.last_name}`.toLowerCase().includes(query)
    );
  });

  // Filter projects based on search query
  const filteredProjects = projects.filter(project => {
    const query = projectSearchQuery.toLowerCase();
    return (
      project.title?.toLowerCase().includes(query) ||
      project.customer?.first_name?.toLowerCase().includes(query) ||
      project.customer?.last_name?.toLowerCase().includes(query) ||
      `${project.customer?.first_name} ${project.customer?.last_name}`.toLowerCase().includes(query)
    );
  });

  const filteredFieldQuotes = fieldQuotes.filter(fq => {
    const query = fieldQuoteSearchQuery.toLowerCase();
    const idMatch = fq.id.toLowerCase().includes(query);
    const statusMatch = (fq.status || '').toLowerCase().includes(query);
    return idMatch || statusMatch;
  });

  // Handle proposal link change
  const handleProposalLinkChange = async (proposalId: string | null) => {
    if (!selectedOpportunityForInfo || !supabase) return;

    try {
      const { error } = await supabase
        .from('proposals')
        .update({ pipeline_opportunities_id: proposalId ? selectedOpportunityForInfo.id : null })
        .eq('id', proposalId || '');

      if (error) {
        console.error('Error updating proposal link:', error);
        return;
      }

      // Reload linked proposal
      if (proposalId) {
        await loadLinkedProposal(selectedOpportunityForInfo.id);
        if (selectedBusiness?.id && selectedOpportunityForInfo.customer_id) {
          recordOpportunityActivity({
            businessId: selectedBusiness.id,
            customerId: selectedOpportunityForInfo.customer_id,
            activityType: 'proposal_linked',
            activityData: { proposal_id: proposalId },
            specificOpportunityId: selectedOpportunityForInfo.id,
          });
        }
      } else {
        setLinkedProposal(null);
      }

      setIsLinkModalOpen(false);
      setLinkModalType(null);
      setProposalSearchQuery('');
      setFieldQuoteSearchQuery('');
    } catch (error) {
      console.error('Error updating proposal link:', error);
    }
  };

  // Handle booking link change
  const handleBookingLinkChange = async (bookingId: string | null) => {
    if (!selectedOpportunityForInfo || !supabase) return;

    try {
      const { error } = await supabase
        .from('bookings')
        .update({ pipeline_opportunities_id: bookingId ? selectedOpportunityForInfo.id : null })
        .eq('id', bookingId || '');

      if (error) {
        console.error('Error updating booking link:', error);
        return;
      }

      // Reload linked booking
      if (bookingId) {
        await loadLinkedBooking(selectedOpportunityForInfo.id);
        if (selectedBusiness?.id && selectedOpportunityForInfo.customer_id) {
          recordOpportunityActivity({
            businessId: selectedBusiness.id,
            customerId: selectedOpportunityForInfo.customer_id,
            activityType: 'booking_linked',
            activityData: { booking_id: bookingId },
            specificOpportunityId: selectedOpportunityForInfo.id,
          });
        }
      } else {
        setLinkedBooking(null);
      }

      setIsLinkModalOpen(false);
      setLinkModalType(null);
      setBookingSearchQuery('');
      setFieldQuoteSearchQuery('');
    } catch (error) {
      console.error('Error updating booking link:', error);
    }
  };

  // Handle project link change
  const handleProjectLinkChange = async (projectId: string | null) => {
    if (!selectedOpportunityForInfo || !supabase) return;

    try {
      const { error } = await supabase
        .from('projects')
        .update({ pipeline_opportunities_id: projectId ? selectedOpportunityForInfo.id : null })
        .eq('id', projectId || '');

      if (error) {
        console.error('Error updating project link:', error);
        return;
      }

      // Reload linked project
      if (projectId) {
        await loadLinkedProject(selectedOpportunityForInfo.id);
        if (selectedBusiness?.id && selectedOpportunityForInfo.customer_id) {
          recordOpportunityActivity({
            businessId: selectedBusiness.id,
            customerId: selectedOpportunityForInfo.customer_id,
            activityType: 'project_linked',
            activityData: { project_id: projectId },
            specificOpportunityId: selectedOpportunityForInfo.id,
          });
        }
      } else {
        setLinkedProject(null);
      }

      setIsLinkModalOpen(false);
      setLinkModalType(null);
      setProjectSearchQuery('');
      setFieldQuoteSearchQuery('');
    } catch (error) {
      console.error('Error updating project link:', error);
    }
  };

  const handleFieldQuoteLinkChange = async (fieldQuoteId: string | null) => {
    if (!selectedOpportunityForInfo || !supabase) return;

    try {
      if (fieldQuoteId) {
        if (linkedFieldQuote?.id && linkedFieldQuote.id !== fieldQuoteId) {
          const { error: clearOldErr } = await supabase
            .from('project_field_quotes')
            .update({ pipeline_opportunities_id: null })
            .eq('id', linkedFieldQuote.id);
          if (clearOldErr) {
            console.error('Error clearing previous field quote link:', clearOldErr);
            return;
          }
        }
        const { error } = await supabase
          .from('project_field_quotes')
          .update({ pipeline_opportunities_id: selectedOpportunityForInfo.id })
          .eq('id', fieldQuoteId);

        if (error) {
          console.error('Error linking field quote:', error);
          return;
        }
        if (selectedBusiness?.id && selectedOpportunityForInfo.customer_id) {
          recordOpportunityActivity({
            businessId: selectedBusiness.id,
            customerId: selectedOpportunityForInfo.customer_id,
            activityType: 'field_quote_linked',
            activityData: { field_quote_id: fieldQuoteId },
            specificOpportunityId: selectedOpportunityForInfo.id,
          });
        }
      } else if (linkedFieldQuote?.id) {
        const { error } = await supabase
          .from('project_field_quotes')
          .update({ pipeline_opportunities_id: null })
          .eq('id', linkedFieldQuote.id);

        if (error) {
          console.error('Error unlinking field quote:', error);
          return;
        }
      }

      if (fieldQuoteId) {
        await loadLinkedFieldQuote(selectedOpportunityForInfo.id);
      } else {
        setLinkedFieldQuote(null);
      }

      setIsLinkModalOpen(false);
      setLinkModalType(null);
      setFieldQuoteSearchQuery('');
    } catch (error) {
      console.error('Error updating field quote link:', error);
    }
  };

  // Handle opening opportunity info modal
  // Per-business CRM settings/app flags are business-level (not per-opportunity),
  // so we load them once per business and reuse — instead of ~5 round trips on
  // every modal open.
  const crmSettingsCacheRef = useRef<Record<string, {
    hasProposalsApp: boolean; hasProjectsApp: boolean;
    showLocationBooking: boolean; showMobileBooking: boolean;
    showFieldQuotes: boolean; liveIntakeForms: boolean;
    showSalesPin: boolean;
  }>>({});

  const applyCrmSettings = (s: {
    hasProposalsApp: boolean; hasProjectsApp: boolean;
    showLocationBooking: boolean; showMobileBooking: boolean;
    showFieldQuotes: boolean; liveIntakeForms: boolean;
    showSalesPin: boolean;
  }) => {
    setHasProposalsApp(s.hasProposalsApp);
    setHasProjectsApp(s.hasProjectsApp);
    setShowLocationBooking(s.showLocationBooking);
    setShowMobileBooking(s.showMobileBooking);
    setShowFieldQuotesInCrm(s.showFieldQuotes);
    setLiveIntakeFormsEnabled(s.liveIntakeForms);
    setShowSalesPinCreate(s.showSalesPin);
  };

  const ensureCrmSettings = async () => {
    const bizId = selectedBusiness?.id;
    if (!bizId || !supabase) return;
    const cached = crmSettingsCacheRef.current[bizId];
    if (cached) { applyCrmSettings(cached); return; }

    const [intakeRes, appsRes] = await Promise.all([
      supabase.rpc('get_app_setting', { p_business_id: bizId, p_app_slug: 'crm', p_setting_key: 'live_intake_forms', p_default_value: false }),
      supabase.from('business_apps_v2').select('app_id').eq('business_id', bizId).eq('status', 'active')
        .in('app_id', ['proposals-addon-app', 'proposals-core-app', 'projects-addon-app', 'projects-core-app', 'calendar-core-app', 'calendar-addon-app', 'mobile-calendar-core-app', 'mobile-calendar-addon-app', 'marketing-addon-app', 'marketing-core-app']),
    ]);

    const appIds = (appsRes.data || []).map((a: any) => a.app_id);
    const hasProposalsApp = appIds.includes('proposals-addon-app') || appIds.includes('proposals-core-app');
    const hasProjectsApp = appIds.includes('projects-addon-app') || appIds.includes('projects-core-app');
    const hasCalendar = appIds.includes('calendar-core-app') || appIds.includes('calendar-addon-app');
    const hasMobileCalendar = appIds.includes('mobile-calendar-core-app') || appIds.includes('mobile-calendar-addon-app');
    const hasMarketingApp = appIds.includes('marketing-addon-app') || appIds.includes('marketing-core-app');

    let showLocationBooking = false;
    let showMobileBooking = false;
    let showFieldQuotes = false;
    let showSalesPin = false;
    const settingCalls: Promise<any>[] = [];
    if (hasCalendar) {
      settingCalls.push(supabase.rpc('get_app_setting', { p_business_id: bizId, p_app_slug: 'calendar', p_setting_key: 'show_location_booking_in_fab', p_default_value: true })
        .then(({ data }) => { showLocationBooking = data ?? true; }));
    }
    if (hasCalendar || hasMobileCalendar) {
      settingCalls.push(supabase.rpc('get_app_setting', { p_business_id: bizId, p_app_slug: 'calendar', p_setting_key: 'show_mobile_booking_in_fab', p_default_value: true })
        .then(({ data }) => { showMobileBooking = data ?? true; }));
      settingCalls.push(supabase.rpc('get_app_setting', { p_business_id: bizId, p_app_slug: 'calendar', p_setting_key: 'show_field_quotes_in_fab', p_default_value: false })
        .then(({ data }) => { showFieldQuotes = Boolean(data); }));
    }
    if (hasMarketingApp) {
      // Same marketing setting that gates the FAB "Sales Pin" action.
      settingCalls.push(supabase.rpc('get_app_setting', { p_business_id: bizId, p_app_slug: 'marketing', p_setting_key: 'create_sales_pin_from_fab', p_default_value: false })
        .then(({ data }) => { showSalesPin = Boolean(data); }));
    }
    await Promise.all(settingCalls);

    const settings = {
      hasProposalsApp, hasProjectsApp,
      showLocationBooking, showMobileBooking, showFieldQuotes,
      liveIntakeForms: Boolean(intakeRes.data),
      showSalesPin,
    };
    crmSettingsCacheRef.current[bizId] = settings;
    applyCrmSettings(settings);
  };

  // Keep the Sales Pin create option in sync when the marketing setting is
  // toggled in App Settings (same event the FAB listens to).
  useEffect(() => {
    const handleMarketingSettingChange = (event: CustomEvent) => {
      if (event.detail?.key === 'create_sales_pin_from_fab') {
        const value = !!event.detail.value;
        setShowSalesPinCreate(value);
        Object.values(crmSettingsCacheRef.current).forEach((s) => { s.showSalesPin = value; });
      }
    };
    window.addEventListener('marketingSettingChanged', handleMarketingSettingChange as EventListener);
    return () => window.removeEventListener('marketingSettingChanged', handleMarketingSettingChange as EventListener);
  }, []);

  // One-round-trip load of linked items + activities via RPC. Falls back to the
  // individual loaders if the RPC isn't deployed yet. Full proposal/booking are
  // fetched only when linked (needed for the row's computed total).
  const loadOpportunityDetailFast = async (opportunity: Opportunity) => {
    if (!selectedBusiness?.id || !supabase) return;
    setIsLoadingLinkedProposal(true);
    setIsLoadingLinkedBooking(true);
    setIsLoadingLinkedFieldQuote(true);
    setIsLoadingLinkedProject(true);
    setIsLoadingLastActivity(true);
    setIsLoadingFirstActivity(true);
    try {
      const detail = await getOpportunityDetail(selectedBusiness.id, opportunity.id);
      if (!detail) throw new Error('no detail');

      setLinkedFieldQuote(detail.field_quote_id ? {
        id: detail.field_quote_id,
        status: detail.field_quote_status || 'scheduled',
        is_measurement: !!detail.field_quote_is_measurement,
      } : null);
      setIsLoadingLinkedFieldQuote(false);

      setLinkedProject(detail.project_id ? {
        id: detail.project_id,
        title: detail.project_title || 'Untitled Project',
        status: detail.project_status || 'draft',
      } : null);
      setIsLoadingLinkedProject(false);

      setLastActivity(detail.last_activity || null);
      setIsLoadingLastActivity(false);

      if (detail.first_activity && opportunity.created_at) {
        setFirstActivity(detail.first_activity);
        const diffMs = new Date(detail.first_activity.created_at).getTime() - new Date(opportunity.created_at).getTime();
        setTimeToFirstResponse(formatTimeDifference(diffMs));
      } else {
        setFirstActivity(null);
        setTimeToFirstResponse(null);
      }
      setIsLoadingFirstActivity(false);

      // Full proposal/booking records (for the row total) — only when linked.
      const [fullProposal, fullBooking] = await Promise.all([
        detail.proposal_id ? getProposal(detail.proposal_id).catch(() => null) : Promise.resolve(null),
        detail.booking_id ? getBooking(detail.booking_id).catch(() => null) : Promise.resolve(null),
      ]);
      setLinkedProposal(fullProposal);
      setIsLoadingLinkedProposal(false);
      setLinkedBooking(fullBooking);
      setIsLoadingLinkedBooking(false);
    } catch (e) {
      console.warn('[crm] fast opportunity detail failed, using individual loaders:', e);
      await Promise.all([
        loadLinkedProposal(opportunity.id),
        loadLinkedBooking(opportunity.id),
        loadLinkedFieldQuote(opportunity.id),
        loadLinkedProject(opportunity.id),
        loadLastActivity(opportunity.id),
        opportunity.created_at ? loadFirstActivity(opportunity.id, opportunity.created_at) : Promise.resolve(),
      ]);
    }
  };

  const handleOpenOpportunityInfo = (opportunity: Opportunity) => {
    // Set opportunity and pre-fill form with data we already have IMMEDIATELY
    setSelectedOpportunityForInfo(opportunity);

    const { pipeline, stage } = resolveOpportunityPipelineStage(pipelines, opportunity);

    setOpportunityTitle(opportunity.title);
    setOpportunityValue(opportunity.value);
    setBusinessName(opportunity.business_name || '');
    setOpportunitySource(opportunity.source || '');
    setOpportunityStatus(opportunity.status || 'Open');
    setLeadInfo((opportunity as any).lead_info || '');
    setAssignedResourceId(opportunity.assigned_resource_id ?? null);
    assignedTouchedRef.current = false;
    setSelectedPipeline(pipeline || null);
    setSelectedStage(stage || null);

    // Parse tags synchronously
    try {
      const parsedTags = opportunity.tags ? JSON.parse(opportunity.tags) : [];
      setSelectedTags(Array.isArray(parsedTags) ? parsedTags : []);
    } catch { setSelectedTags([]); }

    // Parse custom fields synchronously
    let mergedCustomFields = [...customFieldDefinitions.map(def => ({
      id: def.id, name: def.name, type: def.type, value: ''
    }))];
    if (opportunity.custom_fields) {
      try {
        const savedFields = JSON.parse(opportunity.custom_fields);
        mergedCustomFields = mergedCustomFields.map(field => {
          const savedField = savedFields.find((saved: any) => saved.id === field.id || saved.name === field.name);
          return savedField ? { ...field, value: savedField.value } : field;
        });
      } catch { /* ignore */ }
    }
    setCustomFields(mergedCustomFields);
    setCustomModeFieldIds(new Set());

    // Use cached customer from the customerMap if available
    const cachedCustomer = customerMap.get(opportunity.customer_id);
    if (cachedCustomer) {
      setSelectedCustomer(cachedCustomer as any);
    }

    // OPEN MODAL INSTANTLY - don't wait for any DB calls
    setIsOpportunityModalOpen(true);

    // Fire all async work in parallel in the background
    if (supabase) {
      if (!opportunity.details_first_opened_at) {
        const openedAt = new Date().toISOString();
        void markOpportunityDetailsFirstOpened(supabase, opportunity.id);
        setOpportunities((prev) =>
          prev.map((o) =>
            o.id === opportunity.id ? { ...o, details_first_opened_at: openedAt } : o,
          ),
        );
        setSelectedOpportunityForInfo((prev) =>
          prev?.id === opportunity.id ? { ...prev, details_first_opened_at: openedAt } : prev,
        );
      }

      Promise.all([
        // Load customer (full record) if not cached
        !cachedCustomer
          ? supabase.from('customers').select('*').eq('id', opportunity.customer_id).single().then(({ data }) => {
              if (data) setSelectedCustomer(data);
            })
          : Promise.resolve(),
        // Per-business app flags + booking/intake settings (loaded once, cached).
        ensureCrmSettings(),
        // Linked items + activities in ONE round trip (RPC), with fallback.
        loadOpportunityDetailFast(opportunity),
      ]).catch(err => console.error('Error loading opportunity details:', err));
    }
  };

  // AI assistant open_lead: event when CRM is already mounted, plus sessionStorage
  // handoff when open_lead navigated here first and the event fired before mount.
  useEffect(() => {
    const openFromAssistant = async (rawId: string | number) => {
      const opportunityId = typeof rawId === 'number' ? rawId : parseInt(String(rawId), 10);
      if (!Number.isFinite(opportunityId) || !selectedBusiness?.id || !supabase) return;
      try { sessionStorage.removeItem('assistant_open_opportunity'); } catch { /* storage unavailable */ }

      if (activeTab !== 'opportunities') {
        setActiveTab('opportunities');
      }

      let opportunity = opportunities.find((opp) => opp.id === opportunityId);
      if (!opportunity) {
        try {
          const { data, error } = await supabase
            .from('pipeline_opportunities')
            .select('*')
            .eq('id', opportunityId)
            .eq('business_id', selectedBusiness.id)
            .single();
          if (error || !data) {
            console.error('Assistant open_lead: opportunity not found', error);
            return;
          }
          if (data.customer_id) {
            try {
              const customer = await getCustomer(data.customer_id);
              opportunity = {
                ...data,
                customer_name: customer
                  ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown Contact'
                  : 'Unknown Contact',
                customers: customer
                  ? {
                      id: customer.id,
                      first_name: customer.first_name || '',
                      last_name: customer.last_name || '',
                    }
                  : undefined,
              };
            } catch {
              opportunity = { ...data, customer_name: 'Unknown Contact' };
            }
          } else {
            opportunity = { ...data, customer_name: 'Unknown Contact' };
          }
        } catch (err) {
          console.error('Assistant open_lead: failed to fetch opportunity', err);
          return;
        }
      }

      if (opportunity) {
        handleOpenOpportunityInfo(opportunity);
      }
    };

    const onEvent = (e: Event) => {
      const id = (e as CustomEvent).detail?.opportunityId;
      if (id != null) void openFromAssistant(id);
    };
    window.addEventListener('assistant:openOpportunity', onEvent);
    try {
      const pending = sessionStorage.getItem('assistant_open_opportunity');
      if (pending) void openFromAssistant(pending);
    } catch { /* storage unavailable */ }
    return () => window.removeEventListener('assistant:openOpportunity', onEvent);
    // handleOpenOpportunityInfo is intentionally omitted — same pattern as the
    // location.state openOpportunityId effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness?.id, opportunities, activeTab, supabase]);

  const opportunityCreateMenuOptions = useMemo((): CrmCreateActionOption[] => {
    if (!selectedOpportunityForInfo) return [];

    const opportunity = selectedOpportunityForInfo;

    const loadCustomer = async (): Promise<Customer | null> => {
      if (!opportunity.customer_id) return null;
      try {
        return await getCustomer(opportunity.customer_id);
      } catch (error) {
        console.error('Error loading customer:', error);
        return null;
      }
    };

    const opts: CrmCreateActionOption[] = [];

    if (hasProposalsApp) {
      opts.push({
        id: 'proposal',
        label: 'Create Proposal',
        description: linkedProposal
          ? 'This opportunity already has a linked proposal'
          : 'Create a proposal from this opportunity',
        icon: FileText,
        disabled: !!linkedProposal,
        onSelect: async () => {
          if (linkedProposal) return;
          const customer = await loadCustomer();
          setProposalCustomer(customer);
          setOpportunityForProposal(opportunity);
          setIsCreateProposalModalOpen(true);
        },
      });
    }

    if (showLocationBooking) {
      const bookingBlocked = !!(linkedBooking || linkedProposal);
      opts.push({
        id: 'location-booking',
        label: 'Location Booking',
        description: linkedBooking
          ? 'This opportunity already has a linked booking'
          : linkedProposal
            ? 'Unlink the proposal before creating a booking'
            : 'Create a location-based booking',
        icon: Building2,
        disabled: bookingBlocked,
        onSelect: async () => {
          if (bookingBlocked) return;
          const customer = await loadCustomer();
          setBookingCustomer(customer);
          setOpportunityForBooking(opportunity);
          setIsOpportunityInfoModalOpen(false);
          setIsCreateBookingModalOpen(true);
        },
      });
    }

    if (showMobileBooking) {
      const bookingBlocked = !!(linkedBooking || linkedProposal);
      opts.push({
        id: 'mobile-booking',
        label: 'Mobile Booking',
        description: linkedBooking
          ? 'This opportunity already has a linked booking'
          : linkedProposal
            ? 'Unlink the proposal before creating a booking'
            : 'Create a mobile service booking',
        icon: Car,
        disabled: bookingBlocked,
        onSelect: async () => {
          if (bookingBlocked) return;
          const customer = await loadCustomer();
          setBookingCustomer(customer);
          setOpportunityForBooking(opportunity);
          setIsOpportunityInfoModalOpen(false);
          setIsCreateMobileBookingModalOpen(true);
        },
      });
    }

    if (showFieldQuotesInCrm) {
      opts.push({
        id: 'field-quote',
        label: 'Field Quote',
        description: linkedFieldQuote
          ? 'This opportunity already has a linked field quote'
          : linkedProposal
            ? 'Schedule a field visit; your linked proposal will be marked booked'
            : 'Schedule a field quote or measurement',
        icon: FileSearch,
        disabled: !!linkedFieldQuote,
        onSelect: async () => {
          if (linkedFieldQuote) return;
          const customer = await loadCustomer();
          setFieldQuoteCustomer(customer);
          setOpportunityForFieldQuote(opportunity);
          setIsScheduleFieldQuoteFromCrmOpen(true);
        },
      });
    }

    if (hasProjectsApp) {
      opts.push({
        id: 'project',
        label: 'Create Project',
        description: 'Create a project from this opportunity',
        icon: Folder,
        onSelect: async () => {
          const customer = await loadCustomer();
          setProjectCustomer(customer);
          setOpportunityForProject(opportunity);
          setIsCreateProjectModalOpen(true);
        },
      });
    }

    if (showSalesPinCreate) {
      const linkedSalesPin = salesPinMap.get(opportunity.id);
      opts.push({
        id: 'sales-pin',
        label: 'Sales Pin',
        description: linkedSalesPin
          ? 'This lead already has a linked sales pin'
          : 'Drop a map pin linked to this lead',
        icon: MapPin,
        disabled: !!linkedSalesPin,
        onSelect: () => {
          if (linkedSalesPin) return;
          setOpportunityForSalesPin(opportunity);
          setIsSalesPinModalOpen(true);
        },
      });
    }

    if (liveIntakeFormsEnabled && selectedCustomer) {
      opts.push({
        id: 'intake-form',
        label: 'Send & attach live intake form',
        description: 'Choose a template, then SMS or email a unique link',
        icon: ClipboardList,
        onSelect: () => {
          setIsSendIntakeFormModalOpen(true);
        },
      });
    }

    if (gcalConnected) {
      opts.push({
        id: 'google-calendar-event',
        label: 'Google Calendar Event',
        description: 'Create a meeting with customer details pre-filled',
        icon: CalendarDays,
        onSelect: async () => {
          const customer =
            selectedCustomer?.id === opportunity.customer_id
              ? selectedCustomer
              : await loadCustomer();
          setGcalEventPrefill(
            buildGoogleCalendarPrefillFromOpportunity(opportunity, customer),
          );
          setIsCreateGcalEventModalOpen(true);
        },
      });
    }

    return opts;
  }, [
    selectedOpportunityForInfo,
    hasProposalsApp,
    hasProjectsApp,
    showLocationBooking,
    showMobileBooking,
    showFieldQuotesInCrm,
    liveIntakeFormsEnabled,
    selectedCustomer,
    linkedProposal,
    linkedBooking,
    linkedFieldQuote,
    gcalConnected,
    showSalesPinCreate,
    salesPinMap,
  ]);

  /** Re-sync one opportunity's entry in salesPinMap (board pills + menu disabled state). */
  const refreshSalesPinForOpportunity = async (opportunityId: number) => {
    if (!supabase || !selectedBusiness?.id) return;
    try {
      const { data, error } = await supabase
        .from('address_markers')
        .select('id, opportunity_id, marker_type, address')
        .eq('business_id', selectedBusiness.id)
        .eq('opportunity_id', opportunityId)
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      const pin = data?.[0] as { id: string; marker_type: string; address?: string | null } | undefined;
      setSalesPinMap((prev) => {
        const next = new Map(prev);
        if (pin) {
          next.set(opportunityId, {
            id: pin.id,
            marker_type: pin.marker_type || 'interested',
            address: pin.address,
          });
        } else {
          next.delete(opportunityId);
        }
        return next;
      });
    } catch (err) {
      console.error('Error refreshing linked sales pin:', err);
    }
  };

  // Pin created or deleted while the drawer is open (drop modal, pin card
  // delete, FAB) — re-sync the open opportunity's pill + menu disabled state.
  useEffect(() => {
    const oppId = selectedOpportunityForInfo?.id;
    if (!oppId) return;
    const handler = () => { void refreshSalesPinForOpportunity(oppId); };
    window.addEventListener('salesPinCreated', handler);
    return () => window.removeEventListener('salesPinCreated', handler);
    // refreshSalesPinForOpportunity is stable per business; same pattern as
    // the other window-event effects in this file.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOpportunityForInfo?.id, selectedBusiness?.id]);

  // Handle closing opportunity info modal
  const handleCloseOpportunityInfo = () => {
    setIsOpportunityInfoModalOpen(false);
    setIsSendIntakeFormModalOpen(false);
    setLiveIntakeFormsEnabled(false);
    setSelectedOpportunityForInfo(null);
    setLinkedProposal(null);
    setLinkedBooking(null);
    setLinkedFieldQuote(null);
    setOpportunityForFieldQuote(null);
    setFieldQuoteCustomer(null);
    setIsScheduleFieldQuoteFromCrmOpen(false);
    setFieldQuoteSearchQuery('');
    setCrmFieldQuoteDetailId(null);
    setLastActivity(null);
    setFirstActivity(null);
    setTimeToFirstResponse(null);
    // Reset all form fields
    setSelectedCustomer(null);
    setOpportunityTitle('');
    setSelectedPipeline(null);
    setSelectedStage(null);
    setOpportunityValue(0);
    setBusinessName('');
    setOpportunitySource('');
    setOpportunityStatus('Open');
    setSelectedTags([]);
    // Reset custom field values but keep the definitions
    initializeCustomFields();
  };

  // Handle updating an existing opportunity
  const executeOpportunityUpdate = async (lostReason: string | null, options?: { closeAfter?: boolean }) => {
    if (!selectedCustomer || !selectedPipeline || !selectedStage || !selectedOpportunityForInfo || !supabase) return;

    const previousStageId = selectedOpportunityForInfo.stage_id;
    const previousPipelineId = selectedOpportunityForInfo.pipeline_id;

    try {
      console.log('Updating opportunity in database...');
      setIsLoading(true);
      
      const updateData: Record<string, any> = {
        customer_id: selectedCustomer.id,
        title: opportunityTitle,
        pipeline_id: selectedPipeline.id,
        stage_id: selectedStage.id,
        value: opportunityValue,
        business_name: businessName || null,
        source: opportunitySource || null,
        status: opportunityStatus,
        lead_info: leadInfo || null,
        tags: selectedTags.length > 0 ? JSON.stringify(selectedTags) : null,
        custom_fields: customFields.length > 0 ? JSON.stringify(customFields) : null,
        assigned_resource_id: assignedResourceId,
        updated_at: new Date().toISOString()
      };

      if (opportunityStatus === 'Lost') {
        updateData.lost_reason = lostReason;
      } else {
        updateData.lost_reason = null;
      }
      
      console.log('Update data:', updateData);
      
      const { data, error } = await supabase
        .from('pipeline_opportunities')
        .update(updateData)
        .eq('id', selectedOpportunityForInfo.id)
        .select()
        .single();

      console.log('Update response:', { data, error });

      if (error) throw error;

      if (
        updateData.stage_id !== previousStageId &&
        selectedBusiness?.id &&
        data?.id
      ) {
        const oldStage = selectedPipeline.stages?.find((s) => s.id === previousStageId);
        void triggerEnterStageAutomations(selectedBusiness.id, data.id, {
          previousStageId,
          previousStageName: oldStage?.name ?? null,
        });
      }

      const previousStatus = selectedOpportunityForInfo.status;
      if (opportunityStatus !== previousStatus && selectedBusiness?.id && selectedCustomer?.id) {
        recordOpportunityActivity({
          businessId: selectedBusiness.id,
          customerId: selectedCustomer.id,
          activityType: 'status_changed',
          activityData: { from: previousStatus, to: opportunityStatus },
          specificOpportunityId: selectedOpportunityForInfo.id
        });
      }

      if (
        selectedStage.id !== previousStageId &&
        selectedBusiness?.id &&
        selectedCustomer?.id
      ) {
        const oldStage = pipelines
          .find((p) => p.id === previousPipelineId)
          ?.stages?.find((s) => s.id === previousStageId);
        recordOpportunityActivity({
          businessId: selectedBusiness.id,
          customerId: selectedCustomer.id,
          activityType: 'stage_moved',
          activityData: {
            old_stage_id: previousStageId,
            new_stage_id: selectedStage.id,
            old_stage_name: oldStage?.name ?? null,
            new_stage_name: selectedStage.name,
          },
          specificOpportunityId: selectedOpportunityForInfo.id,
        });
      }

      if (
        selectedPipeline.id !== previousPipelineId &&
        selectedBusiness?.id &&
        selectedCustomer?.id
      ) {
        const oldPipeline = pipelines.find((p) => p.id === previousPipelineId);
        recordOpportunityActivity({
          businessId: selectedBusiness.id,
          customerId: selectedCustomer.id,
          activityType: 'pipeline_changed',
          activityData: {
            old_pipeline_id: previousPipelineId,
            new_pipeline_id: selectedPipeline.id,
            old_pipeline_name: oldPipeline?.pipeline_name ?? null,
            new_pipeline_name: selectedPipeline.pipeline_name,
          },
          specificOpportunityId: selectedOpportunityForInfo.id,
        });
      }

      setOpportunities(prev =>
        prev.map(opp => opp.id === selectedOpportunityForInfo.id ? data : opp)
      );
      
      console.log('Opportunity updated successfully:', data);
      
      if (options?.closeAfter === false) {
        // Live-update mode: keep the modal open, just refresh the saved snapshot.
        setSelectedOpportunityForInfo(data);
      } else {
        handleModalClose();
        setSelectedOpportunityForInfo(null);
      }
      
    } catch (err) {
      console.error('Error updating opportunity:', err);
      setError('Failed to update opportunity. Please try again.');
      if (options?.closeAfter !== false) {
        alert('Error updating opportunity: ' + (err as Error).message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Keep a ref to the latest update fn so the auto-save effect can call it without
  // re-subscribing every render (which would reset the debounce timer).
  const executeOpportunityUpdateRef = useRef(executeOpportunityUpdate);
  executeOpportunityUpdateRef.current = executeOpportunityUpdate;

  // Live auto-save: in details mode, persist field changes automatically (debounced)
  // instead of requiring a "Save Changes" button. Skips the first run per opportunity
  // (initial hydration) so opening a lead doesn't trigger a write.
  const autoSaveHydratedIdRef = useRef<string | number | null>(null);
  // Holds the most recent pending save so we can FLUSH it immediately when the app
  // backgrounds/closes or the modal closes — so edits are never lost if the user
  // quits the app before the debounce fires.
  const pendingOppSaveRef = useRef<null | (() => void)>(null);
  useEffect(() => {
    const snap = selectedOpportunityForInfo;
    if (!snap || !supabase) { pendingOppSaveRef.current = null; return; }

    // Hydration guard: skip the first evaluation after a lead is loaded.
    if (autoSaveHydratedIdRef.current !== snap.id) {
      autoSaveHydratedIdRef.current = snap.id;
      pendingOppSaveRef.current = null;
      return;
    }

    // Can't persist an incomplete record (stage is NOT NULL).
    if (!selectedCustomer || !selectedPipeline || !selectedStage) return;
    // Don't auto-save while the lost-reason prompt is resolving.
    if (isLostReasonModalOpen) return;

    const customFieldsStr = customFields.length > 0 ? JSON.stringify(customFields) : null;
    const tagsStr = selectedTags.length > 0 ? JSON.stringify(selectedTags) : null;

    const dirty =
      opportunityTitle !== (snap.title || '') ||
      opportunityValue !== (snap.value ?? 0) ||
      opportunitySource !== (snap.source || '') ||
      businessName !== (snap.business_name || '') ||
      opportunityStatus !== snap.status ||
      leadInfo !== ((snap as any).lead_info || '') ||
      selectedStage.id !== snap.stage_id ||
      selectedPipeline.id !== snap.pipeline_id ||
      customFieldsStr !== ((snap as any).custom_fields ?? null) ||
      tagsStr !== ((snap as any).tags ?? null) ||
      assignedResourceId !== (snap.assigned_resource_id ?? null);

    if (!dirty) { pendingOppSaveRef.current = null; return; }

    // Moving to Lost captures a reason via the dedicated modal first.
    if (opportunityStatus === 'Lost' && snap.status !== 'Lost' && recordLostReasonEnabled) {
      setPendingLostOpportunity(snap);
      setPendingLostSource('modal');
      setIsLostReasonModalOpen(true);
      return;
    }

    const lostArg = opportunityStatus === 'Lost' ? ((snap as any).lost_reason ?? null) : null;
    const doSave = () => {
      pendingOppSaveRef.current = null;
      void executeOpportunityUpdateRef.current(lostArg, { closeAfter: false });
    };
    pendingOppSaveRef.current = doSave;
    const handle = setTimeout(doSave, 600);
    // NOTE: only clear the timer here — keep pendingOppSaveRef so a background/close
    // event can still flush the latest pending edit.
    return () => clearTimeout(handle);
  }, [
    selectedOpportunityForInfo,
    selectedCustomer,
    selectedPipeline,
    selectedStage,
    opportunityTitle,
    opportunityValue,
    opportunitySource,
    businessName,
    opportunityStatus,
    leadInfo,
    customFields,
    selectedTags,
    assignedResourceId,
    isLostReasonModalOpen,
    recordLostReasonEnabled,
  ]);

  // Flush any pending opportunity edit immediately when the app is backgrounded or
  // the page is being unloaded (e.g. user quits the app before the debounce fires).
  useEffect(() => {
    const flush = () => {
      if (pendingOppSaveRef.current) pendingOppSaveRef.current();
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    let appStateListener: { remove: () => void } | null = null;
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) flush();
    }).then((l) => { appStateListener = l; }).catch(() => {});
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
      appStateListener?.remove();
    };
  }, []);

  // Handle deleting an opportunity
  const handleDeleteOpportunity = async () => {
    if (!selectedOpportunityForInfo || !supabase) {
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete the opportunity "${selectedOpportunityForInfo.title}"? This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      console.log('Deleting opportunity from database...');
      setIsLoading(true);
      
      // Delete the opportunity from database
      // Note: facebook_leads_log records will be automatically deleted via CASCADE
      const { error } = await supabase
        .from('pipeline_opportunities')
        .delete()
        .eq('id', selectedOpportunityForInfo.id);

      console.log('Delete response error:', error);

      if (error) throw error;

      // Remove the opportunity from local state
      setOpportunities(prev => 
        prev.filter(opp => opp.id !== selectedOpportunityForInfo.id)
      );
      
      console.log('Opportunity deleted successfully');
      
      // Close the modal without prompting to schedule follow-up on a deleted lead
      setFollowUpPrompt(null);
      handleModalClose({ skipFollowUpPrompt: true });
      setSelectedOpportunityForInfo(null);
      
    } catch (err) {
      console.error('Error deleting opportunity:', err);
      setError('Failed to delete opportunity. Please try again.');
      alert('Error deleting opportunity: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle deleting an opportunity directly from the card
  const handleDeleteOpportunityFromCard = async (opportunity: Opportunity) => {
    if (!supabase) {
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete the opportunity "${opportunity.title}"? This action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    try {
      console.log('Deleting opportunity from card...');
      setIsLoading(true);
      
      // Delete the opportunity from database
      // Note: facebook_leads_log records will be automatically deleted via CASCADE
      const { error } = await supabase
        .from('pipeline_opportunities')
        .delete()
        .eq('id', opportunity.id);

      console.log('Delete response error:', error);

      if (error) throw error;

      // Remove the opportunity from local state
      setOpportunities(prev => 
        prev.filter(opp => opp.id !== opportunity.id)
      );
      
      console.log('Opportunity deleted successfully from card');
      
      if (selectedOpportunityForInfo?.id === opportunity.id) {
        setFollowUpPrompt(null);
        handleModalClose({ skipFollowUpPrompt: true });
        setSelectedOpportunityForInfo(null);
      }
    } catch (err) {
      console.error('Error deleting opportunity:', err);
      setError('Failed to delete opportunity. Please try again.');
      alert('Error deleting opportunity: ' + (err as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickStatusChange = async (opportunity: Opportunity, newStatus: string) => {
    if (!supabase || newStatus === opportunity.status) return;

    if (newStatus === 'Lost' && recordLostReasonEnabled) {
      setPendingLostOpportunity(opportunity);
      setPendingLostSource('card');
      setIsLostReasonModalOpen(true);
      return;
    }

    await executeStatusChange(opportunity, newStatus, null);
  };

  const executeStatusChange = async (opportunity: Opportunity, newStatus: string, lostReason: string | null) => {
    if (!supabase) return;
    const oldStatus = opportunity.status;

    try {
      const updateData: Record<string, any> = { status: newStatus, updated_at: new Date().toISOString() };
      if (newStatus === 'Lost' && lostReason) {
        updateData.lost_reason = lostReason;
      }
      if (newStatus !== 'Lost') {
        updateData.lost_reason = null;
      }

      const { error } = await supabase
        .from('pipeline_opportunities')
        .update(updateData)
        .eq('id', opportunity.id);

      if (error) throw error;

      setOpportunities(prev =>
        prev.map(opp => opp.id === opportunity.id ? { ...opp, status: newStatus } : opp)
      );

      if (selectedOpportunityForInfo?.id === opportunity.id) {
        setSelectedOpportunityForInfo({ ...selectedOpportunityForInfo, status: newStatus });
        setOpportunityStatus(newStatus);
      }

      if (selectedBusiness?.id && opportunity.customer_id) {
        recordOpportunityActivity({
          businessId: selectedBusiness.id,
          customerId: opportunity.customer_id,
          activityType: 'status_changed',
          activityData: { from: oldStatus, to: newStatus },
          specificOpportunityId: opportunity.id
        });
      }
    } catch (err) {
      console.error('Error updating opportunity status:', err);
    }
  };

  // Mobile detection and resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load data on component mount

  // Set default pipeline when pipelines load
  useEffect(() => {
    if (pipelines.length > 0 && !opportunitiesViewPipeline) {
      setOpportunitiesViewPipeline(pipelines[0]);
    }
  }, [pipelines, opportunitiesViewPipeline]);

  // Set up realtime subscription to detect when linked proposal is deleted
  useEffect(() => {
    if (!isOpportunityModalOpen || !selectedOpportunityForInfo || !linkedProposal?.id || !selectedBusiness?.id || !supabase) {
      return;
    }

    console.log('ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ¢â‚¬Â Setting up realtime subscription for proposal:', linkedProposal.id);

    const proposalChannel = supabase
      .channel(`opportunity_proposal_${selectedOpportunityForInfo.id}_${linkedProposal.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'proposals',
          filter: `id=eq.${linkedProposal.id}`
        },
        async (payload: any) => {
          console.log('ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ¢â‚¬Å¾ Proposal updated via realtime:', payload, 'eventType:', payload.eventType, 'hasOld:', !!payload.old, 'hasNew:', !!payload.new);
          
          // Handle DELETE event - Supabase sends DELETE events with old data but no new data
          const isDelete = payload.eventType === 'DELETE' || 
                          (payload.old && !payload.new) ||
                          (payload.old && payload.old.id === linkedProposal.id && !payload.new);
          
          if (isDelete) {
            console.log('ÃƒÂ°Ã…Â¸—Ã¢â‚¬ËœÃƒÂ¯Ã‚Â¸Ã‚Â Linked proposal deleted via realtime, clearing from opportunity view');
            setLinkedProposal(null);
          } 
          // Handle UPDATE events - reload proposal if it still exists
          else if (payload.new && payload.new.id === linkedProposal.id) {
            try {
              const proposalData = await getProposal(payload.new.id);
              setLinkedProposal(proposalData);
            } catch (error) {
              console.error('Error fetching updated proposal:', error);
              // If proposal fetch fails (e.g., it was deleted), clear it
              setLinkedProposal(null);
            }
          }
        }
      )
      .subscribe();

    return () => {
      console.log('ÃƒÂ°Ã…Â¸Ã¢â‚¬Â• Cleaning up realtime subscription for proposal');
      supabase.removeChannel(proposalChannel);
    };
  }, [isOpportunityModalOpen, selectedOpportunityForInfo?.id, linkedProposal?.id, selectedBusiness?.id]);

  // Handle status filter toggle
  const handleStatusToggle = (status: string) => {
    setSelectedStatuses(prev => {
      return prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status];
    });
  };

  // Calculate dropdown position
  const calculateDropdownPosition = () => {
    if (statusButtonRef.current) {
      const rect = statusButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Handle status filter open/close
  const handleStatusFilterToggle = () => {
    if (!isStatusFilterOpen) {
      calculateDropdownPosition();
      setIsStatusFilterOpen(true);
    } else {
      setIsStatusFilterOpen(false);
      setDropdownPosition(null);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isStatusFilterOpen) {
        const target = event.target as Node;
        const isInsideButton = statusButtonRef.current?.contains(target);
        const isInsideDropdown = dropdownRef.current?.contains(target);
        
        if (!isInsideButton && !isInsideDropdown) {
          setIsStatusFilterOpen(false);
          setDropdownPosition(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isStatusFilterOpen]);

  // Mobile swipe handling functions
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
    setDragOffset(0);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || !isDragging) return;
    
    const currentX = e.targetTouches[0].clientX;
    const diff = currentX - touchStart;
    
    // Limit drag distance to prevent over-scrolling
    const maxDrag = window.innerWidth * 0.8;
    const limitedDiff = Math.max(-maxDrag, Math.min(maxDrag, diff));
    
    setTouchEnd(currentX);
    setDragOffset(limitedDiff);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || isAnimating) return;
    
    setIsDragging(false);
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (opportunitiesViewPipeline?.stages) {
      const maxIndex = opportunitiesViewPipeline.stages.length - 1;
      
      if (isLeftSwipe && currentStageIndex < maxIndex) {
        // Animate to complete the swipe
        setIsAnimating(true);
        setSlideDirection('left');
        setTimeout(() => {
          setCurrentStageIndex(prev => prev + 1);
          setDragOffset(0);
          setSlideDirection(null);
          setTimeout(() => setIsAnimating(false), 50);
        }, 150);
      } else if (isRightSwipe && currentStageIndex > 0) {
        // Animate to complete the swipe
        setIsAnimating(true);
        setSlideDirection('right');
        setTimeout(() => {
          setCurrentStageIndex(prev => prev - 1);
          setDragOffset(0);
          setSlideDirection(null);
          setTimeout(() => setIsAnimating(false), 50);
        }, 150);
      } else {
        // Snap back to original position
        setIsAnimating(true);
        setTimeout(() => {
          setDragOffset(0);
          setTimeout(() => setIsAnimating(false), 200);
        }, 50);
      }
    } else {
      // Reset if no pipeline
      setDragOffset(0);
    }
  };

  // Reset stage index when pipeline changes
  React.useEffect(() => {
    setCurrentStageIndex(0);
  }, [opportunitiesViewPipeline]);

  // Scroll snap handler with 1/4 threshold for bounce effect
  const pipelineScrollRef = React.useRef<HTMLDivElement | null>(null);
  
  React.useEffect(() => {
    const scrollContainer = pipelineScrollRef.current;
    if (!scrollContainer || !opportunitiesViewPipeline?.stages) return;

    let scrollTimeout: NodeJS.Timeout;

    const handleScrollEnd = () => {
      const container = scrollContainer;
      const scrollLeft = container.scrollLeft;
      
      // Get all column elements
      const columns = Array.from(container.querySelectorAll('[style*="scrollSnapAlign"]')) as HTMLElement[];
      if (columns.length === 0) return;
      
      // Find the closest column to current scroll position
      let closestColumn = columns[0];
      let closestDistance = Infinity;
      
      columns.forEach((column) => {
        const columnLeft = column.offsetLeft;
        const distance = Math.abs(scrollLeft - columnLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestColumn = column;
        }
      });
      
      const closestColumnLeft = closestColumn.offsetLeft;
      const columnWidth = closestColumn.offsetWidth;
      const scrollOffset = Math.abs(scrollLeft - closestColumnLeft);
      const threshold = columnWidth * 0.25; // 1/4 threshold
      
      // If we've scrolled less than 1/4, snap back to current column
      if (scrollOffset < threshold) {
        container.scrollTo({
          left: closestColumnLeft,
          behavior: 'smooth'
        });
      } else {
        // If we've scrolled more than 1/4, snap to next/previous column
        const currentIndex = columns.indexOf(closestColumn);
        const nextIndex = scrollLeft > closestColumnLeft 
          ? Math.min(currentIndex + 1, columns.length - 1)
          : Math.max(currentIndex - 1, 0);
        const snapTo = columns[nextIndex].offsetLeft;
        
        container.scrollTo({
          left: snapTo,
          behavior: 'smooth'
        });
      }
    };

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      
      // Wait for scroll to end before snapping
      scrollTimeout = setTimeout(() => {
        handleScrollEnd();
      }, 100);
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [opportunitiesViewPipeline]);

  const embedOpenedRef = React.useRef<number | null>(null);
  const embedCreateOpenedRef = React.useRef(false);
  const handleOpenOpportunityModalRef = React.useRef(handleOpenOpportunityModal);
  handleOpenOpportunityModalRef.current = handleOpenOpportunityModal;

  useEffect(() => {
    if (!embedCreateOpportunity || embedOpportunityId != null) return;
    if (!selectedBusiness?.id || pipelines.length === 0) return;
    if (embedCreateOpenedRef.current || isOpportunityModalOpen) return;

    const pipeline =
      (embedPipelineId != null ? pipelines.find((p) => p.id === embedPipelineId) : null) ??
      pipelines[0];
    if (!pipeline) return;

    embedCreateOpenedRef.current = true;
    handleOpenOpportunityModalRef.current(pipeline);
    // Pre-link the customer when provided (e.g. "Create Lead" from a conversation).
    if (embedInitialCustomer) {
      setSelectedCustomer(embedInitialCustomer);
    }
  }, [
    embedCreateOpportunity,
    embedOpportunityId,
    embedPipelineId,
    embedInitialCustomer,
    pipelines,
    selectedBusiness?.id,
    isOpportunityModalOpen,
  ]);

  useEffect(() => {
    if (!embedOpportunityId || !selectedBusiness?.id || !supabase) return;
    if (pipelines.length === 0) return;

    const openEmbeddedOpportunity = async () => {
      if (embedOpenedRef.current === embedOpportunityId && isOpportunityModalOpen) return;

      let opportunity = opportunities.find((opp) => opp.id === embedOpportunityId);

      if (!opportunity) {
        try {
          const { data, error } = await supabase
            .from('pipeline_opportunities')
            .select('*')
            .eq('id', embedOpportunityId)
            .eq('business_id', selectedBusiness.id)
            .single();

          if (error || !data) {
            console.error('Error fetching embedded opportunity:', error);
            return;
          }

          if (data.customer_id) {
            try {
              const customer = await getCustomer(data.customer_id);
              opportunity = {
                ...data,
                customer_name: customer
                  ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown Contact'
                  : 'Unknown Contact',
                customers: customer
                  ? {
                      id: customer.id,
                      first_name: customer.first_name || '',
                      last_name: customer.last_name || '',
                    }
                  : undefined,
              };
            } catch (err) {
              console.error('Error fetching customer for embedded opportunity:', err);
              opportunity = { ...data, customer_name: 'Unknown Contact' };
            }
          } else {
            opportunity = { ...data, customer_name: 'Unknown Contact' };
          }
        } catch (err) {
          console.error('Exception fetching embedded opportunity:', err);
          return;
        }
      }

      embedOpenedRef.current = embedOpportunityId;
      handleOpenOpportunityInfo(opportunity);
    };

    void openEmbeddedOpportunity();
  }, [embedOpportunityId, opportunities, selectedBusiness?.id, supabase, isOpportunityModalOpen, pipelines]);

  return (
    <div className={hidePageChrome ? undefined : 'min-h-screen bg-gray-50/30 border-0'} data-crm-page style={{ border: 'none', outline: 'none' }}>
      {!hidePageChrome && (
      <div className="w-full px-4 sm:px-6 py-4 sm:py-6" style={{ border: 'none', outline: 'none' }}>
        <div className="space-y-6">
           {/* Header - iOS Native Style */}
           <div className={`${isMobile ? 'space-y-4' : 'flex items-center justify-between'}`}>
             <div className={`${isMobile ? 'flex items-center justify-between' : 'flex items-center gap-4'}`}>
               <h1 className={`${isMobile ? 'text-xl' : 'text-3xl'} font-bold text-gray-900 tracking-tight`} style={{ 
                 fontWeight: 700,
                 letterSpacing: '-0.02em',
               }}>
                 {activeTab === 'opportunities' ? 'Deals' : 'Pipelines'}
               </h1>
               
               {/* Pipeline Selector - Only show on Opportunities tab */}
               {!isMobile && activeTab === 'opportunities' && pipelines.length > 1 && (
                 <select
                   value={opportunitiesViewPipeline?.id || ''}
                   onChange={(e) => {
                     const selectedPipeline = pipelines.find(p => p.id === Number(e.target.value));
                     setOpportunitiesViewPipeline(selectedPipeline || null);
                   }}
                   className="px-4 py-2.5 border border-gray-200/60 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 text-sm font-medium text-gray-700 shadow-sm transition-all duration-200"
                   style={{
                     boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                   }}
                 >
                   {pipelines.map(pipeline => (
                     <option key={pipeline.id} value={pipeline.id}>
                       {pipeline.pipeline_name}
                     </option>
                   ))}
                 </select>
               )}
               
               {isMobile && (
                 <div className="flex items-center gap-2 flex-1 justify-end">
                   {activeTab === 'opportunities' && (
                     <>
                       {/* Lead search toggle - Mobile */}
                       <button
                         type="button"
                         onClick={() => setDealSearchOpen(true)}
                         aria-label="Search leads"
                         className="p-2.5 border border-gray-200/60 rounded-xl bg-white/80 backdrop-blur-sm text-gray-500 hover:text-gray-700 shadow-sm transition-all duration-200 active:scale-95 flex-shrink-0"
                         style={{
                           boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                         }}
                       >
                         <Search className="h-4 w-4" />
                       </button>

                       {/* Status Filter - Simple Select Dropdown */}
                       <select
                         value={selectedStatuses[0] || 'Open'}
                         onChange={(e) => {
                           setSelectedStatuses([e.target.value]);
                         }}
                         className="px-3 py-2.5 border border-gray-200/60 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 text-sm font-medium text-gray-700 shadow-sm transition-all duration-200 flex-shrink-0"
                         style={{
                           boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                           width: '90px',
                         }}
                       >
                         {availableStatuses.map(status => (
                           <option key={status} value={status}>
                             {status}
                           </option>
                         ))}
                       </select>
                       
                       <button 
                         onClick={() => setActiveTab('pipelines')}
                         className="px-3 py-2.5 bg-gray-100/80 hover:bg-gray-200/80 text-gray-700 font-semibold rounded-xl transition-all duration-200 text-sm active:scale-95 shadow-sm flex-shrink-0"
                         style={{
                           boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                         }}
                       >
                         Pipelines
                       </button>
                       
                       {/* Create Button - Mobile */}
                       <button 
                         onClick={() => {
                           if (activeTab !== 'opportunities') return handleOpenModal();
                           if (aiCardScanEnabled) return setIsLeadChoiceOpen(true);
                           handleOpenOpportunityModal(opportunitiesViewPipeline || undefined);
                         }}
                         className="px-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-200 active:scale-95 shadow-lg flex-shrink-0 text-sm"
                         style={{
                           boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
                         }}
                       >
                         +New Lead
                       </button>
                     </>
                   )}
                   {activeTab === 'pipelines' && (
                     <>
                     <button 
                       onClick={() => setActiveTab('opportunities')}
                         className="px-3 py-2.5 bg-gray-100/80 hover:bg-gray-200/80 text-gray-700 font-semibold rounded-xl transition-all duration-200 text-sm active:scale-95 shadow-sm flex-shrink-0"
                         style={{
                           boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                         }}
                     >
                         Deals
                     </button>
                   
                   {/* Create Button - Mobile */}
                   <button 
                         onClick={() => handleOpenModal()}
                         className="px-3 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-200 active:scale-95 shadow-lg flex-shrink-0 text-sm"
                         style={{
                           boxShadow: '0 2px 8px rgba(37, 99, 235, 0.3)',
                         }}
                       >
                         Create
                   </button>
                     </>
                   )}
                 </div>
               )}
             </div>
             
             {/* Mobile: Pipeline Selector + Search Bar */}
             {isMobile && activeTab === 'opportunities' && (
               <div className="flex items-center gap-2">
                 {/* Pipeline Selector */}
                 {pipelines.length > 1 && (
                   <select
                     value={opportunitiesViewPipeline?.id || ''}
                     onChange={(e) => {
                       const selectedPipeline = pipelines.find(p => p.id === Number(e.target.value));
                       setOpportunitiesViewPipeline(selectedPipeline || null);
                     }}
                     className="px-3 py-2.5 border border-gray-200/60 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50 text-sm font-medium text-gray-700 flex-shrink-0 shadow-sm transition-all duration-200"
                     style={{
                       boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                     }}
                   >
                     {pipelines.map(pipeline => (
                       <option key={pipeline.id} value={pipeline.id}>
                         {pipeline.pipeline_name}
                       </option>
                     ))}
                   </select>
                 )}
               </div>
             )}
             
             {!isMobile && (
               <div className="flex items-center gap-3">
                 {activeTab === 'opportunities' && (
                   <>
                     {/* Lead search toggle - Desktop */}
                     <button
                       type="button"
                       onClick={() => setDealSearchOpen(true)}
                       aria-label="Search leads"
                       className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 bg-white text-sm font-medium transition-colors"
                     >
                       <Search className="h-4 w-4" />
                       <span>Search</span>
                     </button>

                     {/* Status Filter - Simple Select Dropdown (matches mobile) */}
                     <select
                       value={selectedStatuses[0] || 'Open'}
                       onChange={(e) => {
                         setSelectedStatuses([e.target.value]);
                       }}
                       className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm font-medium text-gray-700"
                     >
                       {availableStatuses.map(status => (
                         <option key={status} value={status}>
                           {status}
                         </option>
                       ))}
                     </select>
                     <button 
                       onClick={() => setActiveTab('pipelines')}
                       className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                     >
                       <Settings className="h-4 w-4" />
                       <span>Pipelines</span>
                     </button>
                   </>
                 )}
                 {activeTab === 'pipelines' && (
                   <button 
                     onClick={() => setActiveTab('opportunities')}
                     className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                   >
                     <TrendingUp className="h-4 w-4" />
                     <span>Deals</span>
                   </button>
                 )}
                 {/* Create Button - Desktop Only */}
                 <button 
                   onClick={() => {
                     if (activeTab !== 'opportunities') return handleOpenModal();
                     if (aiCardScanEnabled) return setIsLeadChoiceOpen(true);
                     handleOpenOpportunityModal(opportunitiesViewPipeline || undefined);
                   }}
                   className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                 >
                   {activeTab === 'opportunities' ? 'Create Deal' : 'Create Pipeline'}
                 </button>
               </div>
             )}
           </div>
      
      {/* Lead search bar — results in a dropdown; the board below stays untouched */}
      {activeTab === 'opportunities' && dealSearchOpen && (
        <div className="relative" ref={dealSearchWrapRef}>
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              ref={dealSearchInputRef}
              value={dealSearchQuery}
              onChange={(e) => setDealSearchQuery(e.target.value)}
              placeholder={`Search ${(selectedStatuses[0] || 'Open').toLowerCase()} leads${opportunitiesViewPipeline ? ` in ${opportunitiesViewPipeline.pipeline_name}` : ''}...`}
              className="w-full pl-10 pr-10 py-2.5 border border-gray-200/60 rounded-xl bg-white shadow-sm text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/50"
            />
            <button
              type="button"
              onClick={() => {
                setDealSearchOpen(false);
                setDealSearchQuery('');
              }}
              aria-label="Close search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Results dropdown */}
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl z-40 max-h-[55vh] overflow-y-auto py-1">
            {dealSearchResults.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-400 text-center">
                No {(selectedStatuses[0] || 'Open').toLowerCase()} leads match your search
              </p>
            ) : (
              dealSearchResults.map(({ opportunity, stageName }) => {
                const customer =
                  opportunity.customer_name ||
                  [opportunity.customers?.first_name, opportunity.customers?.last_name]
                    .filter(Boolean)
                    .join(' ');
                return (
                  <button
                    key={opportunity.id}
                    type="button"
                    onClick={() => {
                      setDealSearchOpen(false);
                      setDealSearchQuery('');
                      handleOpenOpportunityInfo(opportunity);
                    }}
                    className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-gray-50 active:bg-gray-100 touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {opportunity.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {customer ? `${customer} · ` : ''}
                        {stageName}
                      </p>
                    </div>
                    <span className="text-xs font-semibold text-gray-700 shrink-0">
                      {formatCurrency(opportunity.value || 0)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Page content */}
        {activeTab === 'opportunities' && (
            <div className="space-y-6">
            
              {/* Kanban Board */}
              <div className="bg-white overflow-hidden">
                <div className="">
                  {isLoadingOpportunities ? (
                    <CRMSkeleton type="opportunities" isMobile={isMobile} />
                  ) : pipelines.length === 0 ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center max-w-md">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Building className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Pipelines Created</h3>
                        <p className="text-gray-500 text-sm mb-6">
                          Create a pipeline to start managing your opportunities and track your sales process.
                        </p>
                        <button 
                          onClick={() => setActiveTab('pipelines')}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                        >
                          Create Your First Pipeline
                        </button>
                      </div>
                    </div>
                                     ) : !opportunitiesViewPipeline || !opportunitiesViewPipeline.stages || opportunitiesViewPipeline.stages.length === 0 ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center max-w-md">
                        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-lg flex items-center justify-center">
                          <TrendingUp className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Stages in Pipeline</h3>
                        <p className="text-gray-500 text-sm mb-6">
                          This pipeline doesn't have any stages. Edit the pipeline to add stages and start tracking opportunities.
                        </p>
                        <button 
                          onClick={() => setActiveTab('pipelines')}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                        >
                          Edit Pipeline
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Kanban Board - Same for Mobile and Desktop */
                    <div 
                      ref={pipelineScrollRef}
                      className="flex space-x-4 sm:space-x-6 pb-4 overflow-x-auto ios-momentum-scroll" 
                              style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(156, 163, 175, 0.2) transparent',
                        scrollSnapType: 'x mandatory',
                        WebkitOverflowScrolling: 'touch',
                      }}
                    >
                        {boardStages.map(stage => {
                          // Use memoized filtered opportunities
                          const stageOpportunities = opportunitiesByStage.get(stage.id) || [];
                          
                          // Use memoized total value
                          const totalValue = stageTotals.get(stage.id) || 0;
                          
                          return (
                            <div 
                              key={stage.id} 
                              className={`flex-shrink-0 w-80 bg-gray-50/50 backdrop-blur-sm border border-gray-200/60 rounded-2xl p-4 transition-all duration-200 flex flex-col ${
                                dragOverStageId === stage.id ? 'border-blue-400 bg-blue-50/50 shadow-lg' : 'border-gray-200/60'
                              }`}
                              style={{
                                maxHeight: 'calc(100vh - 200px)',
                                boxShadow: dragOverStageId === stage.id 
                                  ? '0 4px 12px rgba(59, 130, 246, 0.15)' 
                                  : '0 1px 3px rgba(0, 0, 0, 0.05)',
                                scrollSnapAlign: 'start',
                                scrollSnapStop: 'always',
                              }}
                              onDragOver={(e) => handleStageDragOver(e, stage.id)}
                              onDragLeave={handleStageDragLeave}
                              onDrop={stage.id === UNASSIGNED_STAGE_ID ? undefined : (e) => handleStageDrop(e, stage.id)}
                            >
                              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                                <div>
                                  <h3 className="font-semibold text-gray-900 text-base tracking-tight">{stage.name}</h3>
                                  <div className="text-xs text-gray-500 mt-1 font-medium">
                                    ${totalValue.toLocaleString()} • {stageOpportunities.length} {stageOpportunities.length === 1 ? 'deal' : 'deals'}
                                  </div>
                                </div>
                                {stage.id === UNASSIGNED_STAGE_ID ? null : (
                                <button className="text-gray-400 hover:text-gray-600 transition-colors duration-150 p-1 rounded-lg hover:bg-gray-100">
                                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>
                                  </svg>
                                </button>
                                )}
                              </div>
                              
                              {/* Opportunity Cards - Scrollable Container */}
                              <div 
                                className="space-y-3 flex-1 overflow-y-auto overflow-x-hidden ios-momentum-scroll"
                                style={{
                                  maxHeight: 'calc(100vh - 280px)',
                                  scrollbarWidth: 'thin',
                                  scrollbarColor: 'rgba(156, 163, 175, 0.3) transparent',
                                }}
                              >
                                {stageOpportunities.length > 0 ? (
                                  stageOpportunities.map((opportunity: Opportunity) => (
                                    <div 
                                      ref={(el) => {
                                        if (opportunity.id) {
                                          opportunityCardRefs.current[opportunity.id] = el;
                                        }
                                      }}
                                    key={opportunity.id} 
                                    draggable={!isMobile}
                                    onDragStart={(e) => handleOpportunityDragStart(e, opportunity)}
                                    onDragEnd={handleOpportunityDragEnd}
                                      className={`bg-white border border-gray-200/60 rounded-xl p-4 cursor-pointer transition-all duration-200 group relative ${
                                        draggedOpportunity?.id === opportunity.id 
                                          ? 'opacity-50 cursor-grabbing scale-95' 
                                          : moveAnimationOpportunity?.id === opportunity.id
                                          ? 'opacity-0'
                                          : 'hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'
                                      }`}
                                      style={{ 
                                        cursor: draggedOpportunity?.id === opportunity.id ? 'grabbing' : 'pointer',
                                        // Remove initial animation for faster load - only animate new items
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                                        overflow: 'visible',
                                      }}
                                    onClick={() => { setOpenStatusMenuId(null); handleOpenOpportunityInfo(opportunity); }}
                                  >
                                    
                                    {/* Deal Title + Status */}
                                    <div className="flex items-start justify-between mb-3 gap-2">
                                        <h4 className="font-semibold text-gray-900 text-sm leading-tight flex-1 min-w-0">{opportunity.title}</h4>
                                      {/* Status pill with dropdown */}
                                        <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                          {opportunity.is_unread && (
                                            <div className="absolute -top-1.5 -right-1 w-2 h-2 bg-red-500 rounded-full z-20 ring-2 ring-white"></div>
                                          )}
                                          <button
                                            onClick={() => setOpenStatusMenuId(openStatusMenuId === opportunity.id ? null : opportunity.id)}
                                            className={`flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full transition-all duration-200 active:scale-95 ${
                                              opportunity.status === 'Won'
                                                ? 'bg-emerald-100 text-emerald-700'
                                                : opportunity.status === 'Lost'
                                                ? 'bg-red-100 text-red-700'
                                                : 'bg-gray-100 text-gray-600'
                                            }`}
                                          >
                                            <span className={`w-1.5 h-1.5 rounded-full ${
                                              opportunity.status === 'Won' ? 'bg-emerald-500' : opportunity.status === 'Lost' ? 'bg-red-500' : 'bg-gray-400'
                                            }`} />
                                            {opportunity.status}
                                            <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${openStatusMenuId === opportunity.id ? 'rotate-180' : ''} ${
                                              opportunity.status === 'Won' ? 'text-emerald-500' : opportunity.status === 'Lost' ? 'text-red-500' : 'text-gray-400'
                                            }`} />
                                          </button>
                                          {openStatusMenuId === opportunity.id && (
                                            <>
                                              <div className="fixed inset-0 z-30" onClick={() => setOpenStatusMenuId(null)} />
                                              <div className="absolute right-0 top-full mt-1.5 z-40 bg-white rounded-xl shadow-lg border border-gray-200/80 overflow-hidden min-w-[120px]"
                                                style={{ boxShadow: '0 8px 30px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)' }}>
                                                {['Open', 'Won', 'Lost'].map((status) => (
                                                  <button
                                                    key={status}
                                                    onClick={() => {
                                                      handleQuickStatusChange(opportunity, status);
                                                      setOpenStatusMenuId(null);
                                                    }}
                                                    className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-medium transition-colors duration-150 ${
                                                      opportunity.status === status
                                                        ? 'bg-gray-50'
                                                        : 'hover:bg-gray-50 active:bg-gray-100'
                                                    }`}
                                                  >
                                                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                                      status === 'Won' ? 'bg-emerald-500' : status === 'Lost' ? 'bg-red-500' : 'bg-gray-400'
                                                    }`} />
                                                    <span className={
                                                      status === 'Won' ? 'text-emerald-700' : status === 'Lost' ? 'text-red-700' : 'text-gray-700'
                                                    }>{status}</span>
                                                    {opportunity.status === status && (
                                                      <svg className="w-3.5 h-3.5 ml-auto text-blue-500" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                                      </svg>
                                                    )}
                                                  </button>
                                                ))}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                    </div>
                                    
                                    {/* Deal Value */}
                                    <div className="text-lg font-bold text-gray-900 tracking-tight">
                                      ${(opportunity.value || 0).toLocaleString()}
                                    </div>

                                    {opportunity.next_follow_up_at ? (
                                      <div className="mt-2 mb-3">
                                        <CrmFollowUpDueBadge nextFollowUpAt={opportunity.next_follow_up_at} />
                                      </div>
                                    ) : (
                                      <div className="mb-3" />
                                    )}
                                    
                                    {/* Move Button - Mobile only */}
                                    {isMobile && (
                                      <div className="flex items-center justify-end mb-3">
                                        <div className="relative">
                                          <button 
                                              className="px-3 py-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-all duration-150 active:scale-95" 
                                            title="Move to different stage"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                                handleOpenMoveAnimation(opportunity, e);
                                              }}
                                              onTouchEnd={(e) => {
                                                e.stopPropagation();
                                                moveButtonTouchHandledRef.current = Date.now();
                                                handleOpenMoveAnimation(opportunity, e);
                                            }}
                                          >
                                            Move
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Customer Card - iOS Style - Matching BookingCustomerInfo */}
                                    {(() => {
                                      const customer = customerMap.get(opportunity.customer_id);
                                      if (!customer) {
                                        // Fallback if customer not loaded yet
                                        return (
                                          <div className="pt-3 border-t border-gray-100/80">
                                            <div className="flex items-center gap-2.5 mb-3">
                                              <div className="w-7 h-7 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center shadow-sm">
                                                <span className="text-xs font-semibold text-white">
                                                  {opportunity.customer_name ? opportunity.customer_name.charAt(0).toUpperCase() : 'U'}
                                                </span>
                                              </div>
                                              <span className="text-sm text-gray-700 font-medium">
                                                {opportunity.customer_name || 'Unknown Contact'}
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      }
                                      
                                      return (
                                        <div 
                                          className="pt-3 border-t border-gray-100/80"
                                          onClick={(e) => e.stopPropagation()}
                                          onMouseDown={(e) => e.stopPropagation()}
                                          onTouchStart={(e) => e.stopPropagation()}
                                        >
                                          <BookingCustomerInfo
                                            customer={customer}
                                            canEdit={true}
                                            onEdit={() => {
                                              setSelectedCustomer(customer);
                                              setIsCustomerDetailsModalOpen(true);
                                            }}
                                            mobile_booking={false}
                                            businessId={selectedBusiness?.id}
                                          />
                                        </div>
                                      );
                                    })()}

                                    {/* Linked Items - Minimal pills */}
                                    {(() => {
                                      const proposal = proposalMap.get(opportunity.id);
                                      const booking = bookingMap.get(opportunity.id);
                                      const project = projectMap.get(opportunity.id);
                                      const fieldQuote = fieldQuoteMap.get(opportunity.id);
                                      const salesPin = salesPinMap.get(opportunity.id);
                                      const assignedName = resourceDisplayName(opportunity.assigned_resource_id);
                                      if (!proposal && !booking && !project && !fieldQuote && !salesPin && !assignedName) return null;
                                      return (
                                        <div className="mt-2 pt-2 border-t border-gray-100/80 flex flex-wrap gap-1.5">
                                          {assignedName && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-600 bg-gray-100 pl-0.5 pr-2 py-0.5 rounded-full">
                                              <span className="h-3.5 w-3.5 rounded-full bg-blue-100 text-blue-700 text-[7px] font-bold flex items-center justify-center">
                                                {assignedName.split(/\s+/).map((p) => p.charAt(0)).join('').slice(0, 2).toUpperCase()}
                                              </span>
                                              {assignedName}
                                            </span>
                                          )}
                                          {salesPin && (
                                            <span
                                              className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full"
                                              style={{
                                                color: addressMarkerService.getMarkerColor(salesPin.marker_type as AddressMarker['marker_type']),
                                                backgroundColor: `${addressMarkerService.getMarkerColor(salesPin.marker_type as AddressMarker['marker_type'])}1A`,
                                              }}
                                            >
                                              <MapPin className="w-2.5 h-2.5" />
                                              Sales Pin · {addressMarkerService.getMarkerTypeLabel(salesPin.marker_type as AddressMarker['marker_type'])}
                                            </span>
                                          )}
                                          {proposal && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                                              <FileText className="w-2.5 h-2.5" />
                                              Proposal · <span className="capitalize">{proposal.status}</span>
                                            </span>
                                          )}
                                          {proposal && <ProposalExpiryBadge validUntil={proposal.valid_until} />}
                                          {booking && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                                              <Calendar className="w-2.5 h-2.5" />
                                              Booking · {formatCurrency(booking.total_amount || 0)}
                                            </span>
                                          )}
                                          {fieldQuote && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-800 bg-amber-50 px-2 py-0.5 rounded-full">
                                              <NotebookPen className="w-2.5 h-2.5" />
                                              {fieldQuote.is_measurement ? 'Measurement' : 'Field quote'} ·{' '}
                                              <span className="capitalize">{(fieldQuote.status || '').replace('_', ' ')}</span>
                                            </span>
                                          )}
                                          {project && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-medium text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">
                                              <Folder className="w-2.5 h-2.5" />
                                              Project
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  ))
                                ) : (
                                  <div className="text-center py-12 text-gray-400 text-sm">
                                    <div className="opacity-50">No deals in this stage</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                  
                  {opportunities.length === 0 && pipelines.length > 0 && opportunitiesViewPipeline?.stages && opportunitiesViewPipeline.stages.length > 0 && (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center max-w-md">
                        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center">
                          <Users className="h-10 w-10 text-blue-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-3">No Opportunities Match the Filters</h3>
                        <p className="text-gray-600 leading-relaxed mb-6">
                          Try adjusting your filters to find the opportunities you're looking for.
                        </p>

                      </div>
              </div>
            )}
                </div>
              </div>
          </div>
        )}
        
        {activeTab === 'pipelines' && (
            <div className="space-y-6">
            
            {error && (
                <div className="bg-red-50/80 backdrop-blur-sm border border-red-200 rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-xl">
                      <X className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-red-800">Error</h3>
                      <p className="text-sm text-red-600 mt-1">{error}</p>
                    </div>
                  </div>
              </div>
            )}
            
            {isLoadingPipelines ? (
                <CRMSkeleton type="pipelines" isMobile={isMobile} />
            ) : pipelines.length === 0 ? (
                <div className="bg-white/70 backdrop-blur-xl border border-white/20 rounded-2xl p-12 shadow-xl text-center">
                  <div className="max-w-md mx-auto">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl flex items-center justify-center">
                      <TrendingUp className="h-10 w-10 text-green-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">No Pipelines Found</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      Create a new pipeline to get started with managing your sales opportunities.
                    </p>
                    <button 
                      onClick={() => handleOpenModal()}
                      className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-150 shadow-lg hover:shadow-xl font-semibold"
                    >
                      Create Your First Pipeline
                    </button>
                  </div>
              </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-hidden">
                    <table className="min-w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Pipeline Name
                          </th>
                          <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pipelines.map((pipeline) => (
                          <tr
                            key={pipeline.id}
                            draggable
                            onDragStart={(e) => handlePipelineDragStart(e, pipeline.id)}
                            onDragOver={(e) => handlePipelineDragOver(e, pipeline.id)}
                            onDragEnd={handlePipelineDragEnd}
                            className={`hover:bg-gray-50 transition-colors duration-150 cursor-grab active:cursor-grabbing ${draggingPipelineId === pipeline.id ? 'opacity-50 bg-blue-50' : ''}`}
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="text-gray-300 hover:text-gray-500 transition-colors flex-shrink-0">
                                  <GripVertical className="h-5 w-5" />
                                </div>
                                <div className="p-2 bg-blue-100 rounded-lg">
                                  <TrendingUp className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                  <div className="font-medium text-gray-900">{pipeline.pipeline_name}</div>
                                  {pipeline.stages && (
                                    <div className="text-sm text-gray-500 mt-1">
                                      {pipeline.stages.length} stages
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex justify-end gap-2">
                                <button 
                                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenModal(true, pipeline);
                                  }}
                                  title="Edit pipeline"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button 
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePipeline(pipeline.id);
                                  }}
                                  title="Delete pipeline"
                                >
                                  <Trash className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
            )}
          </div>
        )}
        </div>
      </div>
      )}

      {/* Mobile Move Animation - iOS Style */}
      {isMobile && moveAnimationOpportunity && moveAnimationPosition && opportunitiesViewPipeline?.stages && createPortal(
        <>
          {/* Backdrop Overlay */}
          <div
            className="fixed inset-0 z-[99997]"
            style={{
              pointerEvents: exitDirection ? 'none' : 'auto',
              background: exitDirection ? 'rgba(0, 0, 0, 0)' : 'rgba(0, 0, 0, 0.2)',
              backdropFilter: exitDirection ? 'blur(0px)' : 'blur(10px)',
              WebkitBackdropFilter: exitDirection ? 'blur(0px)' : 'blur(10px)',
              animation: exitDirection ? 'none' : 'iosBackdropFadeIn 0.1s cubic-bezier(0.4, 0.0, 0.2, 1) forwards',
              cursor: 'pointer',
              opacity: exitDirection ? 0 : 1,
              transition: exitDirection 
                ? 'opacity 0.4s cubic-bezier(0.4, 0.0, 0.2, 1), backdrop-filter 0.4s cubic-bezier(0.4, 0.0, 0.2, 1), background 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)'
                : 'none',
              willChange: exitDirection ? 'opacity, backdrop-filter, background' : 'auto',
            }}
            onClick={(e) => {
              const target = e.target as Element;
              if (target === e.currentTarget || (!target.closest('[data-move-card]') && !target.closest('[data-move-stage-button]'))) {
                setMoveAnimationOpportunity(null);
                setMoveAnimationPosition(null);
                setExitDirection(null);
                setButtonsOffset(0);
              }
            }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          />

          {/* Animated Opportunity Card - Centered at top of screen */}
          <div
            data-move-card
            className="fixed z-[1000000] pointer-events-none"
            style={{
              left: '50%',
              top: '80px',
              width: `${moveAnimationPosition.width}px`,
              height: `${moveAnimationPosition.height}px`,
              transform: exitDirection === 'left' 
                ? 'translateX(-50%) translateX(calc(-100vw - 100px))'
                : exitDirection === 'right'
                ? 'translateX(-50%) translateX(calc(100vw + 100px))'
                : 'translateX(-50%)',
              transformOrigin: 'center top',
              transition: exitDirection 
                ? 'transform 0.4s cubic-bezier(0.4, 0.0, 0.2, 1)'
                : 'all 0.12s cubic-bezier(0.4, 0.0, 0.2, 1)',
              willChange: 'transform',
              opacity: 1,
              visibility: 'visible',
            }}
          >
            <div className="w-full h-full bg-white border border-gray-200/60 rounded-xl p-4 shadow-2xl">
              {/* Unread indicator */}
              {moveAnimationOpportunity.is_unread && (
                <div className="absolute top-3 right-3 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white shadow-sm"></div>
              )}
              
              {/* Deal Title */}
              <div className="flex items-start justify-between mb-3">
                <h4 className="font-semibold text-gray-900 text-sm leading-tight pr-4 flex-1">{moveAnimationOpportunity.title}</h4>
                  </div>
              
              {/* Deal Value */}
              <div className="text-lg font-bold text-gray-900 mb-4 tracking-tight">
                ${(moveAnimationOpportunity.value || 0).toLocaleString()}
                  </div>
              
              {/* Customer Name and Icon */}
              <div className="pt-3 border-t border-gray-100/80">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="w-7 h-7 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center shadow-sm">
                    <span className="text-xs font-semibold text-white">
                      {moveAnimationOpportunity.customer_name ? moveAnimationOpportunity.customer_name.charAt(0).toUpperCase() : 'U'}
                    </span>
                </div>
                  <span className="text-sm text-gray-700 font-medium">
                    {moveAnimationOpportunity.customer_name || 'Unknown Contact'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Pipeline Stage Buttons - Centered below the card */}
          {(() => {
            // Card is at top: 80px, so buttons should be below it
            const cardHeight = moveAnimationPosition.height;
            const buttonsTop = 80 + cardHeight + 16; // 80px top + card height + 16px gap
            
            return (
              <div
                ref={buttonsContainerRef}
                className="fixed z-[999999] pointer-events-none"
                style={{
                  left: '50%',
                  top: `${buttonsTop}px`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  pointerEvents: exitDirection ? 'none' : 'auto',
                  alignItems: 'center',
                  transform: `translateX(-50%)`,
                  willChange: 'transform, opacity',
                  opacity: exitDirection ? 0 : 1,
                  transition: exitDirection 
                    ? 'opacity 0.15s cubic-bezier(0.4, 0.0, 0.2, 1)'
                    : 'opacity 0.2s ease-out',
                }}
              >
                {opportunitiesViewPipeline.stages?.map((stage, index) => (
              <button 
                    key={stage.id}
                    data-move-stage-button
                    className={`bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/90 rounded-lg px-4 py-2.5 shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center active:scale-95 border border-gray-600/50 ${
                      stage.id === moveAnimationOpportunity.stage_id ? 'ring-2 ring-blue-400' : ''
                    }`}
                    style={{
                      width: '150px',
                      height: '42px',
                      animation: `iosAppIconEnter 0.15s cubic-bezier(0.4, 0.0, 0.2, 1) ${0.05 + (index * 0.02)}s both`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (stage.id !== moveAnimationOpportunity.stage_id) {
                        handleQuickMove(moveAnimationOpportunity.id, stage.id);
                      }
                    }}
                  >
                    <span className={`text-sm font-semibold ${
                      stage.id === moveAnimationOpportunity.stage_id ? 'text-blue-400' : 'text-white'
                    }`}>
                      {stage.name}
                    </span>
                  </button>
                ))}
              </div>
            );
          })()}
        </>,
        document.body
      )}

      {/* Create/Edit Pipeline Modal - Right Side Mounted */}
      {isModalOpen && createPortal(
        <div 
          className={`fixed inset-0 bg-black transition-opacity duration-300 ${
            isModalOpen ? 'bg-opacity-50' : 'bg-opacity-0'
          } z-[9999]`}
                onClick={handleCloseModal} 
          style={{
            transform: 'translateZ(0)',
            WebkitTransform: 'translateZ(0)',
            isolation: 'isolate',
            touchAction: 'manipulation',
            WebkitTouchCallout: 'none',
            WebkitUserSelect: 'none',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
            paddingLeft: 'env(safe-area-inset-left)',
            paddingRight: 'env(safe-area-inset-right)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
          }}
        >
          <div 
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-white transform transition-transform duration-300 ease-out shadow-2xl ${
              isModalOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => {
              e.stopPropagation();
            }}
            style={{
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
              height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="bg-gray-900 border-b border-gray-800">
                <div className="flex items-center justify-between px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleCloseModal}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <X className="h-6 w-6" />
              </button>
                    <h2 className="text-xl font-semibold text-white">
                      {isEditMode ? 'Edit Pipeline' : 'Create Pipeline'}
                    </h2>
                  </div>
              </div>
            </div>

              {/* Content Area */}
              <div className="flex-1 overflow-y-auto bg-gray-50">
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div>
                <label htmlFor="pipeline-name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Pipeline Name
                </label>
                <input
                  type="text"
                  id="pipeline-name"
                  placeholder="Enter pipeline name"
                  value={pipelineName}
                  onChange={(e) => setPipelineName(e.target.value)}
                      className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-300 transition-all duration-200"
                  required
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700">
                      Pipeline Stages
                  </label>
                    <p className="text-xs text-gray-500 mt-1">Drag to reorder stages</p>
                  </div>
                  <div className="text-xs font-medium text-gray-500">
                    Actions
                  </div>
                </div>

                <div className="space-y-3">
                {stages
                  .sort((a, b) => (a.position || 0) - (b.position || 0))
                  .map((stage, index) => (
                  <div 
                    key={stage.id} 
                            className="group bg-white border border-gray-200 rounded-lg p-3 transition-all duration-200 hover:bg-gray-50"
                    draggable={true}
                    onDragStart={(e) => handleDragStart(e, stage.id)}
                    onDragOver={(e) => handleDragOver(e, stage.id)}
                    onDragEnd={handleDragEnd}
                  >
                      <div className="flex items-center gap-3">
                        <div className="cursor-grab text-gray-400 hover:text-gray-600 p-1" title="Drag to reorder">
                          <GripVertical className="h-4 w-4" />
                    </div>
                    <input
                      type="text"
                      placeholder="Stage Name"
                      value={stage.name}
                      onChange={(e) => updateStageName(stage.id, e.target.value)}
                                className="flex-1 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/30 focus:border-green-300"
                        />
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <button 
                            type="button" 
                            className="p-2 text-green-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-150"
                            title="Analytics"
                          >
                            <PieChart className="h-4 w-4" />
                      </button>
                          <button 
                            type="button" 
                            className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                            title="Filter"
                          >
                            <Filter className="h-4 w-4" />
                      </button>
                      <button 
                        type="button" 
                        onClick={(e) => {
                          e.stopPropagation();
                          removeStage(stage.id);
                        }}
                            className="p-2 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                        disabled={stages.length <= 1}
                            title="Delete stage"
                      >
                            <Trash className="h-4 w-4" />
                      </button>
                        </div>
                    </div>
                  </div>
                ))}
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    addStage();
                  }}
                  className="mt-4 flex items-center gap-2 text-green-600 hover:text-green-700 text-sm font-medium"
                >
                  <Plus className="h-4 w-4" /> Add Stage
                </button>
              </div>
            </form>
              </div>

              {/* Footer */}
              <div className="bg-white border-t border-gray-200 px-6 py-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all duration-150 w-full sm:w-auto"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="pipeline-form"
                  onClick={handleSubmit}
                    className={`px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-150 shadow-lg hover:shadow-xl w-full sm:w-auto ${
                    isLoading ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2 justify-center">
                      <Loader className="h-4 w-4 animate-spin" />
                      {isEditMode ? 'Updating...' : 'Creating...'}
                    </div>
                  ) : (
                    isEditMode ? 'Update Pipeline' : 'Create Pipeline'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        </div>,
        document.body
      )}

      {/* New Lead chooser (AI Create from Business Card setting) */}
      <LeadCreateChoiceModal
        isOpen={isLeadChoiceOpen}
        onClose={() => setIsLeadChoiceOpen(false)}
        onCreate={() => {
          setIsLeadChoiceOpen(false);
          handleOpenOpportunityModal(opportunitiesViewPipeline || undefined);
        }}
        onCreateFromPhoto={() => {
          setIsLeadChoiceOpen(false);
          setIsCardScanOpen(true);
        }}
      />

      {/* Scan Business Card → Lead */}
      {selectedBusiness?.id && (
        <BusinessCardScanModal
          isOpen={isCardScanOpen}
          businessId={selectedBusiness.id}
          onClose={() => setIsCardScanOpen(false)}
          onCreated={() => {
            void fetchOpportunities();
          }}
        />
      )}

      {/* Create Opportunity Modal */}
      {isOpportunityModalOpen && createPortal(
        <div 
          className="fixed inset-0 transition-opacity duration-200"
          onClick={handleModalClose}
          style={{
            zIndex: opportunityModalZ,
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <div 
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] min-w-0 bg-gray-50 transform transition-transform duration-300 ease-out shadow-2xl ${
              isOpportunityModalOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
            style={{
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
              height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            }}
          >
            <div className="h-full flex flex-col min-w-0">
              {/* Header */}
              <div className="relative z-20 shrink-0 overflow-visible bg-gray-50 border-b border-gray-200 min-w-0">
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                  <button
                    onClick={handleModalClose}
                    className="h-11 w-11 flex items-center justify-center rounded-full hover:bg-gray-200 active:bg-gray-300 active:scale-95 transition-all duration-150 touch-manipulation flex-shrink-0"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                  <h2 className="text-lg font-bold text-gray-900 truncate" style={{ letterSpacing: '-0.02em' }}>
                    {selectedOpportunityForInfo ? 'Opportunity' : 'New Opportunity'}
                  </h2>
                  {selectedOpportunityForInfo ? (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <CrmCreateActionDropdown options={opportunityCreateMenuOptions} />
                      <button
                        type="button"
                        onClick={() => {
                          setLinkModalType(null);
                          setProposalSearchQuery('');
                          setBookingSearchQuery('');
                          setProjectSearchQuery('');
                          setFieldQuoteSearchQuery('');
                          setIsLinkModalOpen(true);
                        }}
                        className={`h-11 w-11 flex items-center justify-center rounded-xl active:scale-95 transition-all duration-150 touch-manipulation ${
                          linkedProposal || linkedBooking || linkedProject || linkedFieldQuote
                            ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' 
                            : 'text-gray-500 bg-white border border-gray-200 hover:bg-gray-50'
                        }`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        title="Link"
                      >
                        <Link className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-11 flex-shrink-0" />
                  )}
                </div>

                {/* Assigned resource — lowkey header chip, tap to change */}
                <div className="flex justify-center pb-2.5 -mt-0.5">
                  {(() => {
                    const assignedName = resourceDisplayName(assignedResourceId);
                    const initials = assignedName
                      ? assignedName
                          .split(/\s+/)
                          .map((p) => p.charAt(0))
                          .join('')
                          .slice(0, 2)
                          .toUpperCase()
                      : null;
                    return (
                      <div className="relative inline-flex">
                        <div
                          className={`inline-flex items-center gap-1.5 pl-1 pr-2 py-[3px] rounded-full border transition-colors ${
                            assignedName
                              ? 'bg-white border-gray-200 shadow-sm'
                              : 'bg-gray-100/80 border-transparent'
                          }`}
                        >
                          <span
                            className={`h-[18px] w-[18px] rounded-full flex items-center justify-center text-[8px] font-bold ${
                              assignedName ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-400'
                            }`}
                          >
                            {initials || <Users className="h-2.5 w-2.5" />}
                          </span>
                          <span className={`text-[11px] font-medium ${assignedName ? 'text-gray-700' : 'text-gray-400'}`}>
                            {assignedName || 'Unassigned'}
                          </span>
                          <ChevronDown className="h-3 w-3 text-gray-300" />
                        </div>
                        <select
                          value={assignedResourceId ?? ''}
                          onChange={(e) => {
                            assignedTouchedRef.current = true;
                            setAssignedResourceId(e.target.value || null);
                          }}
                          className="absolute inset-0 w-full opacity-0 cursor-pointer"
                          aria-label="Assigned to"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <option value="">Unassigned</option>
                          {crmResources
                            .filter((r) => r.status === 'active' || r.id === assignedResourceId)
                            .map((r) => (
                              <option key={r.id} value={r.id}>
                                {`${r.first_name || ''} ${r.last_name || ''}`.trim() || r.email || 'Resource'}
                              </option>
                            ))}
                        </select>
                      </div>
                    );
                  })()}
                </div>

                {selectedOpportunityForInfo?.created_at ? (
                  <div className="overflow-visible border-t border-gray-200/80 px-4 py-2.5 min-w-0">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="min-w-0 text-center">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                          1st Contact
                        </p>
                        <p className="mt-0.5 text-[12px] font-semibold text-gray-900 truncate tabular-nums">
                          {isLoadingFirstActivity ? '...' : timeToFirstResponse || '\u2014'}
                        </p>
                      </div>
                      <div className="min-w-0 text-center">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                          Created
                        </p>
                        <p className="mt-0.5 text-[12px] font-semibold text-gray-900 truncate tabular-nums">
                          {new Date(selectedOpportunityForInfo.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </p>
                        {(() => {
                          const creatorName = resourceDisplayName(selectedOpportunityForInfo.created_by_resource_id)
                            || (selectedOpportunityForInfo.created_by_user_id ? 'Admin' : null);
                          return creatorName ? (
                            <p className="text-[10px] text-gray-400 truncate">by {creatorName}</p>
                          ) : null;
                        })()}
                      </div>
                      <div className="min-w-0 text-center">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                          Activity
                        </p>
                        <div className="mt-0.5 flex min-h-[18px] items-center justify-center">
                          {isLoadingLastActivity ? (
                            <span className="text-[12px] text-gray-400">...</span>
                          ) : lastActivity ? (
                            <button
                              type="button"
                              onClick={handleOpenActivityModal}
                              title="View all activity"
                              className="inline-flex max-w-full items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-0.5 text-[12px] font-semibold text-gray-900 shadow-sm hover:border-blue-300 hover:bg-blue-50 active:scale-95 transition-all touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              {(lastActivity.activity_type === 'sms_sent' || lastActivity.activity_type === 'sms_received') && (
                                <MessageSquare className="h-3.5 w-3.5 shrink-0 text-green-600" />
                              )}
                              {(lastActivity.activity_type === 'email_sent' || lastActivity.activity_type === 'email_received') && (
                                <Mail className="h-3.5 w-3.5 shrink-0 text-blue-600" />
                              )}
                              {(lastActivity.activity_type === 'sms_received' || lastActivity.activity_type === 'email_received') && (
                                <ArrowDownLeft className="h-3 w-3 shrink-0 -ml-0.5 text-green-600" aria-label="Inbound" />
                              )}
                              {(lastActivity.activity_type === 'sms_sent' || lastActivity.activity_type === 'email_sent') && (
                                <ArrowUpRight className="h-3 w-3 shrink-0 -ml-0.5 text-blue-500" aria-label="Outbound" />
                              )}
                              {lastActivity.activity_type === 'proposal_sent' && (
                                <FileText className="h-3.5 w-3.5 shrink-0 text-purple-600" />
                              )}
                              {lastActivity.activity_type === 'phone_call' && (
                                <Phone className="h-3.5 w-3.5 shrink-0 text-orange-600" />
                              )}
                              {lastActivity.activity_type === 'stage_moved' && (
                                <ArrowRightLeft className="h-3.5 w-3.5 shrink-0 text-gray-500" />
                              )}
                              {lastActivity.activity_type === 'status_changed' && (
                                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-indigo-600" />
                              )}
                              {(lastActivity.activity_type === 'follow_up_scheduled' ||
                                lastActivity.activity_type === 'follow_up_cleared') && (
                                <Clock className="h-3.5 w-3.5 shrink-0 text-blue-600" />
                              )}
                              {lastActivity.activity_type === 'follow_up_completed' && (
                                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                              )}
                              <span className="truncate">
                                {new Date(lastActivity.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </span>
                              <ChevronRight className="h-3 w-3 shrink-0 -mr-0.5 text-gray-400" />
                            </button>
                          ) : (
                            <span className="text-[12px] font-semibold text-gray-400">{'\u2014'}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <OpportunityNextFollowUpHeader
                      nextFollowUpAt={selectedOpportunityForInfo.next_follow_up_at ?? null}
                      onSave={handleOpportunityFollowUpSave}
                      onComplete={handleOpportunityFollowUpComplete}
                    />
                  </div>
                ) : null}
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-y-auto bg-gray-50">
                <form 
                  id="opportunity-form"
                  className="p-6 space-y-6" 
                  style={{ WebkitOverflowScrolling: 'touch' }}
                  onSubmit={(e) => {
                    if (selectedOpportunityForInfo) {
                      // Details mode auto-saves; prevent Enter from submitting/closing.
                      e.preventDefault();
                    } else {
                      handleCreateOpportunity(e);
                    }
                  }}
                >
                  {/* Hero: Title, Status, Value — centered, quick-glance & quick-edit */}
                  <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                    <div className="px-4 pt-4 pb-3">
                      <input
                        type="text"
                        placeholder="Opportunity title"
                        value={opportunityTitle}
                        onChange={(e) => setOpportunityTitle(e.target.value)}
                        className="w-full text-center bg-transparent border-0 p-0 font-bold text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-0"
                        style={{ fontSize: '22px', letterSpacing: '-0.02em' }}
                      />
                      {typeof selectedOpportunityForInfo?.lead_score === 'number' ? (
                        <div className="mt-3 flex flex-col items-center gap-1">
                          <LeadScoreThermometer score={selectedOpportunityForInfo.lead_score} />
                          {selectedOpportunityForInfo.lead_score_reason ? (
                            <p className="text-[12px] text-gray-500 text-center leading-snug">{selectedOpportunityForInfo.lead_score_reason}</p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    {/* Status — own line, full width, centered text, tap to change */}
                    <div className="px-4 pb-2">
                      {(() => {
                        const pillCls = opportunityStatus === 'Won'
                          ? 'bg-green-100 text-green-700'
                          : opportunityStatus === 'Lost'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-gray-100 text-gray-700';
                        return (
                          <div className="relative w-full">
                            <select
                              value={opportunityStatus}
                              onChange={(e) => setOpportunityStatus(e.target.value)}
                              className={`appearance-none w-full text-center font-semibold rounded-full px-10 py-2.5 cursor-pointer focus:outline-none transition-colors ${pillCls}`}
                              style={{ fontSize: '16px', WebkitTapHighlightColor: 'transparent', textAlignLast: 'center' }}
                            >
                              <option value="Open">Open</option>
                              <option value="Won">Won</option>
                              <option value="Lost">Lost</option>
                            </select>
                            <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 opacity-60" />
                          </div>
                        );
                      })()}
                    </div>
                    {/* Stage — own line, full width, centered text, tap to change */}
                    <div className="px-4 pb-3">
                      <div className="relative w-full">
                        <select
                          value={selectedStage?.id || ''}
                          onChange={(e) => {
                            const stage = selectedPipeline?.stages?.find(s => s.id.toString() === e.target.value);
                            setSelectedStage(stage || null);
                          }}
                          disabled={!selectedPipeline || !selectedPipeline.stages || selectedPipeline.stages.length === 0}
                          className="appearance-none w-full text-center font-semibold rounded-full px-10 py-2.5 cursor-pointer focus:outline-none bg-blue-50 text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          style={{ fontSize: '16px', WebkitTapHighlightColor: 'transparent', textAlignLast: 'center' }}
                        >
                          <option value="">{selectedOpportunityForInfo && !selectedStage ? 'Unassigned' : 'Stage'}</option>
                          {selectedPipeline?.stages?.map((stage) => (
                            <option key={stage.id} value={stage.id}>{stage.name}</option>
                          ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 opacity-60" />
                      </div>
                    </div>
                    {/* Value — centered */}
                    <div className="px-4 py-3.5 border-t border-gray-100">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 text-center mb-0.5">Value</p>
                      <div className="flex items-baseline justify-center">
                        <span className="text-gray-900 text-lg font-bold">$</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={opportunityValue}
                          onChange={(e) => setOpportunityValue(Number(e.target.value) || 0)}
                          className="bg-transparent border-0 p-0 text-left text-lg font-bold text-gray-900 placeholder-gray-300 focus:outline-none focus:ring-0 tabular-nums"
                          style={{
                            fontSize: '18px',
                            width: `${Math.max(String(opportunityValue || '').length, 4) + 1}ch`,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Linked door-to-door sales pin (only renders when one exists) */}
                  {selectedOpportunityForInfo?.id && (
                    <OpportunitySalesPinCard opportunityId={selectedOpportunityForInfo.id} />
                  )}

                  {/* Linked Items - Minimal iOS Style */}
                  {selectedOpportunityForInfo && (
                    <>
                      {(linkedProposal || linkedBooking || linkedProject || linkedFieldQuote || isLoadingLinkedProposal || isLoadingLinkedBooking || isLoadingLinkedProject || isLoadingLinkedFieldQuote) && (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden divide-y divide-gray-100">
                          {/* Proposal Row */}
                          {isLoadingLinkedProposal ? (
                            <div className="px-4 min-h-[48px] flex items-center">
                              <span className="text-xs text-gray-400">Loading proposal...</span>
                            </div>
                          ) : linkedProposal ? (() => {
                            const getProposalStatus = () => {
                              if (linkedProposal.status === 'approved') return linkedProposal.status;
                              if (linkedProposal.valid_until && new Date(linkedProposal.valid_until) < new Date()) return 'expired';
                              return linkedProposal.status;
                            };
                            const status = getProposalStatus();
                            const statusStyle = (() => {
                              switch (status) {
                                case 'approved': return 'bg-green-50 text-green-700';
                                case 'sent': return 'bg-blue-50 text-blue-700';
                                case 'viewed': return 'bg-purple-50 text-purple-700';
                                case 'rejected': case 'declined': return 'bg-red-50 text-red-700';
                                default: return 'bg-gray-100 text-gray-600';
                              }
                            })();
                            return (
                              <button
                                type="button"
                                onClick={() => setIsProposalDetailsModalOpen(true)}
                                className="w-full px-4 min-h-[52px] flex items-center gap-3 active:bg-gray-50 transition-colors touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-4 h-4 text-blue-500" />
                                </div>
                                <div className="flex-1 min-w-0 text-left">
                                  <div className="text-sm font-semibold text-gray-900 leading-tight">Proposal</div>
                                  <span className={`inline-block text-[10px] font-medium capitalize mt-0.5 px-1.5 py-px rounded-full ${statusStyle}`}>
                                    {status}
                                  </span>
                                </div>
                                <div className="text-right flex-shrink-0">
                                  {isProposalMaxPotential(linkedProposal) && (
                                    <div className="text-[9px] text-gray-400 leading-tight">Max Potential</div>
                                  )}
                                  <span className="text-sm font-bold text-gray-900 tabular-nums">
                                    ${getProposalDisplayTotal(linkedProposal).toLocaleString()}
                                  </span>
                                </div>
                                <ChevronDown className="w-4 h-4 text-gray-300 -rotate-90 flex-shrink-0" />
                              </button>
                            );
                          })() : null}

                          {/* Booking Row */}
                          {isLoadingLinkedBooking ? (
                            <div className="px-4 min-h-[48px] flex items-center">
                              <span className="text-xs text-gray-400">Loading booking...</span>
                            </div>
                          ) : linkedBooking ? (() => {
                            const status = linkedBooking.status || 'draft';
                            const statusStyle = (() => {
                              switch (status) {
                                case 'scheduled': return 'bg-blue-50 text-blue-700';
                                case 'in_progress': return 'bg-amber-50 text-amber-700';
                                case 'completed': return 'bg-green-50 text-green-700';
                                case 'cancelled': return 'bg-red-50 text-red-700';
                                default: return 'bg-gray-100 text-gray-600';
                              }
                            })();
                            return (
                              <button
                                type="button"
                                onClick={() => navigate('/calendarV2', { state: { selectedBookingId: linkedBooking.id } })}
                                className="w-full px-4 min-h-[52px] flex items-center gap-3 active:bg-gray-50 transition-colors touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                                  <Calendar className="w-4 h-4 text-green-500" />
                                </div>
                                <div className="flex-1 min-w-0 text-left">
                                  <div className="text-sm font-semibold text-gray-900 leading-tight">Booking</div>
                                  <span className={`inline-block text-[10px] font-medium capitalize mt-0.5 px-1.5 py-px rounded-full ${statusStyle}`}>
                                    {status.replace('_', ' ')}
                                  </span>
                                </div>
                                <span className="text-sm font-bold text-gray-900 tabular-nums flex-shrink-0">
                                  ${(linkedBooking.total_amount || 0).toLocaleString()}
                                </span>
                                <ChevronDown className="w-4 h-4 text-gray-300 -rotate-90 flex-shrink-0" />
                              </button>
                            );
                          })() : null}

                          {/* Project Row */}
                          {isLoadingLinkedProject ? (
                            <div className="px-4 min-h-[48px] flex items-center">
                              <span className="text-xs text-gray-400">Loading project...</span>
                            </div>
                          ) : linkedProject ? (() => {
                            const status = linkedProject.status || 'draft';
                            const statusStyle = (() => {
                              switch (status?.toLowerCase()) {
                                case 'completed': return 'bg-green-50 text-green-700';
                                case 'in progress': return 'bg-blue-50 text-blue-700';
                                case 'scheduled': return 'bg-amber-50 text-amber-700';
                                default: return 'bg-gray-100 text-gray-600';
                              }
                            })();
                            return (
                              <button
                                type="button"
                                onClick={() => navigate('/projects', { state: { openProjectId: linkedProject.id } })}
                                className="w-full px-4 min-h-[52px] flex items-center gap-3 active:bg-gray-50 transition-colors touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                                  <Folder className="w-4 h-4 text-purple-500" />
                                </div>
                                <div className="flex-1 min-w-0 text-left">
                                  <div className="text-sm font-semibold text-gray-900 leading-tight truncate">{linkedProject.title || 'Project'}</div>
                                  <span className={`inline-block text-[10px] font-medium capitalize mt-0.5 px-1.5 py-px rounded-full ${statusStyle}`}>
                                    {status}
                                  </span>
                                </div>
                                <ChevronDown className="w-4 h-4 text-gray-300 -rotate-90 flex-shrink-0" />
                              </button>
                            );
                          })() : null}

                          {isLoadingLinkedFieldQuote ? (
                            <div className="px-4 min-h-[48px] flex items-center">
                              <span className="text-xs text-gray-400">Loading field quote...</span>
                            </div>
                          ) : linkedFieldQuote ? (() => {
                            const status = linkedFieldQuote.status || 'scheduled';
                            const statusStyle = (() => {
                              switch (status) {
                                case 'scheduled': return 'bg-blue-50 text-blue-700';
                                case 'completed': return 'bg-green-50 text-green-700';
                                case 'cancelled': return 'bg-red-50 text-red-700';
                                default: return 'bg-gray-100 text-gray-600';
                              }
                            })();
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  setCrmFieldQuoteDetailId(linkedFieldQuote.id);
                                  setIsOpportunityModalOpen(false);
                                }}
                                className="w-full px-4 min-h-[52px] flex items-center gap-3 active:bg-gray-50 transition-colors touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                                  <NotebookPen className="w-4 h-4 text-amber-700" />
                                </div>
                                <div className="flex-1 min-w-0 text-left">
                                  <div className="text-sm font-semibold text-gray-900 leading-tight">
                                    {linkedFieldQuote.is_measurement ? 'Measurement' : 'Field quote'}
                                  </div>
                                  <span className={`inline-block text-[10px] font-medium capitalize mt-0.5 px-1.5 py-px rounded-full ${statusStyle}`}>
                                    {status.replace('_', ' ')}
                                  </span>
                                </div>
                                <ChevronDown className="w-4 h-4 text-gray-300 -rotate-90 flex-shrink-0" />
                              </button>
                            );
                          })() : null}
                        </div>
                      )}
                    </>
                  )}


                  {SHOW_OPPORTUNITY_LEAD_AGENT &&
                    selectedOpportunityForInfo &&
                    selectedBusiness?.id && (
                    <OpportunityLeadAgentSection
                      opportunityId={selectedOpportunityForInfo.id}
                      businessId={selectedBusiness.id}
                      assignedAgentId={selectedOpportunityForInfo.lead_agent_id ?? null}
                      leadAgents={leadAgentsForPicker}
                      customerPhone={selectedCustomer?.phone}
                      nextFollowUpAt={selectedOpportunityForInfo.lead_agent_next_follow_up_at ?? null}
                      followUpReason={selectedOpportunityForInfo.lead_agent_follow_up_reason ?? null}
                      onAssignAgent={handleLeadAgentAssign}
                      onAfterAgentTurn={refreshLeadAgentScheduleForModal}
                    />
                  )}

                  {liveIntakeFormsEnabled && selectedOpportunityForInfo && selectedBusiness?.id && (
                    <OpportunityIntakeFormsSection
                      opportunityId={selectedOpportunityForInfo.id}
                      businessId={selectedBusiness.id}
                      refreshNonce={intakeInstancesNonce}
                      customerPhone={selectedCustomer?.phone}
                      customerEmail={selectedCustomer?.email}
                      businessName={selectedBusiness.name || 'Our team'}
                      customerFirstName={selectedCustomer?.first_name || ''}
                    />
                  )}

                  {/* Customer Selection */}
                  <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] overflow-hidden">
                    {selectedCustomer ? (
                      <>
                        <BookingCustomerInfo
                          customer={selectedCustomer}
                          canEdit={true}
                          onEdit={() => setIsCustomerDetailsModalOpen(true)}
                          mobile_booking={false}
                          businessId={selectedBusiness?.id}
                        />
                        <div className="flex border-t border-gray-100">
                          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsCustomerModalOpen(true); }}
                            className="flex-1 min-h-[44px] text-sm font-semibold text-blue-600 hover:bg-blue-50 active:scale-95 transition-all touch-manipulation border-r border-gray-100"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >Change</button>
                          <button type="button" onClick={handleCustomerRemove}
                            className="flex-1 min-h-[44px] text-sm font-semibold text-red-500 hover:bg-red-50 active:scale-95 transition-all touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >Remove</button>
                        </div>
                      </>
                    ) : (
                      <div className="p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Customer *</label>
                        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsCustomerModalOpen(true); }}
                          className="w-full flex items-center justify-center px-4 min-h-[48px] text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl active:scale-[0.98] transition-all duration-150 touch-manipulation shadow-sm"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Plus className="h-5 w-5 mr-2" />
                          Select Customer
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Lead Info / Notes */}
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Lead Info</label>
                    <textarea
                      placeholder="Additional lead information"
                      value={leadInfo}
                      onChange={(e) => {
                        setLeadInfo(e.target.value);
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                      ref={(el) => {
                        if (el) {
                          el.style.height = 'auto';
                          el.style.height = el.scrollHeight + 'px';
                        }
                      }}
                      rows={2}
                      className="w-full px-4 py-3.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none transition-all duration-150 overflow-hidden"
                      style={{ fontSize: '16px', minHeight: '80px' }}
                    />
                  </div>

                  {/* Additional Details Card */}
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 space-y-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Opportunity Source</label>
                      <input
                        type="text"
                        placeholder="Enter opportunity source"
                        value={opportunitySource}
                        onChange={(e) => setOpportunitySource(e.target.value)}
                        className="w-full px-4 min-h-[48px] border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-150"
                        style={{ fontSize: '16px' }}
                      />
                    </div>
                  </div>

                  {/* Pipeline */}
                  <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Pipeline</label>
                    <select
                      value={selectedPipeline?.id || ''}
                      onChange={(e) => {
                        const pipeline = pipelines.find(p => p.id.toString() === e.target.value);
                        handlePipelineSelect(pipeline || null);
                      }}
                      className="w-full px-3 min-h-[48px] text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white transition-all duration-150"
                      style={{ fontSize: '16px' }}
                    >
                      <option value="">Select pipeline</option>
                      {pipelines.map(pipeline => (
                        <option key={pipeline.id} value={pipeline.id}>{pipeline.pipeline_name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Custom Fields (managed in CRM App Settings → Custom Fields) */}
                  {customFieldsEnabled && customFieldDefinitions.length > 0 && (
                    <div className="bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                      <label className="block text-sm font-medium text-gray-700 mb-3">Custom Fields</label>
                      <div className="space-y-3">
                        {customFields.map(field => {
                          const def = customFieldDefinitions.find(d => d.id === field.id);
                          const required = def?.required === true;
                          return (
                          <div key={field.id}>
                            <label className="block text-xs font-medium text-gray-600 mb-1.5">
                              {field.name}
                              {required && <span className="text-red-500 ml-0.5">*</span>}
                            </label>
                            {field.type === 'text' && (
                              <input type="text" value={field.value} onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-4 min-h-[48px] text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-150"
                                placeholder={`Enter ${field.name.toLowerCase()}`} style={{ fontSize: '16px' }} />
                            )}
                            {field.type === 'number' && (() => {
                              const min = def?.options?.min ?? 0;
                              const max = def?.options?.max ?? 100;
                              const count = Math.min(Math.max(max - min + 1, 0), 201);
                              return (
                                <>
                                  <input type="number" value={field.value} min={min} max={max}
                                    list={`custom-field-numbers-${field.id}`}
                                    onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                    className="w-full px-4 min-h-[48px] text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-150"
                                    placeholder={`${min}–${max} or type a number`} style={{ fontSize: '16px' }} />
                                  <datalist id={`custom-field-numbers-${field.id}`}>
                                    {Array.from({ length: count }, (_, i) => (
                                      <option key={min + i} value={min + i} />
                                    ))}
                                  </datalist>
                                </>
                              );
                            })()}
                            {field.type === 'dropdown' && (() => {
                              const choices = def?.options?.choices || [];
                              const allowCustom = def?.options?.allowCustom !== false;
                              const inCustomMode = customModeFieldIds.has(field.id) ||
                                (!!field.value && !choices.includes(field.value));
                              return (
                                <>
                                  <select
                                    value={inCustomMode ? '__custom__' : field.value}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === '__custom__') {
                                        setCustomModeFieldIds(prev => new Set(prev).add(field.id));
                                        handleUpdateCustomField(field.id, '');
                                      } else {
                                        setCustomModeFieldIds(prev => {
                                          const next = new Set(prev);
                                          next.delete(field.id);
                                          return next;
                                        });
                                        handleUpdateCustomField(field.id, v);
                                      }
                                    }}
                                    className="w-full px-3 min-h-[48px] text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 bg-white transition-all duration-150"
                                    style={{ fontSize: '16px' }}
                                  >
                                    <option value="">Select {field.name.toLowerCase()}</option>
                                    {choices.map(choice => (
                                      <option key={choice} value={choice}>{choice}</option>
                                    ))}
                                    {allowCustom && <option value="__custom__">Custom…</option>}
                                  </select>
                                  {inCustomMode && (
                                    <input type="text" value={field.value} autoFocus
                                      onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                      className="mt-2 w-full px-4 min-h-[48px] text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-150"
                                      placeholder={`Type ${field.name.toLowerCase()}`} style={{ fontSize: '16px' }} />
                                  )}
                                </>
                              );
                            })()}
                            {field.type === 'textarea' && (
                              <textarea value={field.value} onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-4 py-3.5 text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none transition-all duration-150"
                                placeholder={`Enter ${field.name.toLowerCase()}`} rows={2} style={{ fontSize: '16px' }} />
                            )}
                            {field.type === 'date' && (
                              <input type="date" value={field.value} onChange={(e) => handleUpdateCustomField(field.id, e.target.value)}
                                className="w-full px-4 min-h-[48px] text-base border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-150"
                                style={{ fontSize: '16px' }} />
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                </form>
              </div>
              
              {/* Footer - sticky at bottom */}
              <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
                <div className="flex justify-between gap-3">
                  {selectedOpportunityForInfo ? (
                    /* Details mode auto-saves — no Save button, just Delete. */
                    <button
                      type="button"
                      onClick={() => handleDeleteOpportunity()}
                      className="px-6 min-h-[48px] text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded-xl active:scale-[0.98] transition-all duration-150 touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Delete
                    </button>
                  ) : (
                    <button
                      type="submit"
                      form="opportunity-form"
                      disabled={!selectedCustomer || !selectedPipeline || !selectedStage}
                      className="ml-auto px-8 min-h-[48px] text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl shadow-sm active:scale-[0.98] transition-all duration-150 touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      Create Opportunity
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>, 
        document.body
      )}

      
      {selectedBusiness?.id && (
        <CreateGoogleCalendarEventModal
          isOpen={isCreateGcalEventModalOpen}
          onClose={() => {
            setIsCreateGcalEventModalOpen(false);
            setGcalEventPrefill(null);
          }}
          businessId={selectedBusiness.id}
          syncId={gcalSyncId}
          prefill={gcalEventPrefill}
          onSuccess={() => {
            setIsCreateGcalEventModalOpen(false);
            setGcalEventPrefill(null);
          }}
        />
      )}

      {/* Drop a sales pin linked to an existing lead (opportunity create menu) */}
      {isSalesPinModalOpen && opportunityForSalesPin && selectedBusiness?.id && (
        <SalesPinDropModal
          isOpen
          businessId={selectedBusiness.id}
          existingLead={{
            opportunityId: opportunityForSalesPin.id,
            customerId: opportunityForSalesPin.customer_id || null,
          }}
          onClose={() => {
            const oppId = opportunityForSalesPin.id;
            setIsSalesPinModalOpen(false);
            setOpportunityForSalesPin(null);
            void refreshSalesPinForOpportunity(oppId);
          }}
        />
      )}

      {/* Create Proposal Modal */}
      {opportunityForProposal && (
        <CreateProposalModal
          isOpen={isCreateProposalModalOpen}
          onClose={() => {
            setIsCreateProposalModalOpen(false);
            setOpportunityForProposal(null);
            setProposalCustomer(null);
          }}
          onSuccess={async (createdProposal) => {
            setIsCreateProposalModalOpen(false);
            setOpportunityForProposal(null);
            setProposalCustomer(null);
            // Reload linked proposal if viewing opportunity
            if (selectedOpportunityForInfo) {
              await loadLinkedProposal(selectedOpportunityForInfo.id);
            }
            // Refresh opportunities list if needed
            if (selectedPipeline) {
              await fetchOpportunities();
            }
          }}
          initialOpportunityId={opportunityForProposal.id}
          initialCustomer={proposalCustomer}
        />
      )}

      {isSendIntakeFormModalOpen && selectedOpportunityForInfo && selectedBusiness?.id && selectedCustomer && (
        <SendIntakeFormModal
          isOpen={isSendIntakeFormModalOpen}
          onClose={() => setIsSendIntakeFormModalOpen(false)}
          businessId={selectedBusiness.id}
          businessName={selectedBusiness.name || 'Our team'}
          opportunityId={selectedOpportunityForInfo.id}
          customerId={selectedCustomer.id}
          customerPhone={selectedCustomer.phone}
          customerEmail={selectedCustomer.email}
          customerFirstName={selectedCustomer.first_name}
          onSent={() => {
            const oid = selectedOpportunityForInfo.id;
            setIntakeInstancesNonce((n) => n + 1);
            void loadLastActivity(oid);
          }}
        />
      )}

      {/* Create Project Modal */}
      {opportunityForProject && (
        <CreateProjectModal
          isOpen={isCreateProjectModalOpen}
          onClose={() => {
            setIsCreateProjectModalOpen(false);
            setOpportunityForProject(null);
            setProjectCustomer(null);
          }}
          onSuccess={async (projectId) => {
            console.log('ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ Project created with ID:', projectId, 'for opportunity:', opportunityForProject?.id);
            setIsCreateProjectModalOpen(false);
            setOpportunityForProject(null);
            setProjectCustomer(null);
            // Small delay to ensure database write is committed
            await new Promise(resolve => setTimeout(resolve, 500));
            // Reload linked project if viewing opportunity
            if (selectedOpportunityForInfo) {
              console.log('ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ¢â‚¬Å¾ Reloading linked project for opportunity:', selectedOpportunityForInfo.id);
              await loadLinkedProject(selectedOpportunityForInfo.id);
            }
            // Reload opportunities list if needed
            if (selectedPipeline) {
              await fetchOpportunities();
            }
          }}
          initialCustomer={projectCustomer}
          initialOpportunityId={opportunityForProject.id}
          navigateToProject={true}
        />
      )}

      {/* Create Booking Modal */}
      {opportunityForBooking && createPortal(
        <CreateBookingModal
          isOpen={isCreateBookingModalOpen}
          onClose={() => {
            setIsCreateBookingModalOpen(false);
            setOpportunityForBooking(null);
            setBookingCustomer(null);
          }}
          onSuccess={async () => {
            setIsCreateBookingModalOpen(false);
            // Reload linked booking and opportunity data if viewing opportunity
            if (selectedOpportunityForInfo) {
              // Reload the opportunity to get updated status
              try {
                if (supabase && selectedBusiness?.id) {
                  const { data: updatedOpportunity, error } = await supabase
                    .from('pipeline_opportunities')
                    .select('*')
                    .eq('id', selectedOpportunityForInfo.id)
                    .eq('business_id', selectedBusiness.id)
                    .single();
                  
                  if (!error && updatedOpportunity) {
                    // Update the selected opportunity with fresh data
                    setSelectedOpportunityForInfo({
                      ...updatedOpportunity,
                      customer_name: selectedOpportunityForInfo.customer_name,
                      customers: selectedOpportunityForInfo.customers
                    } as Opportunity);
                    // Update form fields with new status
                    setOpportunityStatus(updatedOpportunity.status);
                    setOpportunityValue(updatedOpportunity.value);
                  }
                }
              } catch (error) {
                console.error('Error reloading opportunity:', error);
              }
              
              // Reload linked booking
              await loadLinkedBooking(selectedOpportunityForInfo.id);
              
              // Reopen the View Opportunity modal to show the booking
              setIsOpportunityInfoModalOpen(true);
            }
            
            // Keep opportunityForBooking and bookingCustomer until modal is reopened
            // They will be cleared when the modal is closed
            
            // Refresh opportunities list if needed
            if (selectedPipeline) {
              await fetchOpportunities();
            }
          }}
          initialCustomer={bookingCustomer}
          initialOpportunityId={opportunityForBooking.id}
        />,
        document.body
      )}

      {/* Create Mobile Booking Modal */}
      {opportunityForBooking && createPortal(
        <CreateMobileBookingModal
          isOpen={isCreateMobileBookingModalOpen}
          onClose={() => {
            setIsCreateMobileBookingModalOpen(false);
            setOpportunityForBooking(null);
            setBookingCustomer(null);
          }}
          onSuccess={async () => {
            setIsCreateMobileBookingModalOpen(false);
            if (selectedOpportunityForInfo) {
              try {
                if (supabase && selectedBusiness?.id) {
                  const { data: updatedOpportunity, error } = await supabase
                    .from('pipeline_opportunities')
                    .select('*')
                    .eq('id', selectedOpportunityForInfo.id)
                    .eq('business_id', selectedBusiness.id)
                    .single();
                  
                  if (!error && updatedOpportunity) {
                    setSelectedOpportunityForInfo({
                      ...updatedOpportunity,
                      customer_name: selectedOpportunityForInfo.customer_name,
                      customers: selectedOpportunityForInfo.customers
                    } as Opportunity);
                    setOpportunityStatus(updatedOpportunity.status);
                    setOpportunityValue(updatedOpportunity.value);
                  }
                }
              } catch (error) {
                console.error('Error reloading opportunity:', error);
              }
              
              await loadLinkedBooking(selectedOpportunityForInfo.id);
              setIsOpportunityInfoModalOpen(true);
            }
            
            if (selectedPipeline) {
              await fetchOpportunities();
            }
          }}
          initialOpportunityId={opportunityForBooking.id}
          prefilledData={mobileBookingPrefilledData}
        />,
        document.body
      )}

      {/* Create Project Modal */}
      {opportunityForProject && (
        <CreateProjectModal
          isOpen={isCreateProjectModalOpen}
          onClose={() => {
            setIsCreateProjectModalOpen(false);
            setOpportunityForProject(null);
            setProjectCustomer(null);
          }}
          onSuccess={async (projectId) => {
            console.log('ÃƒÂ¢Ã…â€œÃ¢â‚¬Â¦ Project created with ID:', projectId, 'for opportunity:', opportunityForProject?.id);
            setIsCreateProjectModalOpen(false);
            setOpportunityForProject(null);
            setProjectCustomer(null);
            // Small delay to ensure database write is committed
            await new Promise(resolve => setTimeout(resolve, 500));
            // Reload linked project if viewing opportunity
            if (selectedOpportunityForInfo) {
              console.log('ÃƒÂ°Ã…Â¸Ã¢â‚¬ÂÃ¢â‚¬Å¾ Reloading linked project for opportunity:', selectedOpportunityForInfo.id);
              await loadLinkedProject(selectedOpportunityForInfo.id);
            }
            // Reload opportunities list if needed
            if (selectedPipeline) {
              await fetchOpportunities();
            }
          }}
          initialCustomer={projectCustomer}
          initialOpportunityId={opportunityForProject.id}
          navigateToProject={true}
        />
      )}

      {opportunityForFieldQuote && (
        <ScheduleFieldQuoteModal
          isOpen={isScheduleFieldQuoteFromCrmOpen}
          onClose={() => {
            setIsScheduleFieldQuoteFromCrmOpen(false);
            setOpportunityForFieldQuote(null);
            setFieldQuoteCustomer(null);
          }}
          projectId={null}
          projectTitle={null}
          initialOpportunityId={opportunityForFieldQuote.id}
          initialProposal={
            linkedProposal && selectedOpportunityForInfo?.id === opportunityForFieldQuote.id
              ? { id: linkedProposal.id }
              : null
          }
          initialCustomer={fieldQuoteCustomer}
          onSuccess={async () => {
            if (selectedOpportunityForInfo?.id === opportunityForFieldQuote.id && supabase && selectedBusiness?.id) {
              try {
                const { data: updatedOpportunity, error } = await supabase
                  .from('pipeline_opportunities')
                  .select('*')
                  .eq('id', selectedOpportunityForInfo.id)
                  .eq('business_id', selectedBusiness.id)
                  .single();

                if (!error && updatedOpportunity) {
                  setSelectedOpportunityForInfo({
                    ...updatedOpportunity,
                    customer_name: selectedOpportunityForInfo.customer_name,
                    customers: selectedOpportunityForInfo.customers
                  } as Opportunity);
                  setOpportunityStatus(updatedOpportunity.status);
                  setOpportunityValue(updatedOpportunity.value);
                }
              } catch (error) {
                console.error('Error reloading opportunity:', error);
              }

              await loadLinkedProposal(selectedOpportunityForInfo.id);
              await loadLinkedFieldQuote(selectedOpportunityForInfo.id);
            }

            if (selectedPipeline) {
              await fetchOpportunities();
            }
          }}
        />
      )}

      <FieldQuoteDetailModal
        isOpen={Boolean(crmFieldQuoteDetailId)}
        fieldQuoteId={crmFieldQuoteDetailId}
        onClose={() => setCrmFieldQuoteDetailId(null)}
        onUpdated={() => {
          if (selectedOpportunityForInfo) {
            loadLinkedFieldQuote(selectedOpportunityForInfo.id);
          }
          if (selectedPipeline) {
            fetchOpportunities();
          }
        }}
      />

      {/* Link Modal */}
      {isLinkModalOpen && selectedOpportunityForInfo && createPortal(
        <div 
          className="fixed inset-0 transition-opacity duration-200 bg-black/50 z-[100020]"
          onClick={() => {
            setIsLinkModalOpen(false);
            setLinkModalType(null);
            setProposalSearchQuery('');
            setBookingSearchQuery('');
            setProjectSearchQuery('');
            setFieldQuoteSearchQuery('');
          }}
        >
          <div 
            className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-2xl transform transition-transform duration-200 translate-x-0"
            onClick={e => e.stopPropagation()}
            style={{
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
              height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {linkModalType && (
                    <button
                      onClick={() => {
                        setLinkModalType(null);
                        setProposalSearchQuery('');
                        setBookingSearchQuery('');
                        setProjectSearchQuery('');
                        setFieldQuoteSearchQuery('');
                      }}
                      className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {linkModalType === 'bookings'
                      ? 'Link Booking'
                      : linkModalType === 'proposals'
                        ? 'Link Proposal'
                        : linkModalType === 'projects'
                          ? 'Link Project'
                          : linkModalType === 'field_quotes'
                            ? 'Link Field Quote'
                            : 'Link'}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setIsLinkModalOpen(false);
                    setLinkModalType(null);
                    setProposalSearchQuery('');
                    setBookingSearchQuery('');
                    setProjectSearchQuery('');
                    setFieldQuoteSearchQuery('');
                  }}
                  className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {!linkModalType ? (
                  // Show options first
                  <div className="space-y-3">
                    {/* Bookings Option */}
                    <button
                      type="button"
                      onClick={async () => {
                        await fetchBookings();
                        setLinkModalType('bookings');
                      }}
                      className={`w-full px-4 py-3 text-left bg-white border rounded-lg hover:bg-gray-50 transition-all duration-150 ${
                        linkedBooking
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          linkedBooking ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <MapPin className={`h-5 w-5 ${linkedBooking ? 'text-blue-600' : 'text-gray-600'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900">Bookings</div>
                          {linkedBooking && (
                            <div className="text-xs text-blue-600 mt-0.5">Linked: {linkedBooking.title || `Booking #${linkedBooking.booking_number}`}</div>
                          )}
                        </div>
                        {linkedBooking && (
                          <div className="ml-3 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                            <X className="h-3 w-3 text-white rotate-45" />
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Proposals Option - Only show if proposals app is installed */}
                    {hasProposalsApp && (
                      <button
                        type="button"
                        onClick={async () => {
                          await fetchProposals();
                          setLinkModalType('proposals');
                        }}
                        className={`w-full px-4 py-3 text-left bg-white border rounded-lg hover:bg-gray-50 transition-all duration-150 ${
                          linkedProposal
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            linkedProposal ? 'bg-blue-100' : 'bg-gray-100'
                          }`}>
                            <FileText className={`h-5 w-5 ${linkedProposal ? 'text-blue-600' : 'text-gray-600'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900">Proposals</div>
                            {linkedProposal && (
                              <div className="text-xs text-blue-600 mt-0.5">Linked: {linkedProposal.title}</div>
                            )}
                          </div>
                          {linkedProposal && (
                            <div className="ml-3 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                              <X className="h-3 w-3 text-white rotate-45" />
                            </div>
                          )}
                        </div>
                      </button>
                    )}

                    {/* Projects Option - Only show if projects app is installed */}
                    {hasProjectsApp && (
                      <button
                        type="button"
                        onClick={async () => {
                          await fetchProjects();
                          setLinkModalType('projects');
                        }}
                        className={`w-full px-4 py-3 text-left bg-white border rounded-lg hover:bg-gray-50 transition-all duration-150 ${
                          linkedProject
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            linkedProject ? 'bg-blue-100' : 'bg-gray-100'
                          }`}>
                            <Folder className={`h-5 w-5 ${linkedProject ? 'text-blue-600' : 'text-gray-600'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900">Projects</div>
                            {linkedProject && (
                              <div className="text-xs text-blue-600 mt-0.5">Linked: {linkedProject.title}</div>
                            )}
                          </div>
                          {linkedProject && (
                            <div className="ml-3 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                              <X className="h-3 w-3 text-white rotate-45" />
                            </div>
                          )}
                        </div>
                      </button>
                    )}

                    {showFieldQuotesInCrm && (
                      <button
                        type="button"
                        onClick={async () => {
                          await fetchFieldQuotes();
                          setLinkModalType('field_quotes');
                        }}
                        className={`w-full px-4 py-3 text-left bg-white border rounded-lg hover:bg-gray-50 transition-all duration-150 ${
                          linkedFieldQuote
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              linkedFieldQuote ? 'bg-blue-100' : 'bg-gray-100'
                            }`}
                          >
                            <NotebookPen className={`h-5 w-5 ${linkedFieldQuote ? 'text-blue-600' : 'text-gray-600'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900">Field quotes</div>
                            {linkedFieldQuote && (
                              <div className="text-xs text-blue-600 mt-0.5">
                                Linked: {linkedFieldQuote.is_measurement ? 'Measurement' : 'Field quote'} ·{' '}
                                <span className="capitalize">{(linkedFieldQuote.status || '').replace('_', ' ')}</span>
                              </div>
                            )}
                          </div>
                          {linkedFieldQuote && (
                            <div className="ml-3 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                              <X className="h-3 w-3 text-white rotate-45" />
                            </div>
                          )}
                        </div>
                      </button>
                    )}
                  </div>
                ) : linkModalType === 'bookings' ? (
                  // Show bookings list
                  <>
                    {/* Unlink Option */}
                    {linkedBooking && (
                      <button
                        type="button"
                        onClick={() => handleBookingLinkChange(null)}
                        className="w-full px-4 py-3 text-left bg-white border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 active:bg-red-100 transition-all duration-150 text-red-600 mb-4"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                            <Link className="h-5 w-5 text-red-600 rotate-45" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold">Unlink Booking</div>
                            <div className="text-xs text-red-500 mt-0.5">Remove the link to this booking</div>
                          </div>
                        </div>
                      </button>
                    )}

                    {/* Search Input */}
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={bookingSearchQuery}
                        onChange={(e) => setBookingSearchQuery(e.target.value)}
                        placeholder="Search bookings..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>

                    {/* Bookings List */}
                    {isLoadingBookings ? (
                      <div className="text-center py-8 text-gray-500 text-sm">Loading bookings...</div>
                    ) : filteredBookings.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {bookingSearchQuery ? 'No bookings found' : 'No bookings available'}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredBookings.map((booking, index) => (
                          <button
                            key={booking.id}
                            type="button"
                            onClick={() => handleBookingLinkChange(booking.id)}
                            className={`w-full bg-white rounded-xl border shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer flex overflow-hidden ${
                              linkedBooking?.id === booking.id
                                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            style={{
                              animationDelay: `${index * 30}ms`
                            }}
                          >
                            {/* Status Bar - Left Edge */}
                            <div className={`w-1 rounded-l-xl flex-shrink-0 ${
                              booking.status === 'completed' ? 'bg-green-500' :
                              booking.status === 'in_progress' ? 'bg-yellow-500' :
                              booking.status === 'scheduled' ? 'bg-blue-500' :
                              booking.status === 'cancelled' ? 'bg-red-500' :
                              'bg-gray-400'
                            }`} />
                            
                            <div className="p-4 flex-1 flex items-center justify-between min-w-0 overflow-hidden">
                              {/* Left Section - Customer Info */}
                              <div className="flex items-center space-x-4 flex-1 min-w-0 overflow-hidden">
                                <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                                  {booking.customer?.first_name?.[0] || ''}{booking.customer?.last_name?.[0] || ''}
                                </div>
                                <div className="flex-1 min-w-0 overflow-hidden">
                                  <div className="flex items-center space-x-3 mb-1 min-w-0">
                                    <h3 className="text-base font-semibold text-gray-900 truncate flex-shrink">
                                      {booking.customer?.first_name} {booking.customer?.last_name}
                                    </h3>
                                    {booking.booking_number && (
                                      <span className="text-xs text-gray-500 flex-shrink-0">#{booking.booking_number}</span>
                                    )}
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${
                                      booking.status === 'completed' ? 'bg-green-100 text-green-700' :
                                      booking.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                                      booking.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                                      booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>
                                      {booking.status ? booking.status.charAt(0).toUpperCase() + booking.status.slice(1).replace('_', ' ') : 'Draft'}
                                    </span>
                              {linkedBooking?.id === booking.id && (
                                      <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                  <X className="h-3 w-3 text-white rotate-45" />
                                </div>
                              )}
                                  </div>
                                </div>
                              </div>

                              {/* Right Section - Date, Time, Amount */}
                              <div className="flex items-center space-x-6 flex-shrink-0 ml-4">
                                {booking.start_date && (
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-xs text-gray-500 whitespace-nowrap">Date</p>
                                    <p className="text-sm font-medium text-gray-900 whitespace-nowrap">
                                      {new Date(booking.start_date).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                    </p>
                                    {booking.start_time && (
                                      <p className="text-xs text-gray-500 mt-1 whitespace-nowrap">{booking.start_time}</p>
                                    )}
                                  </div>
                                )}
                                <div className="text-right flex-shrink-0">
                                  <p className="text-xs text-gray-500 whitespace-nowrap">Total</p>
                                  <p className="text-lg font-bold text-gray-900 whitespace-nowrap">
                                    ${booking.total_amount?.toLocaleString() || '0.00'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : linkModalType === 'proposals' ? (
                  // Show proposals list
                  <>
                    {/* Unlink Option */}
                    {linkedProposal && (
                      <button
                        type="button"
                        onClick={() => handleProposalLinkChange(null)}
                        className="w-full px-4 py-3 text-left bg-white border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 active:bg-red-100 transition-all duration-150 text-red-600 mb-4"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                            <Link className="h-5 w-5 text-red-600 rotate-45" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold">Unlink Proposal</div>
                            <div className="text-xs text-red-500 mt-0.5">Remove the link to this proposal</div>
                          </div>
                        </div>
                      </button>
                    )}

                    {/* Search Input */}
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={proposalSearchQuery}
                        onChange={(e) => setProposalSearchQuery(e.target.value)}
                        placeholder="Search proposals..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>

                    {/* Proposals List */}
                    {isLoadingProposals ? (
                      <div className="text-center py-8 text-gray-500 text-sm">Loading proposals...</div>
                    ) : filteredProposals.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {proposalSearchQuery ? 'No proposals found' : 'No proposals available'}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredProposals.map((proposal, index) => {
                          const displayStatus = getProposalDisplayStatus(proposal);
                          const isExpired = displayStatus === 'expired';
                          
                          return (
                          <button
                            key={proposal.id}
                            type="button"
                            onClick={() => handleProposalLinkChange(proposal.id)}
                              className={`w-full bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.08)] active:scale-[0.98] cursor-pointer flex transition-all duration-150 overflow-hidden ${
                              linkedProposal?.id === proposal.id
                                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                  : isExpired ? 'opacity-60' : ''
                              }`}
                              style={{
                                WebkitTapHighlightColor: 'transparent',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                                animationDelay: `${index * 30}ms`
                              }}
                            >
                              {/* Status Bar - Left Edge */}
                              <div className={`w-1 rounded-l-[12px] flex-shrink-0 ${
                                displayStatus === 'approved' ? 'bg-green-500' :
                                displayStatus === 'sent' ? 'bg-blue-500' :
                                displayStatus === 'rejected' || displayStatus === 'declined' ? 'bg-red-500' :
                                displayStatus === 'expired' ? 'bg-gray-400' :
                                displayStatus === 'draft' ? 'bg-gray-300' :
                                'bg-gray-300'
                              }`} />
                              
                              <div className="p-4 flex-1 flex items-center justify-between min-w-0 overflow-hidden">
                                {/* Left Section - Customer Info and Title */}
                                <div className="flex items-center space-x-4 flex-1 min-w-0 overflow-hidden">
                                  <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-sm font-semibold text-white flex-shrink-0">
                                    {proposal.customer?.first_name?.[0] || ''}{proposal.customer?.last_name?.[0] || ''}
                                </div>
                                  <div className="flex-1 min-w-0 overflow-hidden">
                                    <div className="flex items-center space-x-3 mb-1 min-w-0">
                                      <h3 className="text-base font-semibold text-gray-900 truncate flex-shrink" style={{ letterSpacing: '-0.01em' }}>
                                        {proposal.customer?.first_name} {proposal.customer?.last_name}
                                      </h3>
                                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-[6px] flex-shrink-0 ${
                                        displayStatus === 'approved' ? 'bg-green-50 text-green-700' :
                                        displayStatus === 'sent' ? 'bg-blue-50 text-blue-700' :
                                        displayStatus === 'rejected' || displayStatus === 'declined' ? 'bg-red-50 text-red-700' :
                                        displayStatus === 'expired' ? 'bg-gray-100 text-gray-600' :
                                        displayStatus === 'draft' ? 'bg-gray-100 text-gray-600' :
                                        'bg-gray-100 text-gray-600'
                                      }`} style={{ letterSpacing: '-0.01em' }}>
                                        {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                                      </span>
                              {linkedProposal?.id === proposal.id && (
                                        <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                  <X className="h-3 w-3 text-white rotate-45" />
                                </div>
                              )}
                                    </div>
                                    <h2 className="text-base font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                                      {proposal.title}
                                    </h2>
                                  </div>
                                </div>

                                {/* Right Section - Amount, Valid Until, Status Timestamp */}
                                <div className="flex items-center space-x-6 flex-shrink-0 ml-4">
                                  {getProposalStatusTimestamp(proposal, displayStatus) && (
                                    <div className="text-right flex-shrink-0">
                                      <p className="text-xs text-gray-500 whitespace-nowrap" style={{ letterSpacing: '-0.01em' }}>
                                        {displayStatus === 'expired' ? 'Expired' : 'Updated'}
                                      </p>
                                      <p className="text-sm font-medium text-gray-900 whitespace-nowrap" style={{ letterSpacing: '-0.01em' }}>
                                        {getProposalStatusTimestamp(proposal, displayStatus)}
                                      </p>
                                    </div>
                                  )}
                                  {proposal.valid_until && (
                                    <div className="text-right flex-shrink-0">
                                      <p className="text-xs text-gray-500 whitespace-nowrap" style={{ letterSpacing: '-0.01em' }}>
                                        Valid until
                                      </p>
                                      <p className="text-sm font-medium text-gray-900 whitespace-nowrap" style={{ letterSpacing: '-0.01em' }}>
                                        {new Date(proposal.valid_until).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </p>
                                    </div>
                                  )}
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-xs text-gray-500 whitespace-nowrap" style={{ letterSpacing: '-0.01em' }}>
                                      Total
                                    </p>
                                    <p className="text-lg font-bold text-gray-900 whitespace-nowrap" style={{ letterSpacing: '-0.02em' }}>
                                      ${proposal.total_amount?.toLocaleString() || '0.00'}
                                    </p>
                                  </div>
                                </div>
                            </div>
                          </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : linkModalType === 'projects' ? (
                  // Show projects list
                  <>
                    {/* Unlink Option */}
                    {linkedProject && (
                      <button
                        type="button"
                        onClick={() => handleProjectLinkChange(null)}
                        className="w-full px-4 py-3 text-left bg-white border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 active:bg-red-100 transition-all duration-150 text-red-600 mb-4"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                            <Link className="h-5 w-5 text-red-600 rotate-45" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold">Unlink Project</div>
                            <div className="text-xs text-red-500 mt-0.5">Remove the link to this project</div>
                          </div>
                        </div>
                      </button>
                    )}

                    {/* Search Input */}
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={projectSearchQuery}
                        onChange={(e) => setProjectSearchQuery(e.target.value)}
                        placeholder="Search projects..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
              </div>

                    {/* Projects List */}
                    {isLoadingProjects ? (
                      <div className="text-center py-8 text-gray-500 text-sm">Loading projects...</div>
                    ) : filteredProjects.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {projectSearchQuery ? 'No projects found' : 'No projects available'}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredProjects.map((project, index) => {
                          const getStatusColor = (status: string) => {
                            switch (status?.toLowerCase()) {
                              case 'completed':
                                return 'bg-green-500';
                              case 'in progress':
                                return 'bg-blue-500';
                              case 'scheduled':
                                return 'bg-yellow-500';
                              default:
                                return 'bg-gray-300';
                            }
                          };

                          const getStatusBadgeColor = (status: string) => {
                            switch (status?.toLowerCase()) {
                              case 'completed':
                                return 'bg-green-50 text-green-700';
                              case 'in progress':
                                return 'bg-blue-50 text-blue-700';
                              case 'scheduled':
                                return 'bg-yellow-50 text-yellow-700';
                              default:
                                return 'bg-gray-100 text-gray-600';
                            }
                          };

                          return (
                            <button
                              key={project.id}
                              type="button"
                              onClick={() => handleProjectLinkChange(project.id)}
                              className={`w-full bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.08)] active:scale-[0.98] cursor-pointer flex transition-all duration-150 overflow-hidden ${
                                linkedProject?.id === project.id
                                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                  : ''
                              }`}
                              style={{
                                WebkitTapHighlightColor: 'transparent',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                                animationDelay: `${index * 30}ms`
                              }}
                            >
                              {/* Status Bar - Left Edge */}
                              <div className={`w-1 rounded-l-[12px] flex-shrink-0 ${getStatusColor(project.status || '')}`} />
                              
                              <div className="p-4 flex-1 flex items-center justify-between min-w-0 overflow-hidden">
                                {/* Left Section - Customer Info and Title */}
                                <div className="flex items-center space-x-4 flex-1 min-w-0 overflow-hidden">
                                  <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-sm font-semibold text-white flex-shrink-0">
                                    {project.customer?.first_name?.[0] || ''}{project.customer?.last_name?.[0] || ''}
                                  </div>
                                  <div className="flex-1 min-w-0 overflow-hidden">
                                    <div className="flex items-center space-x-3 mb-1 min-w-0">
                                      <h3 className="text-base font-semibold text-gray-900 truncate flex-shrink" style={{ letterSpacing: '-0.01em' }}>
                                        {project.customer?.first_name} {project.customer?.last_name}
                                      </h3>
                                      <span className={`px-2 py-0.5 text-xs font-semibold rounded-[6px] flex-shrink-0 ${getStatusBadgeColor(project.status || '')}`} style={{ letterSpacing: '-0.01em' }}>
                                        {project.status || 'Open'}
                                      </span>
                                    </div>
                                    <p className="text-sm text-gray-600 truncate" style={{ letterSpacing: '-0.01em' }}>
                                      {project.title || 'Untitled Project'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : linkModalType === 'field_quotes' ? (
                  <>
                    {linkedFieldQuote && (
                      <button
                        type="button"
                        onClick={() => handleFieldQuoteLinkChange(null)}
                        className="w-full px-4 py-3 text-left bg-white border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 active:bg-red-100 transition-all duration-150 text-red-600 mb-4"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                            <Link className="h-5 w-5 text-red-600 rotate-45" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold">Unlink Field Quote</div>
                            <div className="text-xs text-red-500 mt-0.5">Remove the link to this field quote</div>
                          </div>
                        </div>
                      </button>
                    )}

                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={fieldQuoteSearchQuery}
                        onChange={(e) => setFieldQuoteSearchQuery(e.target.value)}
                        placeholder="Search by id or status..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>

                    {isLoadingFieldQuotes ? (
                      <div className="text-center py-8 text-gray-500 text-sm">Loading field quotes...</div>
                    ) : filteredFieldQuotes.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {fieldQuoteSearchQuery ? 'No field quotes found' : 'No field quotes available'}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredFieldQuotes.map((fq, index) => (
                          <button
                            key={fq.id}
                            type="button"
                            onClick={() => handleFieldQuoteLinkChange(fq.id)}
                            className={`w-full bg-white rounded-xl border shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer flex overflow-hidden ${
                              linkedFieldQuote?.id === fq.id
                                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            style={{ animationDelay: `${index * 30}ms` }}
                          >
                            <div
                              className={`w-1 rounded-l-xl flex-shrink-0 ${
                                fq.status === 'completed'
                                  ? 'bg-green-500'
                                  : fq.status === 'cancelled'
                                    ? 'bg-red-500'
                                    : 'bg-amber-500'
                              }`}
                            />
                            <div className="p-4 flex-1 flex items-center justify-between min-w-0 gap-3">
                              <div className="min-w-0 text-left">
                                <div className="text-sm font-semibold text-gray-900">
                                  {fq.is_measurement ? 'Measurement' : 'Field quote'}
                                </div>
                                <div className="text-xs text-gray-500 font-mono truncate mt-0.5">{fq.id}</div>
                              </div>
                              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 capitalize">
                                  {(fq.status || 'scheduled').replace('_', ' ')}
                                </span>
                                {fq.start_date && (
                                  <span className="text-xs text-gray-500 whitespace-nowrap">
                                    {new Date(fq.start_date).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Select Customer Modal */}
      <SelectCustomerModal
        isOpen={isCustomerModalOpen}
        onClose={() => setIsCustomerModalOpen(false)}
        onSelect={(customer) => {
          setSelectedCustomer(customer);
          setIsCustomerModalOpen(false);
        }}
      />

      {/* Customer Details Modal */}
      <CustomerDetailsModal
        isOpen={isCustomerDetailsModalOpen}
        onClose={() => setIsCustomerDetailsModalOpen(false)}
        customer={selectedCustomer}
        onUpdate={async () => {
          if (selectedCustomer?.id) {
            try {
              const refreshed = await getCustomer(selectedCustomer.id);
              setSelectedCustomer(refreshed);
              setCustomerMap(prev => new Map(prev).set(refreshed.id, refreshed));
            } catch (err) {
              console.error('Error refreshing customer after vehicle update:', err);
            }
          }
        }}
      />

      {/* Message Modal */}
      {messageModalCustomer && (
        <CustomerMessageModal
          isOpen={messageModalOpen}
          onClose={() => {
            setMessageModalOpen(false);
            setMessageModalCustomer(null);
          }}
          customer={messageModalCustomer}
        />
      )}

      {/* Proposal Details Modal */}
      {linkedProposal && (
        <ProposalDetailsModal
          isOpen={isProposalDetailsModalOpen}
          onClose={() => {
            setIsProposalDetailsModalOpen(false);
            // Reload proposal in case it was updated
            if (selectedOpportunityForInfo) {
              loadLinkedProposal(selectedOpportunityForInfo.id);
            }
          }}
          proposal={linkedProposal}
          onUpdate={async (updatedProposal) => {
            setLinkedProposal(updatedProposal);
          }}
          onDelete={() => {
            // When proposal is deleted, clear it from opportunity view
            setLinkedProposal(null);
            setIsProposalDetailsModalOpen(false);
          }}
        />
      )}

      {/* Activity Modal - iOS Style */}
      {isActivityModalOpen && createPortal(
        <div 
          className="fixed inset-0 z-[10001] transition-opacity duration-200"
          onClick={(e) => {
            e.stopPropagation();
            setIsActivityModalOpen(false);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          style={{
            background: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <div 
            className={`fixed inset-y-0 right-0 w-full md:w-[600px] bg-gray-50 transform transition-transform duration-300 ease-out shadow-2xl ${
              isActivityModalOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            style={{
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
              height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="relative z-20 bg-gray-50 border-b border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsActivityModalOpen(false);
                    }}
                    className="h-11 w-11 flex items-center justify-center rounded-full hover:bg-gray-200 active:bg-gray-300 active:scale-95 transition-all duration-150 touch-manipulation flex-shrink-0"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                  <h2 className="text-lg font-bold text-gray-900 truncate" style={{ letterSpacing: '-0.02em' }}>
                    All Activities
                  </h2>
                  <div className="w-11 flex-shrink-0" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto bg-gray-50 p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
                {isLoadingAllActivities ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader className="h-6 w-6 animate-spin text-gray-400" />
                    <span className="ml-2 text-gray-500">Loading activities...</span>
                  </div>
                ) : allActivities.length === 0 ? (
                  <div className="text-center py-12 text-gray-400 text-sm">
                    No activities found
                  </div>
                ) : (
                  <div className="space-y-2">
                    {allActivities.map((activity, index) => {
                      const getActivityIcon = (type: string) => {
                        switch (type) {
                          case 'sms_sent':
                          case 'sms_received': return <MessageSquare className="h-4.5 w-4.5 text-green-600" />;
                          case 'email_sent':
                          case 'email_received': return <Mail className="h-4.5 w-4.5 text-blue-600" />;
                          case 'proposal_sent': return <FileText className="h-4.5 w-4.5 text-purple-600" />;
                          case 'phone_call': return <Phone className="h-4.5 w-4.5 text-orange-600" />;
                          case 'stage_moved': return <ArrowRightLeft className="h-4.5 w-4.5 text-gray-500" />;
                          case 'status_changed': return <ChevronDown className="h-4.5 w-4.5 text-indigo-600" />;
                          case 'follow_up_scheduled':
                          case 'follow_up_cleared':
                            return <Clock className="h-4.5 w-4.5 text-blue-600" />;
                          case 'follow_up_completed':
                            return <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600" />;
                          default: return <div className="h-4.5 w-4.5 rounded-full bg-gray-400" />;
                        }
                      };

                      const getActivityTitle = (type: string, data: any) => {
                        switch (type) {
                          case 'sms_sent': return 'SMS Sent';
                          case 'sms_received': return 'SMS Received';
                          case 'email_sent': return data?.subject ? `Email sent: ${data.subject}` : 'Email Sent';
                          case 'email_received': return data?.subject ? `Email received: ${data.subject}` : 'Email Received';
                          case 'proposal_sent': return data?.proposal_title ? `Proposal: ${data.proposal_title}` : 'Proposal Sent';
                          case 'phone_call': return 'Phone Call';
                          case 'stage_moved': return data?.old_stage_name && data?.new_stage_name 
                            ? `${data.old_stage_name} → ${data.new_stage_name}`
                            : 'Stage Moved';
                          case 'status_changed': return data?.from && data?.to
                            ? `${data.from} → ${data.to}`
                            : 'Status Changed';
                          case 'follow_up_scheduled':
                            return data?.scheduled_for
                              ? `Follow-up scheduled for ${data.scheduled_for}`
                              : 'Follow-up scheduled';
                          case 'follow_up_cleared': return 'Follow-up cleared';
                          case 'follow_up_completed':
                            return data?.scheduled_for
                              ? `Follow-up completed (${data.scheduled_for})`
                              : 'Follow-up completed';
                          default: return 'Activity';
                        }
                      };

                      // Who sent a text / email: a person, a bot, an automation, or the system.
                      const sentBy = (data: any): string | null => {
                        const s = data?.sender;
                        if (!s || typeof s !== 'object') return null;
                        switch (s.kind) {
                          case 'bot': return `AI${s.name ? ` · ${s.name}` : ''}`;
                          case 'automation': return `Automation${s.name ? ` · ${s.name}` : ''}`;
                          case 'phone_agent': return s.name || 'AI phone agent';
                          case 'system': return s.name ? `System · ${s.name}` : 'System';
                          default: return s.name || null;
                        }
                      };
                      // Readable text of a message body (emails arrive as HTML).
                      const messageText = (data: any): string => {
                        const raw = String(data?.body || data?.message_preview || '');
                        const text = raw.includes('<')
                          ? raw.replace(/<style[\s\S]*?<\/style>/gi, ' ').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim()
                          : raw.replace(/\s+/g, ' ').trim();
                        return text.length > 220 ? `${text.slice(0, 220)}…` : text;
                      };
                      const getActivityDetails = (type: string, data: any) => {
                        switch (type) {
                          case 'sms_sent':
                          case 'email_sent': {
                            const parts = [];
                            const by = sentBy(data);
                            if (by) parts.push(`Sent by ${by}`);
                            if (data?.to) parts.push(`To: ${data.to}`);
                            return parts.length ? parts.join(' · ') : null;
                          }
                          case 'sms_received':
                          case 'email_received':
                            return data?.from ? `From: ${data.from}` : null;
                          case 'proposal_sent': return data?.method ? `Sent via ${data.method}` : null;
                          case 'phone_call': {
                            const details = [callOutcomeLabel(data || {})];
                            if (data?.phone_number) details.push(`${data.phone_number}`);
                            return details.join(' · ');
                          }
                          case 'stage_moved': return null;
                          case 'status_changed': return 'Status changed';
                          case 'follow_up_scheduled':
                            return data?.next_follow_up_at
                              ? `Due ${new Date(data.next_follow_up_at).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true,
                                })}`
                              : null;
                          case 'follow_up_cleared':
                            return data?.previous_follow_up_at
                              ? `Removed ${new Date(data.previous_follow_up_at).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true,
                                })}`
                              : null;
                          case 'follow_up_completed':
                            return data?.completed_follow_up_at
                              ? `Was due ${new Date(data.completed_follow_up_at).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true,
                                })}`
                              : null;
                          default: return null;
                        }
                      };

                      const getIconBgColor = (type: string) => {
                        switch (type) {
                          case 'sms_sent':
                          case 'sms_received': return 'bg-green-50';
                          case 'email_sent':
                          case 'email_received': return 'bg-blue-50';
                          case 'proposal_sent': return 'bg-purple-50';
                          case 'phone_call': return 'bg-orange-50';
                          case 'stage_moved': return 'bg-gray-100';
                          case 'status_changed': return 'bg-indigo-50';
                          case 'follow_up_scheduled':
                          case 'follow_up_cleared':
                            return 'bg-blue-50';
                          case 'follow_up_completed':
                            return 'bg-emerald-50';
                          default: return 'bg-gray-100';
                        }
                      };

                      return (
                        <div key={index} className="bg-white rounded-[12px] border border-gray-200/60 p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] active:scale-[0.98] transition-all duration-150 touch-manipulation">
                          <div className="flex items-start gap-3">
                            <div className={`flex-shrink-0 w-9 h-9 rounded-xl ${getIconBgColor(activity.activity_type)} flex items-center justify-center mt-0.5`}>
                              {getActivityIcon(activity.activity_type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900 leading-tight">
                                {getActivityTitle(activity.activity_type, activity.activity_data)}
                              </div>
                              {getActivityDetails(activity.activity_type, activity.activity_data) && (
                                <div className="text-xs text-gray-500 mt-0.5 truncate">
                                  {getActivityDetails(activity.activity_type, activity.activity_data)}
                                </div>
                              )}
                              {['sms_sent', 'sms_received', 'email_sent', 'email_received'].includes(activity.activity_type)
                                && messageText(activity.activity_data) && (
                                <div className="mt-1 whitespace-pre-wrap break-words rounded-lg bg-gray-50 px-2.5 py-1.5 text-[13px] leading-snug text-gray-800">
                                  {messageText(activity.activity_data)}
                                </div>
                              )}
                              {activity.activity_type === 'phone_call' && (
                                <CrmCallActivityBody data={(activity.activity_data || {}) as CallActivityData} />
                              )}
                              <div className="text-[11px] text-gray-400 mt-1">
                                {new Date(activity.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Booking Details Modal */}
      {linkedBooking && (
        <BookingInfoModal
          isOpen={isBookingDetailsModalOpen}
          onClose={() => {
            setIsBookingDetailsModalOpen(false);
            // Reload booking in case it was updated
            if (selectedOpportunityForInfo) {
              loadLinkedBooking(selectedOpportunityForInfo.id);
            }
          }}
          bookingId={linkedBooking.id}
          onUpdate={async () => {
            // Reload booking after update
            if (selectedOpportunityForInfo) {
              await loadLinkedBooking(selectedOpportunityForInfo.id);
            }
          }}
          onDelete={() => {
            // When booking is deleted, clear it from opportunity view
            setLinkedBooking(null);
            setIsBookingDetailsModalOpen(false);
            // Also refresh opportunities to update card display
            if (opportunitiesViewPipeline) {
              fetchOpportunities();
            }
          }}
        />
      )}

      {/* Lost Reason Modal */}
      <LostReasonModal
        isOpen={isLostReasonModalOpen}
        businessId={selectedBusiness?.id || ''}
        onConfirm={(reason) => {
          setIsLostReasonModalOpen(false);
          if (pendingLostSource === 'card' && pendingLostOpportunity) {
            executeStatusChange(pendingLostOpportunity, 'Lost', reason);
          } else if (pendingLostSource === 'modal') {
            executeOpportunityUpdate(reason, { closeAfter: false });
          }
          setPendingLostOpportunity(null);
          setPendingLostSource(null);
        }}
        onSkip={() => {
          setIsLostReasonModalOpen(false);
          if (pendingLostSource === 'card' && pendingLostOpportunity) {
            executeStatusChange(pendingLostOpportunity, 'Lost', null);
          } else if (pendingLostSource === 'modal') {
            executeOpportunityUpdate(null, { closeAfter: false });
          }
          setPendingLostOpportunity(null);
          setPendingLostSource(null);
        }}
        onCancel={() => {
          setIsLostReasonModalOpen(false);
          // Revert the status so the auto-save effect doesn't immediately re-prompt.
          if (pendingLostSource === 'modal' && selectedOpportunityForInfo) {
            setOpportunityStatus(selectedOpportunityForInfo.status);
          }
          setPendingLostOpportunity(null);
          setPendingLostSource(null);
        }}
      />

      <OpportunityFollowUpPromptModal
        isOpen={!!followUpPrompt}
        reason={followUpPrompt?.reason ?? 'fresh'}
        opportunityTitle={followUpPrompt?.opportunity.title ?? 'Opportunity'}
        saving={followUpPromptSaving}
        onDismiss={() => void handleFollowUpPromptDismiss()}
        onSave={handleFollowUpPromptSave}
      />
      
    </div>
  );
}
